/* eslint-disable */
/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
export const AccountErrorCode = {
  AccountNotConfirmed: 'ACCOUNT_NOT_CONFIRMED',
  ActivateOwnAccount: 'ACTIVATE_OWN_ACCOUNT',
  ActivateSuperuserAccount: 'ACTIVATE_SUPERUSER_ACCOUNT',
  ChannelInactive: 'CHANNEL_INACTIVE',
  DeactivateOwnAccount: 'DEACTIVATE_OWN_ACCOUNT',
  DeactivateSuperuserAccount: 'DEACTIVATE_SUPERUSER_ACCOUNT',
  DeleteNonStaffUser: 'DELETE_NON_STAFF_USER',
  DeleteOwnAccount: 'DELETE_OWN_ACCOUNT',
  DeleteStaffAccount: 'DELETE_STAFF_ACCOUNT',
  DeleteSuperuserAccount: 'DELETE_SUPERUSER_ACCOUNT',
  DisabledAuthenticationMethod: 'DISABLED_AUTHENTICATION_METHOD',
  DuplicatedInputItem: 'DUPLICATED_INPUT_ITEM',
  FileSizeLimitExceeded: 'FILE_SIZE_LIMIT_EXCEEDED',
  GraphqlError: 'GRAPHQL_ERROR',
  Inactive: 'INACTIVE',
  Invalid: 'INVALID',
  InvalidCredentials: 'INVALID_CREDENTIALS',
  InvalidPassword: 'INVALID_PASSWORD',
  JwtDecodeError: 'JWT_DECODE_ERROR',
  JwtInvalidCsrfToken: 'JWT_INVALID_CSRF_TOKEN',
  JwtInvalidToken: 'JWT_INVALID_TOKEN',
  JwtMissingToken: 'JWT_MISSING_TOKEN',
  JwtSignatureExpired: 'JWT_SIGNATURE_EXPIRED',
  LeftNotManageablePermission: 'LEFT_NOT_MANAGEABLE_PERMISSION',
  LoginAttemptDelayed: 'LOGIN_ATTEMPT_DELAYED',
  MissingChannelSlug: 'MISSING_CHANNEL_SLUG',
  NotFound: 'NOT_FOUND',
  OutOfScopeGroup: 'OUT_OF_SCOPE_GROUP',
  OutOfScopePermission: 'OUT_OF_SCOPE_PERMISSION',
  OutOfScopeUser: 'OUT_OF_SCOPE_USER',
  PasswordEntirelyNumeric: 'PASSWORD_ENTIRELY_NUMERIC',
  PasswordResetAlreadyRequested: 'PASSWORD_RESET_ALREADY_REQUESTED',
  PasswordTooCommon: 'PASSWORD_TOO_COMMON',
  PasswordTooShort: 'PASSWORD_TOO_SHORT',
  PasswordTooSimilar: 'PASSWORD_TOO_SIMILAR',
  Required: 'REQUIRED',
  Unique: 'UNIQUE',
  UnknownIpAddress: 'UNKNOWN_IP_ADDRESS'
} as const;

export type AccountErrorCode = typeof AccountErrorCode[keyof typeof AccountErrorCode];
/** Filtering options for addresses. */
export type AddressFilterInput = {
  country?: CountryCodeEnumFilterInput | null | undefined;
  phoneNumber?: StringFilterInput | null | undefined;
};

export type AddressInput = {
  /** City. */
  city?: string | null | undefined;
  /** District. */
  cityArea?: string | null | undefined;
  /** Company or organization. */
  companyName?: string | null | undefined;
  /** Country. */
  country?: CountryCode | null | undefined;
  /** State or province. */
  countryArea?: string | null | undefined;
  /** Given name. */
  firstName?: string | null | undefined;
  /** Family name. */
  lastName?: string | null | undefined;
  /**
   * Address public metadata. Can be read by any API client authorized to read the object it's attached to.
   *
   * Warning: never store sensitive information, including financial data such as credit card details.
   */
  metadata?: Array<MetadataInput> | null | undefined;
  /**
   * Phone number.
   *
   * Phone numbers are validated with Google's [libphonenumber](https://github.com/google/libphonenumber) library.
   */
  phone?: string | null | undefined;
  /** Postal code. */
  postalCode?: string | null | undefined;
  /**
   * Determine if the address should be validated. By default, Saleor accepts only address inputs matching ruleset from [Google Address Data]{https://chromium-i18n.appspot.com/ssl-address), using [i18naddress](https://github.com/mirumee/google-i18n-address) library. Some mutations may require additional permissions to use the the field. More info about permissions can be found in relevant mutation.
   *
   * Added in Saleor 3.19.
   *
   * Note: this API is currently in Feature Preview and can be subject to changes at later point.
   */
  skipValidation?: boolean | null | undefined;
  /** Address. */
  streetAddress1?: string | null | undefined;
  /** Address. */
  streetAddress2?: string | null | undefined;
};

export const AddressTypeEnum = {
  Billing: 'BILLING',
  Shipping: 'SHIPPING'
} as const;

export type AddressTypeEnum = typeof AddressTypeEnum[keyof typeof AddressTypeEnum];
export type AssignedAttributeReferenceInput = {
  /** Returns objects with a reference pointing to a category identified by the given slug. */
  categorySlugs?: ContainsFilterInput | null | undefined;
  /** Returns objects with a reference pointing to a collection identified by the given slug. */
  collectionSlugs?: ContainsFilterInput | null | undefined;
  /** Returns objects with a reference pointing to a page identified by the given slug. */
  pageSlugs?: ContainsFilterInput | null | undefined;
  /** Returns objects with a reference pointing to a product identified by the given slug. */
  productSlugs?: ContainsFilterInput | null | undefined;
  /** Returns objects with a reference pointing to a product variant identified by the given sku. */
  productVariantSkus?: ContainsFilterInput | null | undefined;
  /** Returns objects with a reference pointing to an object identified by the given ID. */
  referencedIds?: ContainsFilterInput | null | undefined;
};

export type AssignedAttributeValueInput = {
  /** Filter by boolean value for attributes of boolean type. */
  boolean?: boolean | null | undefined;
  /** Filter by date value for attributes of date type. */
  date?: DateRangeInput | null | undefined;
  /** Filter by date time value for attributes of date time type. */
  dateTime?: DateTimeRangeInput | null | undefined;
  /** Filter by name assigned to AttributeValue. */
  name?: StringFilterInput | null | undefined;
  /** Filter by numeric value for attributes of numeric type. */
  numeric?: DecimalFilterInput | null | undefined;
  /** Filter by reference attribute value. */
  reference?: AssignedAttributeReferenceInput | null | undefined;
  /** Filter by slug assigned to AttributeValue. */
  slug?: StringFilterInput | null | undefined;
};

export type AttributeInput = {
  /**
   * The boolean value of the attribute. Requires `slug` to be provided.
   *
   * DEPRECATED: this field will be removed. Use `value` instead.
   */
  boolean?: boolean | null | undefined;
  /**
   * The date range that the returned values should be in. In case of date/time attributes, the UTC midnight of the given date is used. Requires `slug` to be provided.
   *
   * DEPRECATED: this field will be removed. Use `value` instead.
   */
  date?: DateRangeInput | null | undefined;
  /**
   * The date/time range that the returned values should be in. Requires `slug` to be provided.
   *
   * DEPRECATED: this field will be removed. Use `value` instead.
   */
  dateTime?: DateTimeRangeInput | null | undefined;
  /** Internal representation of an attribute name. */
  slug?: string | null | undefined;
  /** Filter by value of the attribute. Only one value input field is allowed. If provided more than one, the error will be raised. Cannot be combined with deprecated fields of `AttributeInput`.  */
  value?: AssignedAttributeValueInput | null | undefined;
  /**
   * Slugs identifying the attributeValues associated with the Attribute. When specified, it filters the results to include only records with one of the matching values. Requires `slug` to be provided.
   *
   * DEPRECATED: this field will be removed. Use `value` instead.
   */
  values?: Array<string> | null | undefined;
  /**
   * The range that the returned values should be in. Requires `slug` to be provided.
   *
   * DEPRECATED: this field will be removed. Use `value` instead.
   */
  valuesRange?: IntRangeInput | null | undefined;
};

export const AttributeInputTypeEnum = {
  Boolean: 'BOOLEAN',
  Date: 'DATE',
  DateTime: 'DATE_TIME',
  Dropdown: 'DROPDOWN',
  File: 'FILE',
  Multiselect: 'MULTISELECT',
  Numeric: 'NUMERIC',
  PlainText: 'PLAIN_TEXT',
  Reference: 'REFERENCE',
  RichText: 'RICH_TEXT',
  SingleReference: 'SINGLE_REFERENCE',
  Swatch: 'SWATCH'
} as const;

export type AttributeInputTypeEnum = typeof AttributeInputTypeEnum[keyof typeof AttributeInputTypeEnum];
export const CheckoutErrorCode = {
  BillingAddressNotSet: 'BILLING_ADDRESS_NOT_SET',
  ChannelInactive: 'CHANNEL_INACTIVE',
  CheckoutNotFullyPaid: 'CHECKOUT_NOT_FULLY_PAID',
  DeliveryMethodNotApplicable: 'DELIVERY_METHOD_NOT_APPLICABLE',
  EmailNotSet: 'EMAIL_NOT_SET',
  GiftCardNotApplicable: 'GIFT_CARD_NOT_APPLICABLE',
  GraphqlError: 'GRAPHQL_ERROR',
  InactivePayment: 'INACTIVE_PAYMENT',
  InsufficientStock: 'INSUFFICIENT_STOCK',
  Invalid: 'INVALID',
  InvalidShippingMethod: 'INVALID_SHIPPING_METHOD',
  MissingAddressData: 'MISSING_ADDRESS_DATA',
  MissingChannelSlug: 'MISSING_CHANNEL_SLUG',
  NonEditableGiftLine: 'NON_EDITABLE_GIFT_LINE',
  NonRemovableGiftLine: 'NON_REMOVABLE_GIFT_LINE',
  NotFound: 'NOT_FOUND',
  NoLines: 'NO_LINES',
  PaymentError: 'PAYMENT_ERROR',
  PriceOverrideReasonWithoutOverride: 'PRICE_OVERRIDE_REASON_WITHOUT_OVERRIDE',
  ProductNotPublished: 'PRODUCT_NOT_PUBLISHED',
  ProductUnavailableForPurchase: 'PRODUCT_UNAVAILABLE_FOR_PURCHASE',
  QuantityGreaterThanLimit: 'QUANTITY_GREATER_THAN_LIMIT',
  Required: 'REQUIRED',
  ShippingAddressNotSet: 'SHIPPING_ADDRESS_NOT_SET',
  ShippingChangeForbidden: 'SHIPPING_CHANGE_FORBIDDEN',
  ShippingMethodNotApplicable: 'SHIPPING_METHOD_NOT_APPLICABLE',
  ShippingMethodNotSet: 'SHIPPING_METHOD_NOT_SET',
  ShippingNotRequired: 'SHIPPING_NOT_REQUIRED',
  TaxError: 'TAX_ERROR',
  UnavailableVariantInChannel: 'UNAVAILABLE_VARIANT_IN_CHANNEL',
  Unique: 'UNIQUE',
  VoucherNotApplicable: 'VOUCHER_NOT_APPLICABLE',
  ZeroQuantity: 'ZERO_QUANTITY'
} as const;

export type CheckoutErrorCode = typeof CheckoutErrorCode[keyof typeof CheckoutErrorCode];
export type CheckoutLineInput = {
  /** Flag that allow force splitting the same variant into multiple lines by skipping the matching logic.  */
  forceNewLine?: boolean | null | undefined;
  /**
   * Fields required to update the object's metadata. Can be read by any API client authorized to read the object it's attached to.
   *
   * Warning: never store sensitive information, including financial data such as credit card details.
   */
  metadata?: Array<MetadataInput> | null | undefined;
  /** Custom price of the item. Can be set only by apps with `HANDLE_CHECKOUTS` permission. When the line with the same variant will be provided multiple times, the last price will be used. */
  price?: number | null | undefined;
  /**
   * Reason explaining why a custom `price` was set on the line, for debugging and auditing. Can be set only by apps with `HANDLE_CHECKOUTS` permission and only when the line has a `price` override. Setting a new `price` without a reason clears the previous reason. Blank values are stored as no reason. Limited to 255 characters; longer values are truncated.
   *
   * Added in Saleor 3.23.
   */
  priceOverrideReason?: string | null | undefined;
  /** The number of items purchased. */
  quantity: number;
  /** ID of the product variant. */
  variantId: string | number;
};

export type CheckoutLineUpdateInput = {
  /** ID of the line. */
  lineId?: string | number | null | undefined;
  /**
   * Checkout line public metadata. Will add and update keys. To delete keys use deleteMetadata mutation.
   *
   * Added in Saleor 3.21. Can be read by any API client authorized to read the object it's attached to.
   *
   * Warning: never store sensitive information, including financial data such as credit card details.
   */
  metadata?: Array<MetadataInput> | null | undefined;
  /** Custom price of the item. Can be set only by apps with `HANDLE_CHECKOUTS` permission. When the line with the same variant will be provided multiple times, the last price will be used. */
  price?: number | null | undefined;
  /**
   * Reason explaining why a custom `price` was set on the line, for debugging and auditing. Can be set only by apps with `HANDLE_CHECKOUTS` permission and only when the line has a `price` override. Setting a new `price` without a reason clears the previous reason. Blank values are stored as no reason. Limited to 255 characters; longer values are truncated.
   *
   * Added in Saleor 3.23.
   */
  priceOverrideReason?: string | null | undefined;
  /** The number of items purchased. Optional for apps, required for any other users. */
  quantity?: number | null | undefined;
  /**
   * ID of the product variant.
   *
   * DEPRECATED: this field will be removed. Use `lineId` instead.
   */
  variantId?: string | number | null | undefined;
};

/** Define the filtering options for fields that can contain multiple values. */
export type ContainsFilterInput = {
  /** The field contains all of the specified values. */
  containsAll?: Array<string> | null | undefined;
  /** The field contains at least one of the specified values. */
  containsAny?: Array<string> | null | undefined;
};

/**
 * Represents country codes defined by the ISO 3166-1 alpha-2 standard.
 *
 * The `EU` value is DEPRECATED and will be removed in Saleor 3.21.
 */
export const CountryCode = {
  /** Andorra */
  Ad: 'AD',
  /** United Arab Emirates */
  Ae: 'AE',
  /** Afghanistan */
  Af: 'AF',
  /** Antigua and Barbuda */
  Ag: 'AG',
  /** Anguilla */
  Ai: 'AI',
  /** Albania */
  Al: 'AL',
  /** Armenia */
  Am: 'AM',
  /** Angola */
  Ao: 'AO',
  /** Antarctica */
  Aq: 'AQ',
  /** Argentina */
  Ar: 'AR',
  /** American Samoa */
  As: 'AS',
  /** Austria */
  At: 'AT',
  /** Australia */
  Au: 'AU',
  /** Aruba */
  Aw: 'AW',
  /** Åland Islands */
  Ax: 'AX',
  /** Azerbaijan */
  Az: 'AZ',
  /** Bosnia and Herzegovina */
  Ba: 'BA',
  /** Barbados */
  Bb: 'BB',
  /** Bangladesh */
  Bd: 'BD',
  /** Belgium */
  Be: 'BE',
  /** Burkina Faso */
  Bf: 'BF',
  /** Bulgaria */
  Bg: 'BG',
  /** Bahrain */
  Bh: 'BH',
  /** Burundi */
  Bi: 'BI',
  /** Benin */
  Bj: 'BJ',
  /** Saint Barthélemy */
  Bl: 'BL',
  /** Bermuda */
  Bm: 'BM',
  /** Brunei */
  Bn: 'BN',
  /** Bolivia */
  Bo: 'BO',
  /** Bonaire, Sint Eustatius and Saba */
  Bq: 'BQ',
  /** Brazil */
  Br: 'BR',
  /** Bahamas */
  Bs: 'BS',
  /** Bhutan */
  Bt: 'BT',
  /** Bouvet Island */
  Bv: 'BV',
  /** Botswana */
  Bw: 'BW',
  /** Belarus */
  By: 'BY',
  /** Belize */
  Bz: 'BZ',
  /** Canada */
  Ca: 'CA',
  /** Cocos (Keeling) Islands */
  Cc: 'CC',
  /** Congo (the Democratic Republic of the) */
  Cd: 'CD',
  /** Central African Republic */
  Cf: 'CF',
  /** Congo */
  Cg: 'CG',
  /** Switzerland */
  Ch: 'CH',
  /** Côte d'Ivoire */
  Ci: 'CI',
  /** Cook Islands */
  Ck: 'CK',
  /** Chile */
  Cl: 'CL',
  /** Cameroon */
  Cm: 'CM',
  /** China */
  Cn: 'CN',
  /** Colombia */
  Co: 'CO',
  /** Costa Rica */
  Cr: 'CR',
  /** Cuba */
  Cu: 'CU',
  /** Cabo Verde */
  Cv: 'CV',
  /** Curaçao */
  Cw: 'CW',
  /** Christmas Island */
  Cx: 'CX',
  /** Cyprus */
  Cy: 'CY',
  /** Czechia */
  Cz: 'CZ',
  /** Germany */
  De: 'DE',
  /** Djibouti */
  Dj: 'DJ',
  /** Denmark */
  Dk: 'DK',
  /** Dominica */
  Dm: 'DM',
  /** Dominican Republic */
  Do: 'DO',
  /** Algeria */
  Dz: 'DZ',
  /** Ecuador */
  Ec: 'EC',
  /** Estonia */
  Ee: 'EE',
  /** Egypt */
  Eg: 'EG',
  /** Western Sahara */
  Eh: 'EH',
  /** Eritrea */
  Er: 'ER',
  /** Spain */
  Es: 'ES',
  /** Ethiopia */
  Et: 'ET',
  /** European Union */
  Eu: 'EU',
  /** Finland */
  Fi: 'FI',
  /** Fiji */
  Fj: 'FJ',
  /** Falkland Islands (Malvinas) */
  Fk: 'FK',
  /** Micronesia */
  Fm: 'FM',
  /** Faroe Islands */
  Fo: 'FO',
  /** France */
  Fr: 'FR',
  /** Gabon */
  Ga: 'GA',
  /** United Kingdom */
  Gb: 'GB',
  /** Grenada */
  Gd: 'GD',
  /** Georgia */
  Ge: 'GE',
  /** French Guiana */
  Gf: 'GF',
  /** Guernsey */
  Gg: 'GG',
  /** Ghana */
  Gh: 'GH',
  /** Gibraltar */
  Gi: 'GI',
  /** Greenland */
  Gl: 'GL',
  /** Gambia */
  Gm: 'GM',
  /** Guinea */
  Gn: 'GN',
  /** Guadeloupe */
  Gp: 'GP',
  /** Equatorial Guinea */
  Gq: 'GQ',
  /** Greece */
  Gr: 'GR',
  /** South Georgia and the South Sandwich Islands */
  Gs: 'GS',
  /** Guatemala */
  Gt: 'GT',
  /** Guam */
  Gu: 'GU',
  /** Guinea-Bissau */
  Gw: 'GW',
  /** Guyana */
  Gy: 'GY',
  /** Hong Kong */
  Hk: 'HK',
  /** Heard Island and McDonald Islands */
  Hm: 'HM',
  /** Honduras */
  Hn: 'HN',
  /** Croatia */
  Hr: 'HR',
  /** Haiti */
  Ht: 'HT',
  /** Hungary */
  Hu: 'HU',
  /** Indonesia */
  Id: 'ID',
  /** Ireland */
  Ie: 'IE',
  /** Israel */
  Il: 'IL',
  /** Isle of Man */
  Im: 'IM',
  /** India */
  In: 'IN',
  /** British Indian Ocean Territory */
  Io: 'IO',
  /** Iraq */
  Iq: 'IQ',
  /** Iran */
  Ir: 'IR',
  /** Iceland */
  Is: 'IS',
  /** Italy */
  It: 'IT',
  /** Jersey */
  Je: 'JE',
  /** Jamaica */
  Jm: 'JM',
  /** Jordan */
  Jo: 'JO',
  /** Japan */
  Jp: 'JP',
  /** Kenya */
  Ke: 'KE',
  /** Kyrgyzstan */
  Kg: 'KG',
  /** Cambodia */
  Kh: 'KH',
  /** Kiribati */
  Ki: 'KI',
  /** Comoros */
  Km: 'KM',
  /** Saint Kitts and Nevis */
  Kn: 'KN',
  /** North Korea */
  Kp: 'KP',
  /** South Korea */
  Kr: 'KR',
  /** Kuwait */
  Kw: 'KW',
  /** Cayman Islands */
  Ky: 'KY',
  /** Kazakhstan */
  Kz: 'KZ',
  /** Laos */
  La: 'LA',
  /** Lebanon */
  Lb: 'LB',
  /** Saint Lucia */
  Lc: 'LC',
  /** Liechtenstein */
  Li: 'LI',
  /** Sri Lanka */
  Lk: 'LK',
  /** Liberia */
  Lr: 'LR',
  /** Lesotho */
  Ls: 'LS',
  /** Lithuania */
  Lt: 'LT',
  /** Luxembourg */
  Lu: 'LU',
  /** Latvia */
  Lv: 'LV',
  /** Libya */
  Ly: 'LY',
  /** Morocco */
  Ma: 'MA',
  /** Monaco */
  Mc: 'MC',
  /** Moldova */
  Md: 'MD',
  /** Montenegro */
  Me: 'ME',
  /** Saint Martin (French part) */
  Mf: 'MF',
  /** Madagascar */
  Mg: 'MG',
  /** Marshall Islands */
  Mh: 'MH',
  /** North Macedonia */
  Mk: 'MK',
  /** Mali */
  Ml: 'ML',
  /** Myanmar */
  Mm: 'MM',
  /** Mongolia */
  Mn: 'MN',
  /** Macao */
  Mo: 'MO',
  /** Northern Mariana Islands */
  Mp: 'MP',
  /** Martinique */
  Mq: 'MQ',
  /** Mauritania */
  Mr: 'MR',
  /** Montserrat */
  Ms: 'MS',
  /** Malta */
  Mt: 'MT',
  /** Mauritius */
  Mu: 'MU',
  /** Maldives */
  Mv: 'MV',
  /** Malawi */
  Mw: 'MW',
  /** Mexico */
  Mx: 'MX',
  /** Malaysia */
  My: 'MY',
  /** Mozambique */
  Mz: 'MZ',
  /** Namibia */
  Na: 'NA',
  /** New Caledonia */
  Nc: 'NC',
  /** Niger */
  Ne: 'NE',
  /** Norfolk Island */
  Nf: 'NF',
  /** Nigeria */
  Ng: 'NG',
  /** Nicaragua */
  Ni: 'NI',
  /** Netherlands */
  Nl: 'NL',
  /** Norway */
  No: 'NO',
  /** Nepal */
  Np: 'NP',
  /** Nauru */
  Nr: 'NR',
  /** Niue */
  Nu: 'NU',
  /** New Zealand */
  Nz: 'NZ',
  /** Oman */
  Om: 'OM',
  /** Panama */
  Pa: 'PA',
  /** Peru */
  Pe: 'PE',
  /** French Polynesia */
  Pf: 'PF',
  /** Papua New Guinea */
  Pg: 'PG',
  /** Philippines */
  Ph: 'PH',
  /** Pakistan */
  Pk: 'PK',
  /** Poland */
  Pl: 'PL',
  /** Saint Pierre and Miquelon */
  Pm: 'PM',
  /** Pitcairn */
  Pn: 'PN',
  /** Puerto Rico */
  Pr: 'PR',
  /** Palestine, State of */
  Ps: 'PS',
  /** Portugal */
  Pt: 'PT',
  /** Palau */
  Pw: 'PW',
  /** Paraguay */
  Py: 'PY',
  /** Qatar */
  Qa: 'QA',
  /** Réunion */
  Re: 'RE',
  /** Romania */
  Ro: 'RO',
  /** Serbia */
  Rs: 'RS',
  /** Russia */
  Ru: 'RU',
  /** Rwanda */
  Rw: 'RW',
  /** Saudi Arabia */
  Sa: 'SA',
  /** Solomon Islands */
  Sb: 'SB',
  /** Seychelles */
  Sc: 'SC',
  /** Sudan */
  Sd: 'SD',
  /** Sweden */
  Se: 'SE',
  /** Singapore */
  Sg: 'SG',
  /** Saint Helena, Ascension and Tristan da Cunha */
  Sh: 'SH',
  /** Slovenia */
  Si: 'SI',
  /** Svalbard and Jan Mayen */
  Sj: 'SJ',
  /** Slovakia */
  Sk: 'SK',
  /** Sierra Leone */
  Sl: 'SL',
  /** San Marino */
  Sm: 'SM',
  /** Senegal */
  Sn: 'SN',
  /** Somalia */
  So: 'SO',
  /** Suriname */
  Sr: 'SR',
  /** South Sudan */
  Ss: 'SS',
  /** Sao Tome and Principe */
  St: 'ST',
  /** El Salvador */
  Sv: 'SV',
  /** Sint Maarten (Dutch part) */
  Sx: 'SX',
  /** Syria */
  Sy: 'SY',
  /** Eswatini */
  Sz: 'SZ',
  /** Turks and Caicos Islands */
  Tc: 'TC',
  /** Chad */
  Td: 'TD',
  /** French Southern Territories */
  Tf: 'TF',
  /** Togo */
  Tg: 'TG',
  /** Thailand */
  Th: 'TH',
  /** Tajikistan */
  Tj: 'TJ',
  /** Tokelau */
  Tk: 'TK',
  /** Timor-Leste */
  Tl: 'TL',
  /** Turkmenistan */
  Tm: 'TM',
  /** Tunisia */
  Tn: 'TN',
  /** Tonga */
  To: 'TO',
  /** Türkiye */
  Tr: 'TR',
  /** Trinidad and Tobago */
  Tt: 'TT',
  /** Tuvalu */
  Tv: 'TV',
  /** Taiwan */
  Tw: 'TW',
  /** Tanzania */
  Tz: 'TZ',
  /** Ukraine */
  Ua: 'UA',
  /** Uganda */
  Ug: 'UG',
  /** United States Minor Outlying Islands */
  Um: 'UM',
  /** United States of America */
  Us: 'US',
  /** Uruguay */
  Uy: 'UY',
  /** Uzbekistan */
  Uz: 'UZ',
  /** Holy See */
  Va: 'VA',
  /** Saint Vincent and the Grenadines */
  Vc: 'VC',
  /** Venezuela */
  Ve: 'VE',
  /** Virgin Islands (British) */
  Vg: 'VG',
  /** Virgin Islands (U.S.) */
  Vi: 'VI',
  /** Vietnam */
  Vn: 'VN',
  /** Vanuatu */
  Vu: 'VU',
  /** Wallis and Futuna */
  Wf: 'WF',
  /** Samoa */
  Ws: 'WS',
  /** Kosovo */
  Xk: 'XK',
  /** Yemen */
  Ye: 'YE',
  /** Mayotte */
  Yt: 'YT',
  /** South Africa */
  Za: 'ZA',
  /** Zambia */
  Zm: 'ZM',
  /** Zimbabwe */
  Zw: 'ZW'
} as const;

export type CountryCode = typeof CountryCode[keyof typeof CountryCode];
/** Filter by country code. */
export type CountryCodeEnumFilterInput = {
  /** The value equal to. */
  eq?: CountryCode | null | undefined;
  /** The value not included in. */
  notOneOf?: Array<CountryCode> | null | undefined;
  /** The value included in. */
  oneOf?: Array<CountryCode> | null | undefined;
};

export type CustomerOrderWhereInput = {
  /** List of conditions that must be met. */
  AND?: Array<CustomerOrderWhereInput> | null | undefined;
  /** A list of conditions of which at least one must be met. */
  OR?: Array<CustomerOrderWhereInput> | null | undefined;
  /** Filter by authorize status. */
  authorizeStatus?: OrderAuthorizeStatusEnumFilterInput | null | undefined;
  /** Filter by billing address of the order. */
  billingAddress?: AddressFilterInput | null | undefined;
  /** Filter by channel. */
  channelId?: GlobalIdFilterInput | null | undefined;
  /** Filter by charge status. */
  chargeStatus?: OrderChargeStatusEnumFilterInput | null | undefined;
  /** Filter by checkout id. */
  checkoutId?: GlobalIdFilterInput | null | undefined;
  /** Filter by checkout token. */
  checkoutToken?: UuidFilterInput | null | undefined;
  /** Filter order by created at date. */
  createdAt?: DateTimeRangeInput | null | undefined;
  /** Filter by whether the order has any fulfillments. */
  hasFulfillments?: boolean | null | undefined;
  /** Filter by whether the order has any invoices. */
  hasInvoices?: boolean | null | undefined;
  ids?: Array<string | number> | null | undefined;
  /** Filter by invoice data associated with the order. Each list item represents conditions that must be satisfied by a single object. The filter matches orders that have related objects meeting all specified groups of conditions. */
  invoices?: Array<InvoiceFilterInput> | null | undefined;
  /** Filter by whether the order uses the click and collect delivery method. */
  isClickAndCollect?: boolean | null | undefined;
  /** Filter based on whether the order includes a gift card purchase. */
  isGiftCardBought?: boolean | null | undefined;
  /** Filter based on whether a gift card was used in the order. */
  isGiftCardUsed?: boolean | null | undefined;
  /** Filter by number of lines in the order. */
  linesCount?: IntFilterInput | null | undefined;
  /** Filter by metadata fields. */
  metadata?: MetadataFilterInput | null | undefined;
  /** Filter by order number. */
  number?: IntFilterInput | null | undefined;
  /** Filter by the product type of related order lines. */
  productTypeId?: GlobalIdFilterInput | null | undefined;
  /** Filter by shipping address of the order. */
  shippingAddress?: AddressFilterInput | null | undefined;
  /** Filter by order status. */
  status?: OrderStatusEnumFilterInput | null | undefined;
  /** Filter by total gross amount of the order. */
  totalGross?: PriceFilterInput | null | undefined;
  /** Filter by total net amount of the order. */
  totalNet?: PriceFilterInput | null | undefined;
  /** Filter order by updated at date. */
  updatedAt?: DateTimeRangeInput | null | undefined;
  /** Filter by user email. */
  userEmail?: StringFilterInput | null | undefined;
  /** Filter by voucher code used in the order. */
  voucherCode?: StringFilterInput | null | undefined;
};

export type DateRangeInput = {
  /** Start date. */
  gte?: string | null | undefined;
  /** End date. */
  lte?: string | null | undefined;
};

export type DateTimeRangeInput = {
  /** Start date. */
  gte?: string | null | undefined;
  /** End date. */
  lte?: string | null | undefined;
};

/** Define the filtering options for decimal fields. */
export type DecimalFilterInput = {
  /** The value equal to. */
  eq?: number | null | undefined;
  /** The value included in. */
  oneOf?: Array<number> | null | undefined;
  /** The value in range. */
  range?: DecimalRangeInput | null | undefined;
};

export type DecimalRangeInput = {
  /** Decimal value greater than or equal to. */
  gte?: number | null | undefined;
  /** Decimal value less than or equal to. */
  lte?: number | null | undefined;
};

export const DiscountErrorCode = {
  AlreadyExists: 'ALREADY_EXISTS',
  CannotManageProductWithoutVariant: 'CANNOT_MANAGE_PRODUCT_WITHOUT_VARIANT',
  DuplicatedInputItem: 'DUPLICATED_INPUT_ITEM',
  GraphqlError: 'GRAPHQL_ERROR',
  Invalid: 'INVALID',
  NotFound: 'NOT_FOUND',
  Required: 'REQUIRED',
  Unique: 'UNIQUE',
  VoucherAlreadyUsed: 'VOUCHER_ALREADY_USED'
} as const;

export type DiscountErrorCode = typeof DiscountErrorCode[keyof typeof DiscountErrorCode];
export const DiscountValueTypeEnum = {
  Fixed: 'FIXED',
  Percentage: 'PERCENTAGE'
} as const;

export type DiscountValueTypeEnum = typeof DiscountValueTypeEnum[keyof typeof DiscountValueTypeEnum];
/** Filter input for order fulfillments data. */
export type FulfillmentFilterInput = {
  /** Filter by metadata fields. */
  metadata?: MetadataFilterInput | null | undefined;
  /** Filter by fulfillment status. */
  status?: FulfillmentStatusEnumFilterInput | null | undefined;
  /** Filter by fulfillment warehouse. */
  warehouse?: FulfillmentWarehouseFilterInput | null | undefined;
};

export const FulfillmentStatus = {
  Canceled: 'CANCELED',
  Fulfilled: 'FULFILLED',
  Refunded: 'REFUNDED',
  RefundedAndReturned: 'REFUNDED_AND_RETURNED',
  Replaced: 'REPLACED',
  Returned: 'RETURNED',
  WaitingForApproval: 'WAITING_FOR_APPROVAL'
} as const;

export type FulfillmentStatus = typeof FulfillmentStatus[keyof typeof FulfillmentStatus];
/** Filter by fulfillment status. */
export type FulfillmentStatusEnumFilterInput = {
  /** The value equal to. */
  eq?: FulfillmentStatus | null | undefined;
  /** The value included in. */
  oneOf?: Array<FulfillmentStatus> | null | undefined;
};

export type FulfillmentUpdateTrackingInput = {
  /** If true, send an email notification to the customer. */
  notifyCustomer?: boolean | null | undefined;
  /** Fulfillment tracking number. */
  trackingNumber?: string | null | undefined;
};

/** Filter input for fulfillment warehouses. */
export type FulfillmentWarehouseFilterInput = {
  /** Filter fulfillments by warehouse external reference. */
  externalReference?: StringFilterInput | null | undefined;
  /** Filter fulfillments by warehouse ID. */
  id?: GlobalIdFilterInput | null | undefined;
  /** Filter fulfillments by warehouse slug. */
  slug?: StringFilterInput | null | undefined;
};

/** Define the filtering options for foreign key fields. */
export type GlobalIdFilterInput = {
  /** The value equal to. */
  eq?: string | number | null | undefined;
  /** The value included in. */
  oneOf?: Array<string | number> | null | undefined;
};

/** Define the filtering options for integer fields. */
export type IntFilterInput = {
  /** The value equal to. */
  eq?: number | null | undefined;
  /** The value included in. */
  oneOf?: Array<number> | null | undefined;
  /** The value in range. */
  range?: IntRangeInput | null | undefined;
};

export type IntRangeInput = {
  /** Value greater than or equal to. */
  gte?: number | null | undefined;
  /** Value less than or equal to. */
  lte?: number | null | undefined;
};

/** Filter input for invoices. */
export type InvoiceFilterInput = {
  /** Filter invoices by creation date. */
  createdAt?: DateTimeRangeInput | null | undefined;
};

/** Language code enum. It contains all the languages supported by Saleor. */
export const LanguageCodeEnum = {
  /** Afrikaans */
  Af: 'AF',
  /** Afrikaans (Namibia) */
  AfNa: 'AF_NA',
  /** Afrikaans (South Africa) */
  AfZa: 'AF_ZA',
  /** Aghem */
  Agq: 'AGQ',
  /** Aghem (Cameroon) */
  AgqCm: 'AGQ_CM',
  /** Akan */
  Ak: 'AK',
  /** Akan (Ghana) */
  AkGh: 'AK_GH',
  /** Amharic */
  Am: 'AM',
  /** Amharic (Ethiopia) */
  AmEt: 'AM_ET',
  /** Arabic */
  Ar: 'AR',
  /** Arabic (United Arab Emirates) */
  ArAe: 'AR_AE',
  /** Arabic (Bahrain) */
  ArBh: 'AR_BH',
  /** Arabic (Djibouti) */
  ArDj: 'AR_DJ',
  /** Arabic (Algeria) */
  ArDz: 'AR_DZ',
  /** Arabic (Egypt) */
  ArEg: 'AR_EG',
  /** Arabic (Western Sahara) */
  ArEh: 'AR_EH',
  /** Arabic (Eritrea) */
  ArEr: 'AR_ER',
  /** Arabic (Israel) */
  ArIl: 'AR_IL',
  /** Arabic (Iraq) */
  ArIq: 'AR_IQ',
  /** Arabic (Jordan) */
  ArJo: 'AR_JO',
  /** Arabic (Comoros) */
  ArKm: 'AR_KM',
  /** Arabic (Kuwait) */
  ArKw: 'AR_KW',
  /** Arabic (Lebanon) */
  ArLb: 'AR_LB',
  /** Arabic (Libya) */
  ArLy: 'AR_LY',
  /** Arabic (Morocco) */
  ArMa: 'AR_MA',
  /** Arabic (Mauritania) */
  ArMr: 'AR_MR',
  /** Arabic (Oman) */
  ArOm: 'AR_OM',
  /** Arabic (Palestinian Territories) */
  ArPs: 'AR_PS',
  /** Arabic (Qatar) */
  ArQa: 'AR_QA',
  /** Arabic (Saudi Arabia) */
  ArSa: 'AR_SA',
  /** Arabic (Sudan) */
  ArSd: 'AR_SD',
  /** Arabic (Somalia) */
  ArSo: 'AR_SO',
  /** Arabic (South Sudan) */
  ArSs: 'AR_SS',
  /** Arabic (Syria) */
  ArSy: 'AR_SY',
  /** Arabic (Chad) */
  ArTd: 'AR_TD',
  /** Arabic (Tunisia) */
  ArTn: 'AR_TN',
  /** Arabic (Yemen) */
  ArYe: 'AR_YE',
  /** Assamese */
  As: 'AS',
  /** Asu */
  Asa: 'ASA',
  /** Asu (Tanzania) */
  AsaTz: 'ASA_TZ',
  /** Asturian */
  Ast: 'AST',
  /** Asturian (Spain) */
  AstEs: 'AST_ES',
  /** Assamese (India) */
  AsIn: 'AS_IN',
  /** Azerbaijani */
  Az: 'AZ',
  /** Azerbaijani (Cyrillic) */
  AzCyrl: 'AZ_CYRL',
  /** Azerbaijani (Cyrillic, Azerbaijan) */
  AzCyrlAz: 'AZ_CYRL_AZ',
  /** Azerbaijani (Latin) */
  AzLatn: 'AZ_LATN',
  /** Azerbaijani (Latin, Azerbaijan) */
  AzLatnAz: 'AZ_LATN_AZ',
  /** Basaa */
  Bas: 'BAS',
  /** Basaa (Cameroon) */
  BasCm: 'BAS_CM',
  /** Belarusian */
  Be: 'BE',
  /** Bemba */
  Bem: 'BEM',
  /** Bemba (Zambia) */
  BemZm: 'BEM_ZM',
  /** Bena */
  Bez: 'BEZ',
  /** Bena (Tanzania) */
  BezTz: 'BEZ_TZ',
  /** Belarusian (Belarus) */
  BeBy: 'BE_BY',
  /** Bulgarian */
  Bg: 'BG',
  /** Bulgarian (Bulgaria) */
  BgBg: 'BG_BG',
  /** Bambara */
  Bm: 'BM',
  /** Bambara (Mali) */
  BmMl: 'BM_ML',
  /** Bangla */
  Bn: 'BN',
  /** Bangla (Bangladesh) */
  BnBd: 'BN_BD',
  /** Bangla (India) */
  BnIn: 'BN_IN',
  /** Tibetan */
  Bo: 'BO',
  /** Tibetan (China) */
  BoCn: 'BO_CN',
  /** Tibetan (India) */
  BoIn: 'BO_IN',
  /** Breton */
  Br: 'BR',
  /** Bodo */
  Brx: 'BRX',
  /** Bodo (India) */
  BrxIn: 'BRX_IN',
  /** Breton (France) */
  BrFr: 'BR_FR',
  /** Bosnian */
  Bs: 'BS',
  /** Bosnian (Cyrillic) */
  BsCyrl: 'BS_CYRL',
  /** Bosnian (Cyrillic, Bosnia & Herzegovina) */
  BsCyrlBa: 'BS_CYRL_BA',
  /** Bosnian (Latin) */
  BsLatn: 'BS_LATN',
  /** Bosnian (Latin, Bosnia & Herzegovina) */
  BsLatnBa: 'BS_LATN_BA',
  /** Catalan */
  Ca: 'CA',
  /** Catalan (Andorra) */
  CaAd: 'CA_AD',
  /** Catalan (Spain) */
  CaEs: 'CA_ES',
  /** Catalan (Spain, Valencian) */
  CaEsValencia: 'CA_ES_VALENCIA',
  /** Catalan (France) */
  CaFr: 'CA_FR',
  /** Catalan (Italy) */
  CaIt: 'CA_IT',
  /** Chakma */
  Ccp: 'CCP',
  /** Chakma (Bangladesh) */
  CcpBd: 'CCP_BD',
  /** Chakma (India) */
  CcpIn: 'CCP_IN',
  /** Chechen */
  Ce: 'CE',
  /** Cebuano */
  Ceb: 'CEB',
  /** Cebuano (Philippines) */
  CebPh: 'CEB_PH',
  /** Chechen (Russia) */
  CeRu: 'CE_RU',
  /** Chiga */
  Cgg: 'CGG',
  /** Chiga (Uganda) */
  CggUg: 'CGG_UG',
  /** Cherokee */
  Chr: 'CHR',
  /** Cherokee (United States) */
  ChrUs: 'CHR_US',
  /** Central Kurdish */
  Ckb: 'CKB',
  /** Central Kurdish (Iraq) */
  CkbIq: 'CKB_IQ',
  /** Central Kurdish (Iran) */
  CkbIr: 'CKB_IR',
  /** Czech */
  Cs: 'CS',
  /** Czech (Czechia) */
  CsCz: 'CS_CZ',
  /** Church Slavic */
  Cu: 'CU',
  /** Church Slavic (Russia) */
  CuRu: 'CU_RU',
  /** Welsh */
  Cy: 'CY',
  /** Welsh (United Kingdom) */
  CyGb: 'CY_GB',
  /** Danish */
  Da: 'DA',
  /** Taita */
  Dav: 'DAV',
  /** Taita (Kenya) */
  DavKe: 'DAV_KE',
  /** Danish (Denmark) */
  DaDk: 'DA_DK',
  /** Danish (Greenland) */
  DaGl: 'DA_GL',
  /** German */
  De: 'DE',
  /** German (Austria) */
  DeAt: 'DE_AT',
  /** German (Belgium) */
  DeBe: 'DE_BE',
  /** German (Switzerland) */
  DeCh: 'DE_CH',
  /** German (Germany) */
  DeDe: 'DE_DE',
  /** German (Italy) */
  DeIt: 'DE_IT',
  /** German (Liechtenstein) */
  DeLi: 'DE_LI',
  /** German (Luxembourg) */
  DeLu: 'DE_LU',
  /** Zarma */
  Dje: 'DJE',
  /** Zarma (Niger) */
  DjeNe: 'DJE_NE',
  /** Lower Sorbian */
  Dsb: 'DSB',
  /** Lower Sorbian (Germany) */
  DsbDe: 'DSB_DE',
  /** Duala */
  Dua: 'DUA',
  /** Duala (Cameroon) */
  DuaCm: 'DUA_CM',
  /** Jola-Fonyi */
  Dyo: 'DYO',
  /** Jola-Fonyi (Senegal) */
  DyoSn: 'DYO_SN',
  /** Dzongkha */
  Dz: 'DZ',
  /** Dzongkha (Bhutan) */
  DzBt: 'DZ_BT',
  /** Embu */
  Ebu: 'EBU',
  /** Embu (Kenya) */
  EbuKe: 'EBU_KE',
  /** Ewe */
  Ee: 'EE',
  /** Ewe (Ghana) */
  EeGh: 'EE_GH',
  /** Ewe (Togo) */
  EeTg: 'EE_TG',
  /** Greek */
  El: 'EL',
  /** Greek (Cyprus) */
  ElCy: 'EL_CY',
  /** Greek (Greece) */
  ElGr: 'EL_GR',
  /** English */
  En: 'EN',
  /** English (United Arab Emirates) */
  EnAe: 'EN_AE',
  /** English (Antigua & Barbuda) */
  EnAg: 'EN_AG',
  /** English (Anguilla) */
  EnAi: 'EN_AI',
  /** English (American Samoa) */
  EnAs: 'EN_AS',
  /** English (Austria) */
  EnAt: 'EN_AT',
  /** English (Australia) */
  EnAu: 'EN_AU',
  /** English (Barbados) */
  EnBb: 'EN_BB',
  /** English (Belgium) */
  EnBe: 'EN_BE',
  /** English (Burundi) */
  EnBi: 'EN_BI',
  /** English (Bermuda) */
  EnBm: 'EN_BM',
  /** English (Bahamas) */
  EnBs: 'EN_BS',
  /** English (Botswana) */
  EnBw: 'EN_BW',
  /** English (Belize) */
  EnBz: 'EN_BZ',
  /** English (Canada) */
  EnCa: 'EN_CA',
  /** English (Cocos (Keeling) Islands) */
  EnCc: 'EN_CC',
  /** English (Switzerland) */
  EnCh: 'EN_CH',
  /** English (Cook Islands) */
  EnCk: 'EN_CK',
  /** English (Cameroon) */
  EnCm: 'EN_CM',
  /** English (Christmas Island) */
  EnCx: 'EN_CX',
  /** English (Cyprus) */
  EnCy: 'EN_CY',
  /** English (Germany) */
  EnDe: 'EN_DE',
  /** English (Diego Garcia) */
  EnDg: 'EN_DG',
  /** English (Denmark) */
  EnDk: 'EN_DK',
  /** English (Dominica) */
  EnDm: 'EN_DM',
  /** English (Eritrea) */
  EnEr: 'EN_ER',
  /** English (Finland) */
  EnFi: 'EN_FI',
  /** English (Fiji) */
  EnFj: 'EN_FJ',
  /** English (Falkland Islands) */
  EnFk: 'EN_FK',
  /** English (Micronesia) */
  EnFm: 'EN_FM',
  /** English (United Kingdom) */
  EnGb: 'EN_GB',
  /** English (Grenada) */
  EnGd: 'EN_GD',
  /** English (Guernsey) */
  EnGg: 'EN_GG',
  /** English (Ghana) */
  EnGh: 'EN_GH',
  /** English (Gibraltar) */
  EnGi: 'EN_GI',
  /** English (Gambia) */
  EnGm: 'EN_GM',
  /** English (Guam) */
  EnGu: 'EN_GU',
  /** English (Guyana) */
  EnGy: 'EN_GY',
  /** English (Hong Kong SAR China) */
  EnHk: 'EN_HK',
  /** English (Ireland) */
  EnIe: 'EN_IE',
  /** English (Israel) */
  EnIl: 'EN_IL',
  /** English (Isle of Man) */
  EnIm: 'EN_IM',
  /** English (India) */
  EnIn: 'EN_IN',
  /** English (British Indian Ocean Territory) */
  EnIo: 'EN_IO',
  /** English (Jersey) */
  EnJe: 'EN_JE',
  /** English (Jamaica) */
  EnJm: 'EN_JM',
  /** English (Kenya) */
  EnKe: 'EN_KE',
  /** English (Kiribati) */
  EnKi: 'EN_KI',
  /** English (St. Kitts & Nevis) */
  EnKn: 'EN_KN',
  /** English (Cayman Islands) */
  EnKy: 'EN_KY',
  /** English (St. Lucia) */
  EnLc: 'EN_LC',
  /** English (Liberia) */
  EnLr: 'EN_LR',
  /** English (Lesotho) */
  EnLs: 'EN_LS',
  /** English (Madagascar) */
  EnMg: 'EN_MG',
  /** English (Marshall Islands) */
  EnMh: 'EN_MH',
  /** English (Macao SAR China) */
  EnMo: 'EN_MO',
  /** English (Northern Mariana Islands) */
  EnMp: 'EN_MP',
  /** English (Montserrat) */
  EnMs: 'EN_MS',
  /** English (Malta) */
  EnMt: 'EN_MT',
  /** English (Mauritius) */
  EnMu: 'EN_MU',
  /** English (Malawi) */
  EnMw: 'EN_MW',
  /** English (Malaysia) */
  EnMy: 'EN_MY',
  /** English (Namibia) */
  EnNa: 'EN_NA',
  /** English (Norfolk Island) */
  EnNf: 'EN_NF',
  /** English (Nigeria) */
  EnNg: 'EN_NG',
  /** English (Netherlands) */
  EnNl: 'EN_NL',
  /** English (Nauru) */
  EnNr: 'EN_NR',
  /** English (Niue) */
  EnNu: 'EN_NU',
  /** English (New Zealand) */
  EnNz: 'EN_NZ',
  /** English (Papua New Guinea) */
  EnPg: 'EN_PG',
  /** English (Philippines) */
  EnPh: 'EN_PH',
  /** English (Pakistan) */
  EnPk: 'EN_PK',
  /** English (Pitcairn Islands) */
  EnPn: 'EN_PN',
  /** English (Puerto Rico) */
  EnPr: 'EN_PR',
  /** English (Palau) */
  EnPw: 'EN_PW',
  /** English (Rwanda) */
  EnRw: 'EN_RW',
  /** English (Solomon Islands) */
  EnSb: 'EN_SB',
  /** English (Seychelles) */
  EnSc: 'EN_SC',
  /** English (Sudan) */
  EnSd: 'EN_SD',
  /** English (Sweden) */
  EnSe: 'EN_SE',
  /** English (Singapore) */
  EnSg: 'EN_SG',
  /** English (St. Helena) */
  EnSh: 'EN_SH',
  /** English (Slovenia) */
  EnSi: 'EN_SI',
  /** English (Sierra Leone) */
  EnSl: 'EN_SL',
  /** English (South Sudan) */
  EnSs: 'EN_SS',
  /** English (Sint Maarten) */
  EnSx: 'EN_SX',
  /** English (Eswatini) */
  EnSz: 'EN_SZ',
  /** English (Turks & Caicos Islands) */
  EnTc: 'EN_TC',
  /** English (Tokelau) */
  EnTk: 'EN_TK',
  /** English (Tonga) */
  EnTo: 'EN_TO',
  /** English (Trinidad & Tobago) */
  EnTt: 'EN_TT',
  /** English (Tuvalu) */
  EnTv: 'EN_TV',
  /** English (Tanzania) */
  EnTz: 'EN_TZ',
  /** English (Uganda) */
  EnUg: 'EN_UG',
  /** English (U.S. Outlying Islands) */
  EnUm: 'EN_UM',
  /** English (United States) */
  EnUs: 'EN_US',
  /** English (St. Vincent & Grenadines) */
  EnVc: 'EN_VC',
  /** English (British Virgin Islands) */
  EnVg: 'EN_VG',
  /** English (U.S. Virgin Islands) */
  EnVi: 'EN_VI',
  /** English (Vanuatu) */
  EnVu: 'EN_VU',
  /** English (Samoa) */
  EnWs: 'EN_WS',
  /** English (South Africa) */
  EnZa: 'EN_ZA',
  /** English (Zambia) */
  EnZm: 'EN_ZM',
  /** English (Zimbabwe) */
  EnZw: 'EN_ZW',
  /** Esperanto */
  Eo: 'EO',
  /** Spanish */
  Es: 'ES',
  /** Spanish (Argentina) */
  EsAr: 'ES_AR',
  /** Spanish (Bolivia) */
  EsBo: 'ES_BO',
  /** Spanish (Brazil) */
  EsBr: 'ES_BR',
  /** Spanish (Belize) */
  EsBz: 'ES_BZ',
  /** Spanish (Chile) */
  EsCl: 'ES_CL',
  /** Spanish (Colombia) */
  EsCo: 'ES_CO',
  /** Spanish (Costa Rica) */
  EsCr: 'ES_CR',
  /** Spanish (Cuba) */
  EsCu: 'ES_CU',
  /** Spanish (Dominican Republic) */
  EsDo: 'ES_DO',
  /** Spanish (Ceuta & Melilla) */
  EsEa: 'ES_EA',
  /** Spanish (Ecuador) */
  EsEc: 'ES_EC',
  /** Spanish (Spain) */
  EsEs: 'ES_ES',
  /** Spanish (Equatorial Guinea) */
  EsGq: 'ES_GQ',
  /** Spanish (Guatemala) */
  EsGt: 'ES_GT',
  /** Spanish (Honduras) */
  EsHn: 'ES_HN',
  /** Spanish (Canary Islands) */
  EsIc: 'ES_IC',
  /** Spanish (Mexico) */
  EsMx: 'ES_MX',
  /** Spanish (Nicaragua) */
  EsNi: 'ES_NI',
  /** Spanish (Panama) */
  EsPa: 'ES_PA',
  /** Spanish (Peru) */
  EsPe: 'ES_PE',
  /** Spanish (Philippines) */
  EsPh: 'ES_PH',
  /** Spanish (Puerto Rico) */
  EsPr: 'ES_PR',
  /** Spanish (Paraguay) */
  EsPy: 'ES_PY',
  /** Spanish (El Salvador) */
  EsSv: 'ES_SV',
  /** Spanish (United States) */
  EsUs: 'ES_US',
  /** Spanish (Uruguay) */
  EsUy: 'ES_UY',
  /** Spanish (Venezuela) */
  EsVe: 'ES_VE',
  /** Estonian */
  Et: 'ET',
  /** Estonian (Estonia) */
  EtEe: 'ET_EE',
  /** Basque */
  Eu: 'EU',
  /** Basque (Spain) */
  EuEs: 'EU_ES',
  /** Ewondo */
  Ewo: 'EWO',
  /** Ewondo (Cameroon) */
  EwoCm: 'EWO_CM',
  /** Persian */
  Fa: 'FA',
  /** Persian (Afghanistan) */
  FaAf: 'FA_AF',
  /** Persian (Iran) */
  FaIr: 'FA_IR',
  /** Fulah */
  Ff: 'FF',
  /** Fulah (Adlam) */
  FfAdlm: 'FF_ADLM',
  /** Fulah (Adlam, Burkina Faso) */
  FfAdlmBf: 'FF_ADLM_BF',
  /** Fulah (Adlam, Cameroon) */
  FfAdlmCm: 'FF_ADLM_CM',
  /** Fulah (Adlam, Ghana) */
  FfAdlmGh: 'FF_ADLM_GH',
  /** Fulah (Adlam, Gambia) */
  FfAdlmGm: 'FF_ADLM_GM',
  /** Fulah (Adlam, Guinea) */
  FfAdlmGn: 'FF_ADLM_GN',
  /** Fulah (Adlam, Guinea-Bissau) */
  FfAdlmGw: 'FF_ADLM_GW',
  /** Fulah (Adlam, Liberia) */
  FfAdlmLr: 'FF_ADLM_LR',
  /** Fulah (Adlam, Mauritania) */
  FfAdlmMr: 'FF_ADLM_MR',
  /** Fulah (Adlam, Niger) */
  FfAdlmNe: 'FF_ADLM_NE',
  /** Fulah (Adlam, Nigeria) */
  FfAdlmNg: 'FF_ADLM_NG',
  /** Fulah (Adlam, Sierra Leone) */
  FfAdlmSl: 'FF_ADLM_SL',
  /** Fulah (Adlam, Senegal) */
  FfAdlmSn: 'FF_ADLM_SN',
  /** Fulah (Latin) */
  FfLatn: 'FF_LATN',
  /** Fulah (Latin, Burkina Faso) */
  FfLatnBf: 'FF_LATN_BF',
  /** Fulah (Latin, Cameroon) */
  FfLatnCm: 'FF_LATN_CM',
  /** Fulah (Latin, Ghana) */
  FfLatnGh: 'FF_LATN_GH',
  /** Fulah (Latin, Gambia) */
  FfLatnGm: 'FF_LATN_GM',
  /** Fulah (Latin, Guinea) */
  FfLatnGn: 'FF_LATN_GN',
  /** Fulah (Latin, Guinea-Bissau) */
  FfLatnGw: 'FF_LATN_GW',
  /** Fulah (Latin, Liberia) */
  FfLatnLr: 'FF_LATN_LR',
  /** Fulah (Latin, Mauritania) */
  FfLatnMr: 'FF_LATN_MR',
  /** Fulah (Latin, Niger) */
  FfLatnNe: 'FF_LATN_NE',
  /** Fulah (Latin, Nigeria) */
  FfLatnNg: 'FF_LATN_NG',
  /** Fulah (Latin, Sierra Leone) */
  FfLatnSl: 'FF_LATN_SL',
  /** Fulah (Latin, Senegal) */
  FfLatnSn: 'FF_LATN_SN',
  /** Finnish */
  Fi: 'FI',
  /** Filipino */
  Fil: 'FIL',
  /** Filipino (Philippines) */
  FilPh: 'FIL_PH',
  /** Finnish (Finland) */
  FiFi: 'FI_FI',
  /** Faroese */
  Fo: 'FO',
  /** Faroese (Denmark) */
  FoDk: 'FO_DK',
  /** Faroese (Faroe Islands) */
  FoFo: 'FO_FO',
  /** French */
  Fr: 'FR',
  /** French (Belgium) */
  FrBe: 'FR_BE',
  /** French (Burkina Faso) */
  FrBf: 'FR_BF',
  /** French (Burundi) */
  FrBi: 'FR_BI',
  /** French (Benin) */
  FrBj: 'FR_BJ',
  /** French (St. Barthélemy) */
  FrBl: 'FR_BL',
  /** French (Canada) */
  FrCa: 'FR_CA',
  /** French (Congo - Kinshasa) */
  FrCd: 'FR_CD',
  /** French (Central African Republic) */
  FrCf: 'FR_CF',
  /** French (Congo - Brazzaville) */
  FrCg: 'FR_CG',
  /** French (Switzerland) */
  FrCh: 'FR_CH',
  /** French (Côte d’Ivoire) */
  FrCi: 'FR_CI',
  /** French (Cameroon) */
  FrCm: 'FR_CM',
  /** French (Djibouti) */
  FrDj: 'FR_DJ',
  /** French (Algeria) */
  FrDz: 'FR_DZ',
  /** French (France) */
  FrFr: 'FR_FR',
  /** French (Gabon) */
  FrGa: 'FR_GA',
  /** French (French Guiana) */
  FrGf: 'FR_GF',
  /** French (Guinea) */
  FrGn: 'FR_GN',
  /** French (Guadeloupe) */
  FrGp: 'FR_GP',
  /** French (Equatorial Guinea) */
  FrGq: 'FR_GQ',
  /** French (Haiti) */
  FrHt: 'FR_HT',
  /** French (Comoros) */
  FrKm: 'FR_KM',
  /** French (Luxembourg) */
  FrLu: 'FR_LU',
  /** French (Morocco) */
  FrMa: 'FR_MA',
  /** French (Monaco) */
  FrMc: 'FR_MC',
  /** French (St. Martin) */
  FrMf: 'FR_MF',
  /** French (Madagascar) */
  FrMg: 'FR_MG',
  /** French (Mali) */
  FrMl: 'FR_ML',
  /** French (Martinique) */
  FrMq: 'FR_MQ',
  /** French (Mauritania) */
  FrMr: 'FR_MR',
  /** French (Mauritius) */
  FrMu: 'FR_MU',
  /** French (New Caledonia) */
  FrNc: 'FR_NC',
  /** French (Niger) */
  FrNe: 'FR_NE',
  /** French (French Polynesia) */
  FrPf: 'FR_PF',
  /** French (St. Pierre & Miquelon) */
  FrPm: 'FR_PM',
  /** French (Réunion) */
  FrRe: 'FR_RE',
  /** French (Rwanda) */
  FrRw: 'FR_RW',
  /** French (Seychelles) */
  FrSc: 'FR_SC',
  /** French (Senegal) */
  FrSn: 'FR_SN',
  /** French (Syria) */
  FrSy: 'FR_SY',
  /** French (Chad) */
  FrTd: 'FR_TD',
  /** French (Togo) */
  FrTg: 'FR_TG',
  /** French (Tunisia) */
  FrTn: 'FR_TN',
  /** French (Vanuatu) */
  FrVu: 'FR_VU',
  /** French (Wallis & Futuna) */
  FrWf: 'FR_WF',
  /** French (Mayotte) */
  FrYt: 'FR_YT',
  /** Friulian */
  Fur: 'FUR',
  /** Friulian (Italy) */
  FurIt: 'FUR_IT',
  /** Western Frisian */
  Fy: 'FY',
  /** Western Frisian (Netherlands) */
  FyNl: 'FY_NL',
  /** Irish */
  Ga: 'GA',
  /** Irish (United Kingdom) */
  GaGb: 'GA_GB',
  /** Irish (Ireland) */
  GaIe: 'GA_IE',
  /** Scottish Gaelic */
  Gd: 'GD',
  /** Scottish Gaelic (United Kingdom) */
  GdGb: 'GD_GB',
  /** Galician */
  Gl: 'GL',
  /** Galician (Spain) */
  GlEs: 'GL_ES',
  /** Swiss German */
  Gsw: 'GSW',
  /** Swiss German (Switzerland) */
  GswCh: 'GSW_CH',
  /** Swiss German (France) */
  GswFr: 'GSW_FR',
  /** Swiss German (Liechtenstein) */
  GswLi: 'GSW_LI',
  /** Gujarati */
  Gu: 'GU',
  /** Gusii */
  Guz: 'GUZ',
  /** Gusii (Kenya) */
  GuzKe: 'GUZ_KE',
  /** Gujarati (India) */
  GuIn: 'GU_IN',
  /** Manx */
  Gv: 'GV',
  /** Manx (Isle of Man) */
  GvIm: 'GV_IM',
  /** Hausa */
  Ha: 'HA',
  /** Hawaiian */
  Haw: 'HAW',
  /** Hawaiian (United States) */
  HawUs: 'HAW_US',
  /** Hausa (Ghana) */
  HaGh: 'HA_GH',
  /** Hausa (Niger) */
  HaNe: 'HA_NE',
  /** Hausa (Nigeria) */
  HaNg: 'HA_NG',
  /** Hebrew */
  He: 'HE',
  /** Hebrew (Israel) */
  HeIl: 'HE_IL',
  /** Hindi */
  Hi: 'HI',
  /** Hindi (India) */
  HiIn: 'HI_IN',
  /** Croatian */
  Hr: 'HR',
  /** Croatian (Bosnia & Herzegovina) */
  HrBa: 'HR_BA',
  /** Croatian (Croatia) */
  HrHr: 'HR_HR',
  /** Upper Sorbian */
  Hsb: 'HSB',
  /** Upper Sorbian (Germany) */
  HsbDe: 'HSB_DE',
  /** Hungarian */
  Hu: 'HU',
  /** Hungarian (Hungary) */
  HuHu: 'HU_HU',
  /** Armenian */
  Hy: 'HY',
  /** Armenian (Armenia) */
  HyAm: 'HY_AM',
  /** Interlingua */
  Ia: 'IA',
  /** Indonesian */
  Id: 'ID',
  /** Indonesian (Indonesia) */
  IdId: 'ID_ID',
  /** Igbo */
  Ig: 'IG',
  /** Igbo (Nigeria) */
  IgNg: 'IG_NG',
  /** Sichuan Yi */
  Ii: 'II',
  /** Sichuan Yi (China) */
  IiCn: 'II_CN',
  /** Icelandic */
  Is: 'IS',
  /** Icelandic (Iceland) */
  IsIs: 'IS_IS',
  /** Italian */
  It: 'IT',
  /** Italian (Switzerland) */
  ItCh: 'IT_CH',
  /** Italian (Italy) */
  ItIt: 'IT_IT',
  /** Italian (San Marino) */
  ItSm: 'IT_SM',
  /** Italian (Vatican City) */
  ItVa: 'IT_VA',
  /** Japanese */
  Ja: 'JA',
  /** Japanese (Japan) */
  JaJp: 'JA_JP',
  /** Ngomba */
  Jgo: 'JGO',
  /** Ngomba (Cameroon) */
  JgoCm: 'JGO_CM',
  /** Machame */
  Jmc: 'JMC',
  /** Machame (Tanzania) */
  JmcTz: 'JMC_TZ',
  /** Javanese */
  Jv: 'JV',
  /** Javanese (Indonesia) */
  JvId: 'JV_ID',
  /** Georgian */
  Ka: 'KA',
  /** Kabyle */
  Kab: 'KAB',
  /** Kabyle (Algeria) */
  KabDz: 'KAB_DZ',
  /** Kamba */
  Kam: 'KAM',
  /** Kamba (Kenya) */
  KamKe: 'KAM_KE',
  /** Georgian (Georgia) */
  KaGe: 'KA_GE',
  /** Makonde */
  Kde: 'KDE',
  /** Makonde (Tanzania) */
  KdeTz: 'KDE_TZ',
  /** Kabuverdianu */
  Kea: 'KEA',
  /** Kabuverdianu (Cape Verde) */
  KeaCv: 'KEA_CV',
  /** Koyra Chiini */
  Khq: 'KHQ',
  /** Koyra Chiini (Mali) */
  KhqMl: 'KHQ_ML',
  /** Kikuyu */
  Ki: 'KI',
  /** Kikuyu (Kenya) */
  KiKe: 'KI_KE',
  /** Kazakh */
  Kk: 'KK',
  /** Kako */
  Kkj: 'KKJ',
  /** Kako (Cameroon) */
  KkjCm: 'KKJ_CM',
  /** Kazakh (Kazakhstan) */
  KkKz: 'KK_KZ',
  /** Kalaallisut */
  Kl: 'KL',
  /** Kalenjin */
  Kln: 'KLN',
  /** Kalenjin (Kenya) */
  KlnKe: 'KLN_KE',
  /** Kalaallisut (Greenland) */
  KlGl: 'KL_GL',
  /** Khmer */
  Km: 'KM',
  /** Khmer (Cambodia) */
  KmKh: 'KM_KH',
  /** Kannada */
  Kn: 'KN',
  /** Kannada (India) */
  KnIn: 'KN_IN',
  /** Korean */
  Ko: 'KO',
  /** Konkani */
  Kok: 'KOK',
  /** Konkani (India) */
  KokIn: 'KOK_IN',
  /** Korean (North Korea) */
  KoKp: 'KO_KP',
  /** Korean (South Korea) */
  KoKr: 'KO_KR',
  /** Kashmiri */
  Ks: 'KS',
  /** Shambala */
  Ksb: 'KSB',
  /** Shambala (Tanzania) */
  KsbTz: 'KSB_TZ',
  /** Bafia */
  Ksf: 'KSF',
  /** Bafia (Cameroon) */
  KsfCm: 'KSF_CM',
  /** Colognian */
  Ksh: 'KSH',
  /** Colognian (Germany) */
  KshDe: 'KSH_DE',
  /** Kashmiri (Arabic) */
  KsArab: 'KS_ARAB',
  /** Kashmiri (Arabic, India) */
  KsArabIn: 'KS_ARAB_IN',
  /** Kurdish */
  Ku: 'KU',
  /** Kurdish (Turkey) */
  KuTr: 'KU_TR',
  /** Cornish */
  Kw: 'KW',
  /** Cornish (United Kingdom) */
  KwGb: 'KW_GB',
  /** Kyrgyz */
  Ky: 'KY',
  /** Kyrgyz (Kyrgyzstan) */
  KyKg: 'KY_KG',
  /** Langi */
  Lag: 'LAG',
  /** Langi (Tanzania) */
  LagTz: 'LAG_TZ',
  /** Luxembourgish */
  Lb: 'LB',
  /** Luxembourgish (Luxembourg) */
  LbLu: 'LB_LU',
  /** Ganda */
  Lg: 'LG',
  /** Ganda (Uganda) */
  LgUg: 'LG_UG',
  /** Lakota */
  Lkt: 'LKT',
  /** Lakota (United States) */
  LktUs: 'LKT_US',
  /** Lingala */
  Ln: 'LN',
  /** Lingala (Angola) */
  LnAo: 'LN_AO',
  /** Lingala (Congo - Kinshasa) */
  LnCd: 'LN_CD',
  /** Lingala (Central African Republic) */
  LnCf: 'LN_CF',
  /** Lingala (Congo - Brazzaville) */
  LnCg: 'LN_CG',
  /** Lao */
  Lo: 'LO',
  /** Lao (Laos) */
  LoLa: 'LO_LA',
  /** Northern Luri */
  Lrc: 'LRC',
  /** Northern Luri (Iraq) */
  LrcIq: 'LRC_IQ',
  /** Northern Luri (Iran) */
  LrcIr: 'LRC_IR',
  /** Lithuanian */
  Lt: 'LT',
  /** Lithuanian (Lithuania) */
  LtLt: 'LT_LT',
  /** Luba-Katanga */
  Lu: 'LU',
  /** Luo */
  Luo: 'LUO',
  /** Luo (Kenya) */
  LuoKe: 'LUO_KE',
  /** Luyia */
  Luy: 'LUY',
  /** Luyia (Kenya) */
  LuyKe: 'LUY_KE',
  /** Luba-Katanga (Congo - Kinshasa) */
  LuCd: 'LU_CD',
  /** Latvian */
  Lv: 'LV',
  /** Latvian (Latvia) */
  LvLv: 'LV_LV',
  /** Maithili */
  Mai: 'MAI',
  /** Maithili (India) */
  MaiIn: 'MAI_IN',
  /** Masai */
  Mas: 'MAS',
  /** Masai (Kenya) */
  MasKe: 'MAS_KE',
  /** Masai (Tanzania) */
  MasTz: 'MAS_TZ',
  /** Meru */
  Mer: 'MER',
  /** Meru (Kenya) */
  MerKe: 'MER_KE',
  /** Morisyen */
  Mfe: 'MFE',
  /** Morisyen (Mauritius) */
  MfeMu: 'MFE_MU',
  /** Malagasy */
  Mg: 'MG',
  /** Makhuwa-Meetto */
  Mgh: 'MGH',
  /** Makhuwa-Meetto (Mozambique) */
  MghMz: 'MGH_MZ',
  /** Metaʼ */
  Mgo: 'MGO',
  /** Metaʼ (Cameroon) */
  MgoCm: 'MGO_CM',
  /** Malagasy (Madagascar) */
  MgMg: 'MG_MG',
  /** Maori */
  Mi: 'MI',
  /** Maori (New Zealand) */
  MiNz: 'MI_NZ',
  /** Macedonian */
  Mk: 'MK',
  /** Macedonian (North Macedonia) */
  MkMk: 'MK_MK',
  /** Malayalam */
  Ml: 'ML',
  /** Malayalam (India) */
  MlIn: 'ML_IN',
  /** Mongolian */
  Mn: 'MN',
  /** Manipuri */
  Mni: 'MNI',
  /** Manipuri (Bangla) */
  MniBeng: 'MNI_BENG',
  /** Manipuri (Bangla, India) */
  MniBengIn: 'MNI_BENG_IN',
  /** Mongolian (Mongolia) */
  MnMn: 'MN_MN',
  /** Marathi */
  Mr: 'MR',
  /** Marathi (India) */
  MrIn: 'MR_IN',
  /** Malay */
  Ms: 'MS',
  /** Malay (Brunei) */
  MsBn: 'MS_BN',
  /** Malay (Indonesia) */
  MsId: 'MS_ID',
  /** Malay (Malaysia) */
  MsMy: 'MS_MY',
  /** Malay (Singapore) */
  MsSg: 'MS_SG',
  /** Maltese */
  Mt: 'MT',
  /** Maltese (Malta) */
  MtMt: 'MT_MT',
  /** Mundang */
  Mua: 'MUA',
  /** Mundang (Cameroon) */
  MuaCm: 'MUA_CM',
  /** Burmese */
  My: 'MY',
  /** Burmese (Myanmar (Burma)) */
  MyMm: 'MY_MM',
  /** Mazanderani */
  Mzn: 'MZN',
  /** Mazanderani (Iran) */
  MznIr: 'MZN_IR',
  /** Nama */
  Naq: 'NAQ',
  /** Nama (Namibia) */
  NaqNa: 'NAQ_NA',
  /** Norwegian Bokmål */
  Nb: 'NB',
  /** Norwegian Bokmål (Norway) */
  NbNo: 'NB_NO',
  /** Norwegian Bokmål (Svalbard & Jan Mayen) */
  NbSj: 'NB_SJ',
  /** North Ndebele */
  Nd: 'ND',
  /** Low German */
  Nds: 'NDS',
  /** Low German (Germany) */
  NdsDe: 'NDS_DE',
  /** Low German (Netherlands) */
  NdsNl: 'NDS_NL',
  /** North Ndebele (Zimbabwe) */
  NdZw: 'ND_ZW',
  /** Nepali */
  Ne: 'NE',
  /** Nepali (India) */
  NeIn: 'NE_IN',
  /** Nepali (Nepal) */
  NeNp: 'NE_NP',
  /** Dutch */
  Nl: 'NL',
  /** Dutch (Aruba) */
  NlAw: 'NL_AW',
  /** Dutch (Belgium) */
  NlBe: 'NL_BE',
  /** Dutch (Caribbean Netherlands) */
  NlBq: 'NL_BQ',
  /** Dutch (Curaçao) */
  NlCw: 'NL_CW',
  /** Dutch (Netherlands) */
  NlNl: 'NL_NL',
  /** Dutch (Suriname) */
  NlSr: 'NL_SR',
  /** Dutch (Sint Maarten) */
  NlSx: 'NL_SX',
  /** Kwasio */
  Nmg: 'NMG',
  /** Kwasio (Cameroon) */
  NmgCm: 'NMG_CM',
  /** Norwegian Nynorsk */
  Nn: 'NN',
  /** Ngiemboon */
  Nnh: 'NNH',
  /** Ngiemboon (Cameroon) */
  NnhCm: 'NNH_CM',
  /** Norwegian Nynorsk (Norway) */
  NnNo: 'NN_NO',
  /** Nuer */
  Nus: 'NUS',
  /** Nuer (South Sudan) */
  NusSs: 'NUS_SS',
  /** Nyankole */
  Nyn: 'NYN',
  /** Nyankole (Uganda) */
  NynUg: 'NYN_UG',
  /** Oromo */
  Om: 'OM',
  /** Oromo (Ethiopia) */
  OmEt: 'OM_ET',
  /** Oromo (Kenya) */
  OmKe: 'OM_KE',
  /** Odia */
  Or: 'OR',
  /** Odia (India) */
  OrIn: 'OR_IN',
  /** Ossetic */
  Os: 'OS',
  /** Ossetic (Georgia) */
  OsGe: 'OS_GE',
  /** Ossetic (Russia) */
  OsRu: 'OS_RU',
  /** Punjabi */
  Pa: 'PA',
  /** Punjabi (Arabic) */
  PaArab: 'PA_ARAB',
  /** Punjabi (Arabic, Pakistan) */
  PaArabPk: 'PA_ARAB_PK',
  /** Punjabi (Gurmukhi) */
  PaGuru: 'PA_GURU',
  /** Punjabi (Gurmukhi, India) */
  PaGuruIn: 'PA_GURU_IN',
  /** Nigerian Pidgin */
  Pcm: 'PCM',
  /** Nigerian Pidgin (Nigeria) */
  PcmNg: 'PCM_NG',
  /** Polish */
  Pl: 'PL',
  /** Polish (Poland) */
  PlPl: 'PL_PL',
  /** Prussian */
  Prg: 'PRG',
  /** Pashto */
  Ps: 'PS',
  /** Pashto (Afghanistan) */
  PsAf: 'PS_AF',
  /** Pashto (Pakistan) */
  PsPk: 'PS_PK',
  /** Portuguese */
  Pt: 'PT',
  /** Portuguese (Angola) */
  PtAo: 'PT_AO',
  /** Portuguese (Brazil) */
  PtBr: 'PT_BR',
  /** Portuguese (Switzerland) */
  PtCh: 'PT_CH',
  /** Portuguese (Cape Verde) */
  PtCv: 'PT_CV',
  /** Portuguese (Equatorial Guinea) */
  PtGq: 'PT_GQ',
  /** Portuguese (Guinea-Bissau) */
  PtGw: 'PT_GW',
  /** Portuguese (Luxembourg) */
  PtLu: 'PT_LU',
  /** Portuguese (Macao SAR China) */
  PtMo: 'PT_MO',
  /** Portuguese (Mozambique) */
  PtMz: 'PT_MZ',
  /** Portuguese (Portugal) */
  PtPt: 'PT_PT',
  /** Portuguese (São Tomé & Príncipe) */
  PtSt: 'PT_ST',
  /** Portuguese (Timor-Leste) */
  PtTl: 'PT_TL',
  /** Quechua */
  Qu: 'QU',
  /** Quechua (Bolivia) */
  QuBo: 'QU_BO',
  /** Quechua (Ecuador) */
  QuEc: 'QU_EC',
  /** Quechua (Peru) */
  QuPe: 'QU_PE',
  /** Romansh */
  Rm: 'RM',
  /** Romansh (Switzerland) */
  RmCh: 'RM_CH',
  /** Rundi */
  Rn: 'RN',
  /** Rundi (Burundi) */
  RnBi: 'RN_BI',
  /** Romanian */
  Ro: 'RO',
  /** Rombo */
  Rof: 'ROF',
  /** Rombo (Tanzania) */
  RofTz: 'ROF_TZ',
  /** Romanian (Moldova) */
  RoMd: 'RO_MD',
  /** Romanian (Romania) */
  RoRo: 'RO_RO',
  /** Russian */
  Ru: 'RU',
  /** Russian (Belarus) */
  RuBy: 'RU_BY',
  /** Russian (Kyrgyzstan) */
  RuKg: 'RU_KG',
  /** Russian (Kazakhstan) */
  RuKz: 'RU_KZ',
  /** Russian (Moldova) */
  RuMd: 'RU_MD',
  /** Russian (Russia) */
  RuRu: 'RU_RU',
  /** Russian (Ukraine) */
  RuUa: 'RU_UA',
  /** Kinyarwanda */
  Rw: 'RW',
  /** Rwa */
  Rwk: 'RWK',
  /** Rwa (Tanzania) */
  RwkTz: 'RWK_TZ',
  /** Kinyarwanda (Rwanda) */
  RwRw: 'RW_RW',
  /** Sakha */
  Sah: 'SAH',
  /** Sakha (Russia) */
  SahRu: 'SAH_RU',
  /** Samburu */
  Saq: 'SAQ',
  /** Samburu (Kenya) */
  SaqKe: 'SAQ_KE',
  /** Santali */
  Sat: 'SAT',
  /** Santali (Ol Chiki) */
  SatOlck: 'SAT_OLCK',
  /** Santali (Ol Chiki, India) */
  SatOlckIn: 'SAT_OLCK_IN',
  /** Sangu */
  Sbp: 'SBP',
  /** Sangu (Tanzania) */
  SbpTz: 'SBP_TZ',
  /** Sindhi */
  Sd: 'SD',
  /** Sindhi (Arabic) */
  SdArab: 'SD_ARAB',
  /** Sindhi (Arabic, Pakistan) */
  SdArabPk: 'SD_ARAB_PK',
  /** Sindhi (Devanagari) */
  SdDeva: 'SD_DEVA',
  /** Sindhi (Devanagari, India) */
  SdDevaIn: 'SD_DEVA_IN',
  /** Northern Sami */
  Se: 'SE',
  /** Sena */
  Seh: 'SEH',
  /** Sena (Mozambique) */
  SehMz: 'SEH_MZ',
  /** Koyraboro Senni */
  Ses: 'SES',
  /** Koyraboro Senni (Mali) */
  SesMl: 'SES_ML',
  /** Northern Sami (Finland) */
  SeFi: 'SE_FI',
  /** Northern Sami (Norway) */
  SeNo: 'SE_NO',
  /** Northern Sami (Sweden) */
  SeSe: 'SE_SE',
  /** Sango */
  Sg: 'SG',
  /** Sango (Central African Republic) */
  SgCf: 'SG_CF',
  /** Tachelhit */
  Shi: 'SHI',
  /** Tachelhit (Latin) */
  ShiLatn: 'SHI_LATN',
  /** Tachelhit (Latin, Morocco) */
  ShiLatnMa: 'SHI_LATN_MA',
  /** Tachelhit (Tifinagh) */
  ShiTfng: 'SHI_TFNG',
  /** Tachelhit (Tifinagh, Morocco) */
  ShiTfngMa: 'SHI_TFNG_MA',
  /** Sinhala */
  Si: 'SI',
  /** Sinhala (Sri Lanka) */
  SiLk: 'SI_LK',
  /** Slovak */
  Sk: 'SK',
  /** Slovak (Slovakia) */
  SkSk: 'SK_SK',
  /** Slovenian */
  Sl: 'SL',
  /** Slovenian (Slovenia) */
  SlSi: 'SL_SI',
  /** Inari Sami */
  Smn: 'SMN',
  /** Inari Sami (Finland) */
  SmnFi: 'SMN_FI',
  /** Shona */
  Sn: 'SN',
  /** Shona (Zimbabwe) */
  SnZw: 'SN_ZW',
  /** Somali */
  So: 'SO',
  /** Somali (Djibouti) */
  SoDj: 'SO_DJ',
  /** Somali (Ethiopia) */
  SoEt: 'SO_ET',
  /** Somali (Kenya) */
  SoKe: 'SO_KE',
  /** Somali (Somalia) */
  SoSo: 'SO_SO',
  /** Albanian */
  Sq: 'SQ',
  /** Albanian (Albania) */
  SqAl: 'SQ_AL',
  /** Albanian (North Macedonia) */
  SqMk: 'SQ_MK',
  /** Albanian (Kosovo) */
  SqXk: 'SQ_XK',
  /** Serbian */
  Sr: 'SR',
  /** Serbian (Cyrillic) */
  SrCyrl: 'SR_CYRL',
  /** Serbian (Cyrillic, Bosnia & Herzegovina) */
  SrCyrlBa: 'SR_CYRL_BA',
  /** Serbian (Cyrillic, Montenegro) */
  SrCyrlMe: 'SR_CYRL_ME',
  /** Serbian (Cyrillic, Serbia) */
  SrCyrlRs: 'SR_CYRL_RS',
  /** Serbian (Cyrillic, Kosovo) */
  SrCyrlXk: 'SR_CYRL_XK',
  /** Serbian (Latin) */
  SrLatn: 'SR_LATN',
  /** Serbian (Latin, Bosnia & Herzegovina) */
  SrLatnBa: 'SR_LATN_BA',
  /** Serbian (Latin, Montenegro) */
  SrLatnMe: 'SR_LATN_ME',
  /** Serbian (Latin, Serbia) */
  SrLatnRs: 'SR_LATN_RS',
  /** Serbian (Latin, Kosovo) */
  SrLatnXk: 'SR_LATN_XK',
  /** Sundanese */
  Su: 'SU',
  /** Sundanese (Latin) */
  SuLatn: 'SU_LATN',
  /** Sundanese (Latin, Indonesia) */
  SuLatnId: 'SU_LATN_ID',
  /** Swedish */
  Sv: 'SV',
  /** Swedish (Åland Islands) */
  SvAx: 'SV_AX',
  /** Swedish (Finland) */
  SvFi: 'SV_FI',
  /** Swedish (Sweden) */
  SvSe: 'SV_SE',
  /** Swahili */
  Sw: 'SW',
  /** Swahili (Congo - Kinshasa) */
  SwCd: 'SW_CD',
  /** Swahili (Kenya) */
  SwKe: 'SW_KE',
  /** Swahili (Tanzania) */
  SwTz: 'SW_TZ',
  /** Swahili (Uganda) */
  SwUg: 'SW_UG',
  /** Tamil */
  Ta: 'TA',
  /** Tamil (India) */
  TaIn: 'TA_IN',
  /** Tamil (Sri Lanka) */
  TaLk: 'TA_LK',
  /** Tamil (Malaysia) */
  TaMy: 'TA_MY',
  /** Tamil (Singapore) */
  TaSg: 'TA_SG',
  /** Telugu */
  Te: 'TE',
  /** Teso */
  Teo: 'TEO',
  /** Teso (Kenya) */
  TeoKe: 'TEO_KE',
  /** Teso (Uganda) */
  TeoUg: 'TEO_UG',
  /** Telugu (India) */
  TeIn: 'TE_IN',
  /** Tajik */
  Tg: 'TG',
  /** Tajik (Tajikistan) */
  TgTj: 'TG_TJ',
  /** Thai */
  Th: 'TH',
  /** Thai (Thailand) */
  ThTh: 'TH_TH',
  /** Tigrinya */
  Ti: 'TI',
  /** Tigrinya (Eritrea) */
  TiEr: 'TI_ER',
  /** Tigrinya (Ethiopia) */
  TiEt: 'TI_ET',
  /** Turkmen */
  Tk: 'TK',
  /** Turkmen (Turkmenistan) */
  TkTm: 'TK_TM',
  /** Tongan */
  To: 'TO',
  /** Tongan (Tonga) */
  ToTo: 'TO_TO',
  /** Turkish */
  Tr: 'TR',
  /** Turkish (Cyprus) */
  TrCy: 'TR_CY',
  /** Turkish (Turkey) */
  TrTr: 'TR_TR',
  /** Tatar */
  Tt: 'TT',
  /** Tatar (Russia) */
  TtRu: 'TT_RU',
  /** Tasawaq */
  Twq: 'TWQ',
  /** Tasawaq (Niger) */
  TwqNe: 'TWQ_NE',
  /** Central Atlas Tamazight */
  Tzm: 'TZM',
  /** Central Atlas Tamazight (Morocco) */
  TzmMa: 'TZM_MA',
  /** Uyghur */
  Ug: 'UG',
  /** Uyghur (China) */
  UgCn: 'UG_CN',
  /** Ukrainian */
  Uk: 'UK',
  /** Ukrainian (Ukraine) */
  UkUa: 'UK_UA',
  /** Urdu */
  Ur: 'UR',
  /** Urdu (India) */
  UrIn: 'UR_IN',
  /** Urdu (Pakistan) */
  UrPk: 'UR_PK',
  /** Uzbek */
  Uz: 'UZ',
  /** Uzbek (Arabic) */
  UzArab: 'UZ_ARAB',
  /** Uzbek (Arabic, Afghanistan) */
  UzArabAf: 'UZ_ARAB_AF',
  /** Uzbek (Cyrillic) */
  UzCyrl: 'UZ_CYRL',
  /** Uzbek (Cyrillic, Uzbekistan) */
  UzCyrlUz: 'UZ_CYRL_UZ',
  /** Uzbek (Latin) */
  UzLatn: 'UZ_LATN',
  /** Uzbek (Latin, Uzbekistan) */
  UzLatnUz: 'UZ_LATN_UZ',
  /** Vai */
  Vai: 'VAI',
  /** Vai (Latin) */
  VaiLatn: 'VAI_LATN',
  /** Vai (Latin, Liberia) */
  VaiLatnLr: 'VAI_LATN_LR',
  /** Vai (Vai) */
  VaiVaii: 'VAI_VAII',
  /** Vai (Vai, Liberia) */
  VaiVaiiLr: 'VAI_VAII_LR',
  /** Vietnamese */
  Vi: 'VI',
  /** Vietnamese (Vietnam) */
  ViVn: 'VI_VN',
  /** Volapük */
  Vo: 'VO',
  /** Vunjo */
  Vun: 'VUN',
  /** Vunjo (Tanzania) */
  VunTz: 'VUN_TZ',
  /** Walser */
  Wae: 'WAE',
  /** Walser (Switzerland) */
  WaeCh: 'WAE_CH',
  /** Wolof */
  Wo: 'WO',
  /** Wolof (Senegal) */
  WoSn: 'WO_SN',
  /** Xhosa */
  Xh: 'XH',
  /** Xhosa (South Africa) */
  XhZa: 'XH_ZA',
  /** Soga */
  Xog: 'XOG',
  /** Soga (Uganda) */
  XogUg: 'XOG_UG',
  /** Yangben */
  Yav: 'YAV',
  /** Yangben (Cameroon) */
  YavCm: 'YAV_CM',
  /** Yiddish */
  Yi: 'YI',
  /** Yoruba */
  Yo: 'YO',
  /** Yoruba (Benin) */
  YoBj: 'YO_BJ',
  /** Yoruba (Nigeria) */
  YoNg: 'YO_NG',
  /** Cantonese */
  Yue: 'YUE',
  /** Cantonese (Simplified) */
  YueHans: 'YUE_HANS',
  /** Cantonese (Simplified, China) */
  YueHansCn: 'YUE_HANS_CN',
  /** Cantonese (Traditional) */
  YueHant: 'YUE_HANT',
  /** Cantonese (Traditional, Hong Kong SAR China) */
  YueHantHk: 'YUE_HANT_HK',
  /** Standard Moroccan Tamazight */
  Zgh: 'ZGH',
  /** Standard Moroccan Tamazight (Morocco) */
  ZghMa: 'ZGH_MA',
  /** Chinese */
  Zh: 'ZH',
  /** Chinese (Simplified) */
  ZhHans: 'ZH_HANS',
  /** Chinese (Simplified, China) */
  ZhHansCn: 'ZH_HANS_CN',
  /** Chinese (Simplified, Hong Kong SAR China) */
  ZhHansHk: 'ZH_HANS_HK',
  /** Chinese (Simplified, Macao SAR China) */
  ZhHansMo: 'ZH_HANS_MO',
  /** Chinese (Simplified, Singapore) */
  ZhHansSg: 'ZH_HANS_SG',
  /** Chinese (Traditional) */
  ZhHant: 'ZH_HANT',
  /** Chinese (Traditional, Hong Kong SAR China) */
  ZhHantHk: 'ZH_HANT_HK',
  /** Chinese (Traditional, Macao SAR China) */
  ZhHantMo: 'ZH_HANT_MO',
  /** Chinese (Traditional, Taiwan) */
  ZhHantTw: 'ZH_HANT_TW',
  /** Zulu */
  Zu: 'ZU',
  /** Zulu (South Africa) */
  ZuZa: 'ZU_ZA'
} as const;

export type LanguageCodeEnum = typeof LanguageCodeEnum[keyof typeof LanguageCodeEnum];
/** Filter input for order lines data. */
export type LinesFilterInput = {
  /** Filter by metadata fields of order lines. */
  metadata?: MetadataFilterInput | null | undefined;
};

export const MetadataErrorCode = {
  GraphqlError: 'GRAPHQL_ERROR',
  Invalid: 'INVALID',
  NotFound: 'NOT_FOUND',
  NotUpdated: 'NOT_UPDATED',
  Required: 'REQUIRED'
} as const;

export type MetadataErrorCode = typeof MetadataErrorCode[keyof typeof MetadataErrorCode];
export type MetadataFilter = {
  /** Key of a metadata item. */
  key: string;
  /** Value of a metadata item. */
  value?: string | null | undefined;
};

/**
 * Allows filtering based on metadata key/value pairs.
 *
 *         Examples:
 *         - `{key: "size"}`
 *           Matches objects where the metadata key "size" exists, regardless of its value.
 *         - `{key: "color", value: {oneOf: ["blue", "green"]}}`
 *           Matches objects where the metadata key "color" is set to either "blue" or "green".
 *         - `{key: "status", value: {eq: "active"}}`
 *           Matches objects where the metadata key "status" is set to "active".
 *
 */
export type MetadataFilterInput = {
  /** Key to filter by. If not other fields provided - checking the existence of the key in metadata. */
  key: string;
  /** Value to filter by. */
  value?: MetadataValueFilterInput | null | undefined;
};

export type MetadataInput = {
  /** Key of a metadata item. */
  key: string;
  /** Value of a metadata item. */
  value: string;
};

/** Define the filtering options for metadata value fields. */
export type MetadataValueFilterInput = {
  /** The value equal to. */
  eq?: string | null | undefined;
  /** The value included in. */
  oneOf?: Array<string> | null | undefined;
};

export type NameTranslationInput = {
  name?: string | null | undefined;
};

/**
 * Determine a current authorize status for order.
 *
 *     We treat the order as fully authorized when the sum of authorized and charged funds
 *     cover the `order.total`-`order.totalGrantedRefund`.
 *     We treat the order as partially authorized when the sum of authorized and charged
 *     funds covers only part of the `order.total`-`order.totalGrantedRefund`.
 *     We treat the order as not authorized when the sum of authorized and charged funds is
 *     0.
 *
 *     NONE - the funds are not authorized
 *     PARTIAL - the funds that are authorized and charged don't cover fully the
 *     `order.total`-`order.totalGrantedRefund`
 *     FULL - the funds that are authorized and charged fully cover the
 *     `order.total`-`order.totalGrantedRefund`
 *
 */
export const OrderAuthorizeStatusEnum = {
  Full: 'FULL',
  None: 'NONE',
  Partial: 'PARTIAL'
} as const;

export type OrderAuthorizeStatusEnum = typeof OrderAuthorizeStatusEnum[keyof typeof OrderAuthorizeStatusEnum];
/** Filter by authorize status. */
export type OrderAuthorizeStatusEnumFilterInput = {
  /** The value equal to. */
  eq?: OrderAuthorizeStatusEnum | null | undefined;
  /** The value included in. */
  oneOf?: Array<OrderAuthorizeStatusEnum> | null | undefined;
};

/**
 * Determine the current charge status for the order.
 *
 *     An order is considered overcharged when the sum of the
 *     transactionItem's charge amounts exceeds the value of
 *     `order.total` - `order.totalGrantedRefund`.
 *     If the sum of the transactionItem's charge amounts equals
 *     `order.total` - `order.totalGrantedRefund`, we consider the order to be fully
 *     charged.
 *     If the sum of the transactionItem's charge amounts covers a part of the
 *     `order.total` - `order.totalGrantedRefund`, we treat the order as partially charged.
 *
 *     NONE - the funds are not charged.
 *     PARTIAL - the funds that are charged don't cover the
 *     `order.total`-`order.totalGrantedRefund`
 *     FULL - the funds that are charged fully cover the
 *     `order.total`-`order.totalGrantedRefund`
 *     OVERCHARGED - the charged funds are bigger than the
 *     `order.total`-`order.totalGrantedRefund`
 *
 */
export const OrderChargeStatusEnum = {
  Full: 'FULL',
  None: 'NONE',
  Overcharged: 'OVERCHARGED',
  Partial: 'PARTIAL'
} as const;

export type OrderChargeStatusEnum = typeof OrderChargeStatusEnum[keyof typeof OrderChargeStatusEnum];
/** Filter by charge status. */
export type OrderChargeStatusEnumFilterInput = {
  /** The value equal to. */
  eq?: OrderChargeStatusEnum | null | undefined;
  /** The value included in. */
  oneOf?: Array<OrderChargeStatusEnum> | null | undefined;
};

export const OrderDirection = {
  /** Specifies an ascending sort order. */
  Asc: 'ASC',
  /** Specifies a descending sort order. */
  Desc: 'DESC'
} as const;

export type OrderDirection = typeof OrderDirection[keyof typeof OrderDirection];
export const OrderDiscountType = {
  Manual: 'MANUAL',
  OrderPromotion: 'ORDER_PROMOTION',
  Promotion: 'PROMOTION',
  Sale: 'SALE',
  Voucher: 'VOUCHER'
} as const;

export type OrderDiscountType = typeof OrderDiscountType[keyof typeof OrderDiscountType];
export const OrderErrorCode = {
  BillingAddressNotSet: 'BILLING_ADDRESS_NOT_SET',
  CannotCancelFulfillment: 'CANNOT_CANCEL_FULFILLMENT',
  CannotCancelOrder: 'CANNOT_CANCEL_ORDER',
  CannotDelete: 'CANNOT_DELETE',
  CannotDiscount: 'CANNOT_DISCOUNT',
  CannotFulfillUnpaidOrder: 'CANNOT_FULFILL_UNPAID_ORDER',
  CannotRefund: 'CANNOT_REFUND',
  CaptureInactivePayment: 'CAPTURE_INACTIVE_PAYMENT',
  ChannelInactive: 'CHANNEL_INACTIVE',
  DuplicatedInputItem: 'DUPLICATED_INPUT_ITEM',
  FulfillOrderLine: 'FULFILL_ORDER_LINE',
  GiftCardLine: 'GIFT_CARD_LINE',
  GraphqlError: 'GRAPHQL_ERROR',
  InsufficientStock: 'INSUFFICIENT_STOCK',
  Invalid: 'INVALID',
  InvalidQuantity: 'INVALID_QUANTITY',
  InvalidVoucher: 'INVALID_VOUCHER',
  InvalidVoucherCode: 'INVALID_VOUCHER_CODE',
  MissingAddressData: 'MISSING_ADDRESS_DATA',
  NonEditableGiftLine: 'NON_EDITABLE_GIFT_LINE',
  NonRemovableGiftLine: 'NON_REMOVABLE_GIFT_LINE',
  NotAvailableInChannel: 'NOT_AVAILABLE_IN_CHANNEL',
  NotEditable: 'NOT_EDITABLE',
  NotFound: 'NOT_FOUND',
  OrderNoShippingAddress: 'ORDER_NO_SHIPPING_ADDRESS',
  PaymentError: 'PAYMENT_ERROR',
  PaymentMissing: 'PAYMENT_MISSING',
  ProductNotPublished: 'PRODUCT_NOT_PUBLISHED',
  ProductUnavailableForPurchase: 'PRODUCT_UNAVAILABLE_FOR_PURCHASE',
  Required: 'REQUIRED',
  ShippingMethodNotApplicable: 'SHIPPING_METHOD_NOT_APPLICABLE',
  ShippingMethodRequired: 'SHIPPING_METHOD_REQUIRED',
  TaxError: 'TAX_ERROR',
  TransactionError: 'TRANSACTION_ERROR',
  Unique: 'UNIQUE',
  VoidInactivePayment: 'VOID_INACTIVE_PAYMENT',
  ZeroQuantity: 'ZERO_QUANTITY'
} as const;

export type OrderErrorCode = typeof OrderErrorCode[keyof typeof OrderErrorCode];
/** Filter input for order events data. */
export type OrderEventFilterInput = {
  /** Filter order events by date. */
  date?: DateTimeRangeInput | null | undefined;
  /** Filter order events by type. */
  type?: OrderEventTypeEnumFilterInput | null | undefined;
};

export type OrderEventTypeEnumFilterInput = {
  /** The value equal to. */
  eq?: OrderEventsEnum | null | undefined;
  /** The value included in. */
  oneOf?: Array<OrderEventsEnum> | null | undefined;
};

/** The different order event types.  */
export const OrderEventsEnum = {
  AddedProducts: 'ADDED_PRODUCTS',
  Canceled: 'CANCELED',
  Confirmed: 'CONFIRMED',
  DraftCreated: 'DRAFT_CREATED',
  DraftCreatedFromReplace: 'DRAFT_CREATED_FROM_REPLACE',
  EmailSent: 'EMAIL_SENT',
  Expired: 'EXPIRED',
  ExternalServiceNotification: 'EXTERNAL_SERVICE_NOTIFICATION',
  FulfillmentAwaitsApproval: 'FULFILLMENT_AWAITS_APPROVAL',
  FulfillmentCanceled: 'FULFILLMENT_CANCELED',
  FulfillmentFulfilledItems: 'FULFILLMENT_FULFILLED_ITEMS',
  FulfillmentRefunded: 'FULFILLMENT_REFUNDED',
  FulfillmentReplaced: 'FULFILLMENT_REPLACED',
  FulfillmentRestockedItems: 'FULFILLMENT_RESTOCKED_ITEMS',
  FulfillmentReturned: 'FULFILLMENT_RETURNED',
  InvoiceGenerated: 'INVOICE_GENERATED',
  InvoiceRequested: 'INVOICE_REQUESTED',
  InvoiceSent: 'INVOICE_SENT',
  InvoiceUpdated: 'INVOICE_UPDATED',
  NoteAdded: 'NOTE_ADDED',
  NoteUpdated: 'NOTE_UPDATED',
  OrderDiscountAdded: 'ORDER_DISCOUNT_ADDED',
  OrderDiscountAutomaticallyUpdated: 'ORDER_DISCOUNT_AUTOMATICALLY_UPDATED',
  OrderDiscountDeleted: 'ORDER_DISCOUNT_DELETED',
  OrderDiscountUpdated: 'ORDER_DISCOUNT_UPDATED',
  OrderFullyPaid: 'ORDER_FULLY_PAID',
  OrderLineDiscountRemoved: 'ORDER_LINE_DISCOUNT_REMOVED',
  OrderLineDiscountUpdated: 'ORDER_LINE_DISCOUNT_UPDATED',
  OrderLineProductDeleted: 'ORDER_LINE_PRODUCT_DELETED',
  OrderLineVariantDeleted: 'ORDER_LINE_VARIANT_DELETED',
  OrderMarkedAsPaid: 'ORDER_MARKED_AS_PAID',
  OrderReplacementCreated: 'ORDER_REPLACEMENT_CREATED',
  Other: 'OTHER',
  OversoldItems: 'OVERSOLD_ITEMS',
  PaymentAuthorized: 'PAYMENT_AUTHORIZED',
  PaymentCaptured: 'PAYMENT_CAPTURED',
  PaymentFailed: 'PAYMENT_FAILED',
  PaymentRefunded: 'PAYMENT_REFUNDED',
  PaymentVoided: 'PAYMENT_VOIDED',
  Placed: 'PLACED',
  PlacedAutomaticallyFromPaidCheckout: 'PLACED_AUTOMATICALLY_FROM_PAID_CHECKOUT',
  PlacedFromDraft: 'PLACED_FROM_DRAFT',
  RemovedProducts: 'REMOVED_PRODUCTS',
  TrackingUpdated: 'TRACKING_UPDATED',
  TransactionCancelRequested: 'TRANSACTION_CANCEL_REQUESTED',
  TransactionChargeRequested: 'TRANSACTION_CHARGE_REQUESTED',
  TransactionEvent: 'TRANSACTION_EVENT',
  TransactionMarkAsPaidFailed: 'TRANSACTION_MARK_AS_PAID_FAILED',
  TransactionRefundRequested: 'TRANSACTION_REFUND_REQUESTED',
  UpdatedAddress: 'UPDATED_ADDRESS'
} as const;

export type OrderEventsEnum = typeof OrderEventsEnum[keyof typeof OrderEventsEnum];
export type OrderFilterInput = {
  authorizeStatus?: Array<OrderAuthorizeStatusEnum> | null | undefined;
  channels?: Array<string | number> | null | undefined;
  chargeStatus?: Array<OrderChargeStatusEnum> | null | undefined;
  checkoutIds?: Array<string | number> | null | undefined;
  checkoutTokens?: Array<string> | null | undefined;
  created?: DateRangeInput | null | undefined;
  customer?: string | null | undefined;
  giftCardBought?: boolean | null | undefined;
  giftCardUsed?: boolean | null | undefined;
  ids?: Array<string | number> | null | undefined;
  isClickAndCollect?: boolean | null | undefined;
  /**
   * Filter by orders containing a variant that is currently in preorder.
   *
   * DEPRECATED: this field will be removed. Preorder is deprecated and will be removed. Model pre-sales with regular stock instead: create the planned quantity in a warehouse, or set `trackInventory` to false to sell without a stock limit.
   */
  isPreorder?: boolean | null | undefined;
  metadata?: Array<MetadataFilter> | null | undefined;
  numbers?: Array<string> | null | undefined;
  /**
   * Filter orders by payment charge status.
   *
   * DEPRECATED: this field will be removed. The legacy Payments API is deprecated and will be removed. Use the Transactions API instead.
   */
  paymentStatus?: Array<PaymentChargeStatusEnum> | null | undefined;
  search?: string | null | undefined;
  status?: Array<OrderStatusFilter> | null | undefined;
  updatedAt?: DateTimeRangeInput | null | undefined;
};

export type OrderFulfillInput = {
  /** If true, then allow proceed fulfillment when stock is exceeded. */
  allowStockToBeExceeded?: boolean | null | undefined;
  /** List of items informing how to fulfill the order. */
  lines: Array<OrderFulfillLineInput>;
  /** If true, send an email notification to the customer. */
  notifyCustomer?: boolean | null | undefined;
  /** Fulfillment tracking number. */
  trackingNumber?: string | null | undefined;
};

export type OrderFulfillLineInput = {
  /** The ID of the order line. */
  orderLineId?: string | number | null | undefined;
  /** List of stock items to create. */
  stocks: Array<OrderFulfillStockInput>;
};

export type OrderFulfillStockInput = {
  /** The number of line items to be fulfilled from given warehouse. */
  quantity: number;
  /** ID of the warehouse from which the item will be fulfilled. */
  warehouse: string | number;
};

export const OrderStatus = {
  Canceled: 'CANCELED',
  Draft: 'DRAFT',
  Expired: 'EXPIRED',
  Fulfilled: 'FULFILLED',
  PartiallyFulfilled: 'PARTIALLY_FULFILLED',
  PartiallyReturned: 'PARTIALLY_RETURNED',
  Returned: 'RETURNED',
  Unconfirmed: 'UNCONFIRMED',
  Unfulfilled: 'UNFULFILLED'
} as const;

export type OrderStatus = typeof OrderStatus[keyof typeof OrderStatus];
/** Filter by order status. */
export type OrderStatusEnumFilterInput = {
  /** The value equal to. */
  eq?: OrderStatus | null | undefined;
  /** The value included in. */
  oneOf?: Array<OrderStatus> | null | undefined;
};

export const OrderStatusFilter = {
  Canceled: 'CANCELED',
  Fulfilled: 'FULFILLED',
  PartiallyFulfilled: 'PARTIALLY_FULFILLED',
  ReadyToCapture: 'READY_TO_CAPTURE',
  ReadyToFulfill: 'READY_TO_FULFILL',
  Unconfirmed: 'UNCONFIRMED',
  Unfulfilled: 'UNFULFILLED'
} as const;

export type OrderStatusFilter = typeof OrderStatusFilter[keyof typeof OrderStatusFilter];
export type OrderWhereInput = {
  /** List of conditions that must be met. */
  AND?: Array<OrderWhereInput> | null | undefined;
  /** A list of conditions of which at least one must be met. */
  OR?: Array<OrderWhereInput> | null | undefined;
  /** Filter by authorize status. */
  authorizeStatus?: OrderAuthorizeStatusEnumFilterInput | null | undefined;
  /** Filter by billing address of the order. */
  billingAddress?: AddressFilterInput | null | undefined;
  /** Filter by channel. */
  channelId?: GlobalIdFilterInput | null | undefined;
  /** Filter by charge status. */
  chargeStatus?: OrderChargeStatusEnumFilterInput | null | undefined;
  /** Filter by checkout id. */
  checkoutId?: GlobalIdFilterInput | null | undefined;
  /** Filter by checkout token. */
  checkoutToken?: UuidFilterInput | null | undefined;
  /** Filter order by created at date. */
  createdAt?: DateTimeRangeInput | null | undefined;
  /** Filter by order events. Each list item represents conditions that must be satisfied by a single object. The filter matches orders that have related objects meeting all specified groups of conditions. */
  events?: Array<OrderEventFilterInput> | null | undefined;
  /** Filter by fulfillment data associated with the order. Each list item represents conditions that must be satisfied by a single object. The filter matches orders that have related objects meeting all specified groups of conditions. */
  fulfillments?: Array<FulfillmentFilterInput> | null | undefined;
  /** Filter by whether the order has any fulfillments. */
  hasFulfillments?: boolean | null | undefined;
  /** Filter by whether the order has any invoices. */
  hasInvoices?: boolean | null | undefined;
  ids?: Array<string | number> | null | undefined;
  /** Filter by invoice data associated with the order. Each list item represents conditions that must be satisfied by a single object. The filter matches orders that have related objects meeting all specified groups of conditions. */
  invoices?: Array<InvoiceFilterInput> | null | undefined;
  /** Filter by whether the order uses the click and collect delivery method. */
  isClickAndCollect?: boolean | null | undefined;
  /** Filter based on whether the order includes a gift card purchase. */
  isGiftCardBought?: boolean | null | undefined;
  /** Filter based on whether a gift card was used in the order. */
  isGiftCardUsed?: boolean | null | undefined;
  /** Filter by line items associated with the order. Each list item represents conditions that must be satisfied by a single object. The filter matches orders that have related objects meeting all specified groups of conditions. */
  lines?: Array<LinesFilterInput> | null | undefined;
  /** Filter by number of lines in the order. */
  linesCount?: IntFilterInput | null | undefined;
  /** Filter by metadata fields. */
  metadata?: MetadataFilterInput | null | undefined;
  /** Filter by order number. */
  number?: IntFilterInput | null | undefined;
  /** Filter by the product type of related order lines. */
  productTypeId?: GlobalIdFilterInput | null | undefined;
  /** Filter by shipping address of the order. */
  shippingAddress?: AddressFilterInput | null | undefined;
  /** Filter by order status. */
  status?: OrderStatusEnumFilterInput | null | undefined;
  /** Filter by total gross amount of the order. */
  totalGross?: PriceFilterInput | null | undefined;
  /** Filter by total net amount of the order. */
  totalNet?: PriceFilterInput | null | undefined;
  /** Filter by transaction data associated with the order. Each list item represents conditions that must be satisfied by a single object. The filter matches orders that have related objects meeting all specified groups of conditions. */
  transactions?: Array<TransactionFilterInput> | null | undefined;
  /** Filter order by updated at date. */
  updatedAt?: DateTimeRangeInput | null | undefined;
  /** Filter by user. */
  user?: GlobalIdFilterInput | null | undefined;
  /** Filter by user email. */
  userEmail?: StringFilterInput | null | undefined;
  /** Filter by voucher code used in the order. */
  voucherCode?: StringFilterInput | null | undefined;
};

export type PageTranslationInput = {
  /**
   * Translated page content.
   *
   * Rich text format. For reference see https://editorjs.io/
   */
  content?: string | null | undefined;
  seoDescription?: string | null | undefined;
  seoTitle?: string | null | undefined;
  slug?: string | null | undefined;
  title?: string | null | undefined;
};

export const PaymentChargeStatusEnum = {
  Cancelled: 'CANCELLED',
  FullyCharged: 'FULLY_CHARGED',
  FullyRefunded: 'FULLY_REFUNDED',
  NotCharged: 'NOT_CHARGED',
  PartiallyCharged: 'PARTIALLY_CHARGED',
  PartiallyRefunded: 'PARTIALLY_REFUNDED',
  Pending: 'PENDING',
  Refused: 'REFUSED'
} as const;

export type PaymentChargeStatusEnum = typeof PaymentChargeStatusEnum[keyof typeof PaymentChargeStatusEnum];
export type PaymentMethodDetailsCardFilterInput = {
  /** Filter by payment method brand used to pay for the order. */
  brand?: StringFilterInput | null | undefined;
};

export type PaymentMethodDetailsFilterInput = {
  /** Filter by card details used to pay for the order. Skips `type` filter if provided. */
  card?: PaymentMethodDetailsCardFilterInput | null | undefined;
  /** Filter by payment method type used to pay for the order. */
  type?: PaymentMethodTypeEnumFilterInput | null | undefined;
};

/**
 * Represents possible payment method types.
 *
 *     The following types are possible:
 *     CARD - represents a card payment method.
 *     OTHER - represents any payment method that is not a card payment.
 *     GIFT_CARD - represents a gift card payment method.
 *
 *
 */
export const PaymentMethodTypeEnum = {
  Card: 'CARD',
  GiftCard: 'GIFT_CARD',
  Other: 'OTHER'
} as const;

export type PaymentMethodTypeEnum = typeof PaymentMethodTypeEnum[keyof typeof PaymentMethodTypeEnum];
export type PaymentMethodTypeEnumFilterInput = {
  /** The value equal to. */
  eq?: PaymentMethodTypeEnum | null | undefined;
  /** The value included in. */
  oneOf?: Array<PaymentMethodTypeEnum> | null | undefined;
};

export type PriceFilterInput = {
  /** The amount of the price to filter by. */
  amount: DecimalFilterInput;
  /** The currency of the price to filter by. */
  currency?: string | null | undefined;
};

export type PriceRangeInput = {
  /** Price greater than or equal to. */
  gte?: number | null | undefined;
  /** Price less than or equal to. */
  lte?: number | null | undefined;
};

export type ProductChannelListingAddInput = {
  /** List of variants to which the channel should be assigned. */
  addVariants?: Array<string | number> | null | undefined;
  /** A start date time from which a product will be available for purchase. When not set and `isAvailable` is set to True, the current day is assumed. */
  availableForPurchaseAt?: string | null | undefined;
  /**
   * A start date from which a product will be available for purchase. When not set and isAvailable is set to True, the current day is assumed.
   *
   * DEPRECATED: this field will be removed. Use `availableForPurchaseAt` field instead.
   */
  availableForPurchaseDate?: string | null | undefined;
  /** ID of a channel. */
  channelId: string | number;
  /** Determines if product should be available for purchase in this channel. This does not guarantee the availability of stock. When set to `False`, this product is still visible to customers, but it cannot be purchased. */
  isAvailableForPurchase?: boolean | null | undefined;
  /** Determines if object is visible to customers. */
  isPublished?: boolean | null | undefined;
  /**
   * Publication date. ISO 8601 standard.
   *
   * DEPRECATED: this field will be removed. Use `publishedAt` field instead.
   */
  publicationDate?: string | null | undefined;
  /** Publication date time. ISO 8601 standard. */
  publishedAt?: string | null | undefined;
  /** List of variants from which the channel should be unassigned. */
  removeVariants?: Array<string | number> | null | undefined;
  /** Determines if product is visible in product listings (doesn't apply to product collections). */
  visibleInListings?: boolean | null | undefined;
};

export type ProductChannelListingUpdateInput = {
  /** List of channels from which the product should be unassigned. */
  removeChannels?: Array<string | number> | null | undefined;
  /** List of channels to which the product should be assigned or updated. */
  updateChannels?: Array<ProductChannelListingAddInput> | null | undefined;
};

export const ProductErrorCode = {
  AlreadyExists: 'ALREADY_EXISTS',
  AttributeAlreadyAssigned: 'ATTRIBUTE_ALREADY_ASSIGNED',
  AttributeCannotBeAssigned: 'ATTRIBUTE_CANNOT_BE_ASSIGNED',
  AttributeVariantsDisabled: 'ATTRIBUTE_VARIANTS_DISABLED',
  CannotManageProductWithoutVariant: 'CANNOT_MANAGE_PRODUCT_WITHOUT_VARIANT',
  DuplicatedInputItem: 'DUPLICATED_INPUT_ITEM',
  FileSizeLimitExceeded: 'FILE_SIZE_LIMIT_EXCEEDED',
  GraphqlError: 'GRAPHQL_ERROR',
  Invalid: 'INVALID',
  InvalidFileType: 'INVALID_FILE_TYPE',
  InvalidPrice: 'INVALID_PRICE',
  MediaAlreadyAssigned: 'MEDIA_ALREADY_ASSIGNED',
  NotFound: 'NOT_FOUND',
  NotProductsImage: 'NOT_PRODUCTS_IMAGE',
  NotProductsVariant: 'NOT_PRODUCTS_VARIANT',
  PreorderVariantCannotBeDeactivated: 'PREORDER_VARIANT_CANNOT_BE_DEACTIVATED',
  ProductNotAssignedToChannel: 'PRODUCT_NOT_ASSIGNED_TO_CHANNEL',
  ProductWithoutCategory: 'PRODUCT_WITHOUT_CATEGORY',
  Required: 'REQUIRED',
  Unique: 'UNIQUE',
  UnsupportedMediaProvider: 'UNSUPPORTED_MEDIA_PROVIDER',
  UnsupportedMimeType: 'UNSUPPORTED_MIME_TYPE'
} as const;

export type ProductErrorCode = typeof ProductErrorCode[keyof typeof ProductErrorCode];
export type ProductFilterInput = {
  attributes?: Array<AttributeInput> | null | undefined;
  /** Filter by the date of availability for purchase. */
  availableFrom?: string | null | undefined;
  categories?: Array<string | number> | null | undefined;
  /**
   * Specifies the channel by which the data should be filtered.
   *
   * DEPRECATED: this field will be removed. Use root-level channel argument instead.
   */
  channel?: string | null | undefined;
  collections?: Array<string | number> | null | undefined;
  /** Filter on whether product is a gift card or not. */
  giftCard?: boolean | null | undefined;
  hasCategory?: boolean | null | undefined;
  /**
   * Filter by product with preordered variants.
   *
   * DEPRECATED: this field will be removed. Preorder is deprecated and will be removed. Model pre-sales with regular stock instead: create the planned quantity in a warehouse, or set `trackInventory` to false to sell without a stock limit.
   */
  hasPreorderedVariants?: boolean | null | undefined;
  ids?: Array<string | number> | null | undefined;
  /** Filter by availability for purchase. */
  isAvailable?: boolean | null | undefined;
  isPublished?: boolean | null | undefined;
  /** Filter by visibility in product listings. */
  isVisibleInListing?: boolean | null | undefined;
  metadata?: Array<MetadataFilter> | null | undefined;
  /** Filter by the lowest variant price after discounts. */
  minimalPrice?: PriceRangeInput | null | undefined;
  price?: PriceRangeInput | null | undefined;
  productTypes?: Array<string | number> | null | undefined;
  /** Filter by the publication date. */
  publishedFrom?: string | null | undefined;
  search?: string | null | undefined;
  slugs?: Array<string> | null | undefined;
  /** Filter by variants having specific stock status. */
  stockAvailability?: StockAvailability | null | undefined;
  stocks?: ProductStockFilterInput | null | undefined;
  /** Filter by when was the most recent update. */
  updatedAt?: DateTimeRangeInput | null | undefined;
};

export const ProductMediaType = {
  Image: 'IMAGE',
  Video: 'VIDEO'
} as const;

export type ProductMediaType = typeof ProductMediaType[keyof typeof ProductMediaType];
export type ProductOrder = {
  /**
   * Sort product by the selected attribute's values.
   * Note: this doesn't take translations into account yet.
   */
  attributeId?: string | number | null | undefined;
  /**
   * Specifies the channel in which to sort the data.
   *
   * DEPRECATED: this field will be removed. Use root-level channel argument instead.
   */
  channel?: string | null | undefined;
  /** Specifies the direction in which to sort products. */
  direction: OrderDirection;
  /** Sort products by the selected field. */
  field?: ProductOrderField | null | undefined;
};

export const ProductOrderField = {
  /**
   * Sort products by collection. Note: This option is available only for the `Collection.products` query.
   *
   * This option requires a channel filter to work as the values can vary between channels.
   */
  Collection: 'COLLECTION',
  /** Sort products by creation date. */
  CreatedAt: 'CREATED_AT',
  /** Sort products by update date. */
  Date: 'DATE',
  /** Sort products by update date. */
  LastModified: 'LAST_MODIFIED',
  /** Sort products by update date. */
  LastModifiedAt: 'LAST_MODIFIED_AT',
  /**
   * Sort products by a minimal price of a product's variant.
   *
   * This option requires a channel filter to work as the values can vary between channels.
   */
  MinimalPrice: 'MINIMAL_PRICE',
  /** Sort products by name. */
  Name: 'NAME',
  /**
   * Sort products by price.
   *
   * This option requires a channel filter to work as the values can vary between channels.
   */
  Price: 'PRICE',
  /**
   * Sort products by publication date.
   *
   * This option requires a channel filter to work as the values can vary between channels.
   */
  PublicationDate: 'PUBLICATION_DATE',
  /**
   * Sort products by publication status.
   *
   * This option requires a channel filter to work as the values can vary between channels.
   */
  Published: 'PUBLISHED',
  /**
   * Sort products by publication date.
   *
   * This option requires a channel filter to work as the values can vary between channels.
   */
  PublishedAt: 'PUBLISHED_AT',
  /** Sort products by rank. Note: This option is available only with the `search` filter. */
  Rank: 'RANK',
  /** Sort products by rating. */
  Rating: 'RATING',
  /** Sort products by type. */
  Type: 'TYPE'
} as const;

export type ProductOrderField = typeof ProductOrderField[keyof typeof ProductOrderField];
export type ProductStockFilterInput = {
  quantity?: IntRangeInput | null | undefined;
  warehouseIds?: Array<string | number> | null | undefined;
};

export const StockAvailability = {
  InStock: 'IN_STOCK',
  OutOfStock: 'OUT_OF_STOCK'
} as const;

export type StockAvailability = typeof StockAvailability[keyof typeof StockAvailability];
/** Define the filtering options for string fields. */
export type StringFilterInput = {
  /** The value equal to. */
  eq?: string | null | undefined;
  /** The value included in. */
  oneOf?: Array<string> | null | undefined;
};

/** Filter input for transactions. */
export type TransactionFilterInput = {
  /** Filter by metadata fields of transactions. */
  metadata?: MetadataFilterInput | null | undefined;
  /** Filter by payment method details used to pay for the order. */
  paymentMethodDetails?: PaymentMethodDetailsFilterInput | null | undefined;
  /**
   * Filter by PSP reference of transactions.
   *
   * Added in Saleor 3.22.
   */
  pspReference?: StringFilterInput | null | undefined;
};

export type TranslationInput = {
  /**
   * Translated description.
   *
   * Rich text format. For reference see https://editorjs.io/
   */
  description?: string | null | undefined;
  /**
   * Translated full description. Only supported for categories and collections; product translation mutations reject this field. Pass null to clear it. Omit to preserve the current value on update.
   *
   * Rich text format. For reference see https://editorjs.io/
   *
   * Added in Saleor 3.23.
   */
  fullDescription?: string | null | undefined;
  name?: string | null | undefined;
  seoDescription?: string | null | undefined;
  seoTitle?: string | null | undefined;
  slug?: string | null | undefined;
};

/** Define the filtering options for string fields. */
export type UuidFilterInput = {
  /** The value equal to. */
  eq?: string | null | undefined;
  /** The value included in. */
  oneOf?: Array<string> | null | undefined;
};

export type VoucherChannelListingAddInput = {
  /** ID of a channel. */
  channelId: string | number;
  /** Value of the voucher. */
  discountValue?: number | null | undefined;
  /** Min purchase amount required to apply the voucher. */
  minAmountSpent?: number | null | undefined;
};

export type VoucherChannelListingInput = {
  /** List of channels to which the voucher should be assigned. */
  addChannels?: Array<VoucherChannelListingAddInput> | null | undefined;
  /** List of channels from which the voucher should be unassigned. */
  removeChannels?: Array<string | number> | null | undefined;
};

export type VoucherInput = {
  /**
   * List of codes to add.
   *
   * Added in Saleor 3.18.
   *
   * Note: this API is currently in Feature Preview and can be subject to changes at later point.
   */
  addCodes?: Array<string> | null | undefined;
  /** Voucher should be applied once per customer. */
  applyOncePerCustomer?: boolean | null | undefined;
  /** Voucher should be applied to the cheapest item or entire order. */
  applyOncePerOrder?: boolean | null | undefined;
  /** Categories discounted by the voucher. */
  categories?: Array<string | number> | null | undefined;
  /**
   * Code to use the voucher.
   *
   * DEPRECATED: this field will be removed. Use `addCodes` instead.
   */
  code?: string | null | undefined;
  /** Collections discounted by the voucher. */
  collections?: Array<string | number> | null | undefined;
  /** Country codes that can be used with the shipping voucher. */
  countries?: Array<string> | null | undefined;
  /** Choices: fixed or percentage. */
  discountValueType?: DiscountValueTypeEnum | null | undefined;
  /** End date of the voucher in ISO 8601 format. */
  endDate?: string | null | undefined;
  /**
   * External ID of this voucher.
   *
   * Added in Saleor 3.23.
   */
  externalReference?: string | null | undefined;
  /** Minimal quantity of checkout items required to apply the voucher. */
  minCheckoutItemsQuantity?: number | null | undefined;
  /** Voucher name. */
  name?: string | null | undefined;
  /** Voucher can be used only by staff user. */
  onlyForStaff?: boolean | null | undefined;
  /** Products discounted by the voucher. */
  products?: Array<string | number> | null | undefined;
  /**
   * When set to 'True', each voucher code can be used only once; otherwise, codes can be used multiple times depending on `usageLimit`.
   *
   * The option can only be changed if none of the voucher codes have been used.
   *
   * Added in Saleor 3.18.
   *
   * Note: this API is currently in Feature Preview and can be subject to changes at later point.
   */
  singleUse?: boolean | null | undefined;
  /** Start date of the voucher in ISO 8601 format. */
  startDate?: string | null | undefined;
  /** Voucher type: PRODUCT, CATEGORY SHIPPING or ENTIRE_ORDER. */
  type?: VoucherTypeEnum | null | undefined;
  /** Limit number of times this voucher can be used in total. */
  usageLimit?: number | null | undefined;
  /** Variants discounted by the voucher. */
  variants?: Array<string | number> | null | undefined;
};

export const VoucherTypeEnum = {
  EntireOrder: 'ENTIRE_ORDER',
  Shipping: 'SHIPPING',
  SpecificProduct: 'SPECIFIC_PRODUCT'
} as const;

export type VoucherTypeEnum = typeof VoucherTypeEnum[keyof typeof VoucherTypeEnum];
export type AccountAddressFragmentFragment = { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } };

export type AccountErrorFragmentFragment = { message: string | null, field: string | null, code: AccountErrorCode };

export type AccountUserWithAddressesFragmentFragment = { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null };

export type AccountAddressDeleteMutationVariables = Exact<{
  id: string | number;
}>;


export type AccountAddressDeleteMutation = { accountAddressDelete: { user: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null, errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }>, address: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null };

export type AccountAddressUpdateMutationVariables = Exact<{
  id: string | number;
  address: AddressInput;
}>;


export type AccountAddressUpdateMutation = { accountAddressUpdate: { user: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null, errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }>, address: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null };

export type AccountAddressCreateMutationVariables = Exact<{
  address: AddressInput;
  type?: AddressTypeEnum | null | undefined;
}>;


export type AccountAddressCreateMutation = { accountAddressCreate: { user: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null, errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }>, address: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null };

export type AccountSetDefaultAddressMutationVariables = Exact<{
  id: string | number;
  type: AddressTypeEnum;
}>;


export type AccountSetDefaultAddressMutation = { accountSetDefaultAddress: { user: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null, errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }> } | null };

export type AccountConfirmMutationVariables = Exact<{
  email: string;
  token: string;
}>;


export type AccountConfirmMutation = { confirmAccount: { user: { id: string, email: string, isActive: boolean } | null, errors: Array<{ field: string | null, message: string | null, code: AccountErrorCode }> } | null };

export type BacCrossSellProductQueryVariables = Exact<{
  slug: string;
  channel: string;
}>;


export type BacCrossSellProductQuery = { product: { slug: string, thumbnail: { url: string } | null, variants: Array<{ id: string, name: string, pricing: { price: { gross: { amount: number, currency: string } } | null } | null }> | null } | null };

export type BlackFridayCheckoutQueryVariables = Exact<{
  id: string | number;
  includeReason: boolean;
}>;


export type BlackFridayCheckoutQuery = { checkout: { id: string, email: string | null, voucherCode: string | null, channel: { slug: string }, totalPrice: { gross: { amount: number, currency: string } }, lines: Array<{ id: string, quantity: number, priceOverrideReason?: string | null, metadata: Array<{ key: string, value: string }>, priorUnitPrice: { amount: number } | null, undiscountedUnitPrice: { amount: number }, variant: { id: string, product: { slug: string }, pricing: { priceUndiscounted: { net: { amount: number, currency: string } } | null } | null } }> } | null };

export type BlackFridayCheckoutLinesUpdateMutationVariables = Exact<{
  id: string | number;
  lines: Array<CheckoutLineUpdateInput> | CheckoutLineUpdateInput;
}>;


export type BlackFridayCheckoutLinesUpdateMutation = { checkoutLinesUpdate: { checkout: { id: string } | null, errors: Array<{ code: CheckoutErrorCode, field: string | null }> } | null };

export type BlackFridayCheckoutRemovePromoCodeMutationVariables = Exact<{
  id: string | number;
  promoCode: string;
}>;


export type BlackFridayCheckoutRemovePromoCodeMutation = { checkoutRemovePromoCode: { checkout: { id: string } | null, errors: Array<{ code: CheckoutErrorCode, field: string | null }> } | null };

export type BlackFridayProductsQueryVariables = Exact<{
  channel: string;
  languageCode: LanguageCodeEnum;
  slugs: Array<string> | string;
}>;


export type BlackFridayProductsQuery = { products: { edges: Array<{ node: { id: string, slug: string, name: string, isAvailable: boolean | null, isAvailableForPurchase: boolean | null, translation: { name: string | null } | null, thumbnail: { url: string, alt: string | null } | null, variants: Array<{ id: string, name: string, sku: string | null, quantityAvailable: number | null, pricing: { price: { gross: { amount: number, currency: string } } | null, priceUndiscounted: { gross: { amount: number, currency: string } } | null } | null }> | null } }> } | null };

export type CalculatorBuyBridgeProductQueryVariables = Exact<{
  slug: string;
  channel: string;
  languageCode: LanguageCodeEnum;
}>;


export type CalculatorBuyBridgeProductQuery = { product: { id: string, slug: string, name: string, isAvailable: boolean | null, translation: { name: string | null } | null, thumbnail: { url: string, alt: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null } | null } | null, variants: Array<{ id: string, name: string, quantityAvailable: number | null, pricing: { price: { gross: { amount: number, currency: string } } | null } | null }> | null } | null };

export type CategoriesBySlugQueryVariables = Exact<{
  slugs: Array<string> | string;
  first?: number | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type CategoriesBySlugQuery = { categories: { edges: Array<{ node: { id: string, slug: string, name: string, translation: { name: string | null } | null } }> } | null };

export type CategoryTranslateMutationVariables = Exact<{
  id: string | number;
  languageCode: LanguageCodeEnum;
  input: TranslationInput;
}>;


export type CategoryTranslateMutation = { categoryTranslate: { category: { id: string } | null, errors: Array<{ field: string | null, message: string | null }> } | null };

export type CategoryTreeForSwitcherQueryVariables = Exact<{
  languageCode: LanguageCodeEnum;
  channel: string;
}>;


export type CategoryTreeForSwitcherQuery = { category: { id: string, slug: string, children: { edges: Array<{ node: { id: string, slug: string, name: string, translation: { name: string | null } | null, products: { totalCount: number | null } | null } }> } | null } | null };

export type ChannelsListQueryVariables = Exact<{ [key: string]: never; }>;


export type ChannelsListQuery = { channels: Array<{ id: string, name: string, slug: string, isActive: boolean, currencyCode: string, countries: Array<{ country: string, code: string }> | null }> | null };

export type ChatwootPanelOrderFragment = { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, chargeStatus: OrderChargeStatusEnum, authorizeStatus: OrderAuthorizeStatusEnum, isPaid: boolean, userEmail: string | null, languageCode: string, user: { id: string, email: string } | null, channel: { slug: string, currencyCode: string }, total: { gross: { amount: number, currency: string } }, totalCharged: { amount: number, currency: string }, totalRefunded: { amount: number, currency: string }, totalRefundPending: { amount: number, currency: string }, shippingAddress: { city: string, country: { code: string, country: string } } | null, lines: Array<{ id: string, productName: string, variantName: string, productSku: string | null, quantity: number, quantityFulfilled: number, quantityToFulfill: number }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, created: string, trackingNumber: string, lines: Array<{ quantity: number, orderLine: { id: string } | null }> | null }> };

export type ChatwootPanelTrackingDetailsQueryVariables = Exact<{
  ids: Array<string | number> | string | number;
}>;


export type ChatwootPanelTrackingDetailsQuery = { orders: { edges: Array<{ node: { id: string, number: string, status: OrderStatus, userEmail: string | null, user: { email: string } | null, channel: { slug: string }, fulfillments: Array<{ id: string, created: string, status: FulfillmentStatus, trackingNumber: string, metadata: Array<{ key: string, value: string }> }> } }> } | null };

export type CheckoutAddLineMutationVariables = Exact<{
  id: string | number;
  productVariantId: string | number;
  quantity: number;
}>;


export type CheckoutAddLineMutation = { checkoutLinesAdd: { checkout: { id: string, lines: Array<{ id: string, quantity: number, unitPrice: { gross: { amount: number, currency: string } }, totalPrice: { gross: { amount: number, currency: string } }, variant: { id: string, name: string, sku: string | null, selectionAttributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null }> }>, product: { id: string, name: string, slug: string } } }> } | null, errors: Array<{ field: string | null, code: CheckoutErrorCode, message: string | null }> } | null };

export type CheckoutAddLinesMutationVariables = Exact<{
  id: string | number;
  lines: Array<CheckoutLineInput> | CheckoutLineInput;
}>;


export type CheckoutAddLinesMutation = { checkoutLinesAdd: { checkout: { id: string, lines: Array<{ id: string, quantity: number, unitPrice: { gross: { amount: number, currency: string } }, totalPrice: { gross: { amount: number, currency: string } }, variant: { id: string, name: string, selectionAttributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null }> }> } }> } | null, errors: Array<{ field: string | null, code: CheckoutErrorCode, message: string | null }> } | null };

export type CheckoutAddPromoCodeMutationVariables = Exact<{
  checkoutId?: string | number | null | undefined;
  promoCode: string;
}>;


export type CheckoutAddPromoCodeMutation = { checkoutAddPromoCode: { errors: Array<{ field: string | null, code: CheckoutErrorCode, message: string | null }>, checkout: { id: string, voucherCode: string | null, discount: { amount: number, currency: string } | null, totalPrice: { gross: { amount: number, currency: string } } } | null } | null };

export type CheckoutCreateMutationVariables = Exact<{
  channel: string;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutCreateMutation = { checkoutCreate: { checkout: { id: string, email: string | null, lines: Array<{ id: string, quantity: number, totalPrice: { gross: { amount: number, currency: string } }, variant: { name: string, id: string, product: { id: string, name: string, slug: string, thumbnail: { url: string, alt: string | null } | null, category: { name: string } | null }, pricing: { price: { gross: { amount: number, currency: string } } | null } | null } }>, totalPrice: { gross: { amount: number, currency: string } } } | null, errors: Array<{ field: string | null, code: CheckoutErrorCode }> } | null };

export type CheckoutDeleteLinesMutationVariables = Exact<{
  checkoutId: string | number;
  lineIds: Array<string | number> | string | number;
}>;


export type CheckoutDeleteLinesMutation = { checkoutLinesDelete: { checkout: { id: string, lines: Array<{ id: string }>, channel: { slug: string } } | null, errors: Array<{ field: string | null, code: CheckoutErrorCode }> } | null };

export type CheckoutFindQueryVariables = Exact<{
  id: string | number;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutFindQuery = { checkout: { id: string, email: string | null, isShippingRequired: boolean, voucherCode: string | null, shippingAddress: { country: { code: string } } | null, problems: Array<
      | { __typename: 'CheckoutLineProblemInsufficientStock' }
      | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
      | { __typename: 'CheckoutProblemDeliveryMethodInvalid' }
      | { __typename: 'CheckoutProblemDeliveryMethodStale' }
    > | null, channel: { slug: string }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, undiscountedUnitPrice: { amount: number, currency: string }, totalPrice: { net: { amount: number, currency: string }, gross: { amount: number, currency: string } }, variant: { sku: string | null, name: string, id: string, product: { id: string, name: string, slug: string, translation: { name: string | null } | null, thumbnail: { url: string, alt: string | null } | null, category: { name: string, translation: { name: string | null } | null } | null }, pricing: { price: { net: { amount: number, currency: string }, gross: { amount: number, currency: string } } | null, priceUndiscounted: { gross: { amount: number, currency: string } } | null } | null, selectionAttributes: Array<{ attribute: { id: string, name: string, slug: string }, values: Array<{ id: string, slug: string | null, name: string | null, value: string | null }> }>, nonSelectionAttributes: Array<{ attribute: { id: string, name: string, slug: string }, values: Array<{ id: string, slug: string | null, name: string | null, value: string | null }> }> } }>, discount: { amount: number, currency: string } | null, delivery: { id: string } | null, shippingPrice: { gross: { amount: number, currency: string } }, subtotalPrice: { gross: { amount: number, currency: string }, net: { amount: number, currency: string } }, totalPrice: { gross: { amount: number, currency: string } } } | null };

export type CheckoutLinesUpdateMutationVariables = Exact<{
  checkoutId: string | number;
  lines: Array<CheckoutLineUpdateInput> | CheckoutLineUpdateInput;
}>;


export type CheckoutLinesUpdateMutation = { checkoutLinesUpdate: { checkout: { id: string, lines: Array<{ id: string, quantity: number, totalPrice: { gross: { amount: number, currency: string } } }>, totalPrice: { gross: { amount: number, currency: string } } } | null, errors: Array<{ field: string | null, message: string | null, code: CheckoutErrorCode }> } | null };

export type CheckoutReplacementLineAddMutationVariables = Exact<{
  id: string | number;
  line: CheckoutLineInput;
}>;


export type CheckoutReplacementLineAddMutation = { checkoutLinesAdd: { checkout: { id: string, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, variant: { id: string } }> } | null, errors: Array<{ field: string | null, code: CheckoutErrorCode, message: string | null }> } | null };

export type CollectionTranslateMutationVariables = Exact<{
  id: string | number;
  languageCode: LanguageCodeEnum;
  input: TranslationInput;
}>;


export type CollectionTranslateMutation = { collectionTranslate: { collection: { id: string } | null, errors: Array<{ field: string | null, message: string | null }> } | null };

export type CollectionsListQueryVariables = Exact<{
  channel: string;
  first?: number | null | undefined;
  after?: string | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type CollectionsListQuery = { collections: { edges: Array<{ node: { id: string, slug: string, name: string, translation: { name: string | null } | null } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null };

export type CompareBySlugQueryVariables = Exact<{
  slug: string;
  languageCode: LanguageCodeEnum;
}>;


export type CompareBySlugQuery = { page: { id: string, slug: string, title: string, seoTitle: string | null, seoDescription: string | null, publishedAt: string | null, created: string, metadata: Array<{ key: string, value: string }>, pageType: { slug: string }, assignedAttributes: Array<
      | { __typename: 'AssignedBooleanAttribute', booleanValue: boolean | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedDateAttribute', dateValue: string | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedDateTimeAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedFileAttribute', fileValue: { url: string, contentType: string | null } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiCategoryReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiChoiceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiCollectionReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiPageReferenceAttribute', pageListValue: Array<{ slug: string, title: string }>, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiProductReferenceAttribute', productListValue: Array<{ slug: string, name: string }>, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiProductVariantReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedNumericAttribute', numericValue: number | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedPlainTextAttribute', plainTextValue: string | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleCategoryReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleChoiceAttribute', choiceValue: { name: string | null, slug: string | null } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleCollectionReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSinglePageReferenceAttribute', pageValue: { slug: string, title: string } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleProductReferenceAttribute', productValue: { slug: string, name: string } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleProductVariantReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSwatchAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedTextAttribute', richTextValue: unknown, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
    >, attributes: Array<{ attribute: { slug: string }, values: Array<{ id: string, plainText: string | null, translation: { plainText: string | null } | null }> }>, translation: { title: string | null, seoTitle: string | null, seoDescription: string | null } | null } | null };

export type ComparisonCtaProductQueryVariables = Exact<{
  slug: string;
  channel: string;
  languageCode: LanguageCodeEnum;
}>;


export type ComparisonCtaProductQuery = { product: { name: string, translation: { name: string | null } | null, thumbnail: { url: string, alt: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null } | null } | null } | null };

export type ComparisonTranslationPresenceQueryVariables = Exact<{
  slug: string;
}>;


export type ComparisonTranslationPresenceQuery = { page: { id: string, slug: string, pageType: { slug: string }, attributes: Array<{ attribute: { slug: string }, values: Array<{ id: string, translationEn: { plainText: string | null } | null, translationIt: { plainText: string | null } | null, translationEs: { plainText: string | null } | null, translationEsMx: { plainText: string | null } | null, translationFr: { plainText: string | null } | null, translationNl: { plainText: string | null } | null, translationPt: { plainText: string | null } | null, translationPtBr: { plainText: string | null } | null, translationPl: { plainText: string | null } | null, translationSv: { plainText: string | null } | null, translationDa: { plainText: string | null } | null, translationEl: { plainText: string | null } | null, translationCs: { plainText: string | null } | null, translationFi: { plainText: string | null } | null, translationRo: { plainText: string | null } | null, translationHu: { plainText: string | null } | null, translationSk: { plainText: string | null } | null, translationTr: { plainText: string | null } | null, translationNb: { plainText: string | null } | null, translationBg: { plainText: string | null } | null, translationHr: { plainText: string | null } | null, translationSl: { plainText: string | null } | null, translationSr: { plainText: string | null } | null, translationBs: { plainText: string | null } | null, translationMk: { plainText: string | null } | null, translationLt: { plainText: string | null } | null, translationLv: { plainText: string | null } | null, translationEt: { plainText: string | null } | null }> }> } | null };

export type ComparisonsListQueryVariables = Exact<{
  first: number;
  after?: string | null | undefined;
  pageTypes?: Array<string | number> | string | number | null | undefined;
}>;


export type ComparisonsListQuery = { pages: { edges: Array<{ node: { id: string, slug: string, publishedAt: string | null, metadata: Array<{ key: string, value: string }>, assignedAttributes: Array<
          | { __typename: 'AssignedBooleanAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedDateAttribute', dateValue: string | null, attribute: { slug: string } }
          | { __typename: 'AssignedDateTimeAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedFileAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiCategoryReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiChoiceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiCollectionReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiPageReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiProductReferenceAttribute', productListValue: Array<{ slug: string, name: string }>, attribute: { slug: string } }
          | { __typename: 'AssignedMultiProductVariantReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedNumericAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedPlainTextAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSingleCategoryReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSingleChoiceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSingleCollectionReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSinglePageReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSingleProductReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSingleProductVariantReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSwatchAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedTextAttribute', attribute: { slug: string } }
        > } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null };

export type CurrentUserQueryVariables = Exact<{ [key: string]: never; }>;


export type CurrentUserQuery = { me: { id: string, email: string, firstName: string, lastName: string, avatar: { url: string, alt: string | null } | null } | null };

export type CurrentUserAccountSummaryQueryVariables = Exact<{ [key: string]: never; }>;


export type CurrentUserAccountSummaryQuery = { me: { id: string, email: string, firstName: string, lastName: string, orders: { totalCount: number | null } | null, avatar: { url: string, alt: string | null } | null } | null };

export type CurrentUserAddressesQueryVariables = Exact<{ [key: string]: never; }>;


export type CurrentUserAddressesQuery = { me: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null };

export type CurrentUserOrderDetailsQueryVariables = Exact<{
  id: string | number;
}>;


export type CurrentUserOrderDetailsQuery = { order: { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, voucherCode: string | null, subtotal: { gross: { amount: number, currency: string } }, shippingPrice: { gross: { amount: number, currency: string } }, total: { gross: { amount: number, currency: string } }, undiscountedTotal: { gross: { amount: number, currency: string } }, discounts: Array<{ id: string, type: OrderDiscountType, valueType: DiscountValueTypeEnum, value: number, reason: string | null, amount: { amount: number, currency: string } }>, shippingAddress: { firstName: string, lastName: string, companyName: string, streetAddress1: string, streetAddress2: string, city: string, postalCode: string, countryArea: string, phone: string | null, country: { code: string, country: string } } | null, billingAddress: { firstName: string, lastName: string, companyName: string, streetAddress1: string, streetAddress2: string, city: string, postalCode: string, countryArea: string, phone: string | null, country: { code: string, country: string } } | null, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, variant: { id: string, name: string, product: { id: string, name: string, slug: string, thumbnail: { url: string, alt: string | null } | null }, pricing: { price: { gross: { amount: number, currency: string } } | null } | null } | null }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, statusDisplay: string | null, trackingNumber: string, metadata: Array<{ key: string, value: string }>, lines: Array<{ id: string, quantity: number, orderLine: { id: string } | null }> | null }> } | null };

export type CurrentUserOrderListQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
  where?: CustomerOrderWhereInput | null | undefined;
}>;


export type CurrentUserOrderListQuery = { me: { id: string, email: string, firstName: string, lastName: string, orders: { edges: Array<{ cursor: string, node: { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, total: { gross: { amount: number, currency: string } }, lines: Array<{ id: string, quantity: number, variant: { id: string, name: string, product: { id: string, name: string, description: string | null, slug: string, thumbnail: { url: string, alt: string | null } | null, category: { id: string, name: string } | null }, pricing: { price: { gross: { amount: number, currency: string } } | null } | null } | null }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, statusDisplay: string | null, trackingNumber: string, metadata: Array<{ key: string, value: string }>, lines: Array<{ id: string, quantity: number, orderLine: { id: string } | null }> | null }> } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null, avatar: { url: string, alt: string | null } | null } | null };

export type FileUploadMutationVariables = Exact<{
  file: unknown;
}>;


export type FileUploadMutation = { fileUpload: { uploadedFile: { url: string, contentType: string | null } | null, errors: Array<{ field: string | null, message: string | null }> } | null };

type AssignedAttributeFields_AssignedBooleanAttribute_Fragment = { __typename: 'AssignedBooleanAttribute', booleanValue: boolean | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedDateAttribute_Fragment = { __typename: 'AssignedDateAttribute', dateValue: string | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedDateTimeAttribute_Fragment = { __typename: 'AssignedDateTimeAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedFileAttribute_Fragment = { __typename: 'AssignedFileAttribute', fileValue: { url: string, contentType: string | null } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedMultiCategoryReferenceAttribute_Fragment = { __typename: 'AssignedMultiCategoryReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedMultiChoiceAttribute_Fragment = { __typename: 'AssignedMultiChoiceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedMultiCollectionReferenceAttribute_Fragment = { __typename: 'AssignedMultiCollectionReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedMultiPageReferenceAttribute_Fragment = { __typename: 'AssignedMultiPageReferenceAttribute', pageListValue: Array<{ slug: string, title: string }>, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedMultiProductReferenceAttribute_Fragment = { __typename: 'AssignedMultiProductReferenceAttribute', productListValue: Array<{ slug: string, name: string }>, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedMultiProductVariantReferenceAttribute_Fragment = { __typename: 'AssignedMultiProductVariantReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedNumericAttribute_Fragment = { __typename: 'AssignedNumericAttribute', numericValue: number | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedPlainTextAttribute_Fragment = { __typename: 'AssignedPlainTextAttribute', plainTextValue: string | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedSingleCategoryReferenceAttribute_Fragment = { __typename: 'AssignedSingleCategoryReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedSingleChoiceAttribute_Fragment = { __typename: 'AssignedSingleChoiceAttribute', choiceValue: { name: string | null, slug: string | null } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedSingleCollectionReferenceAttribute_Fragment = { __typename: 'AssignedSingleCollectionReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedSinglePageReferenceAttribute_Fragment = { __typename: 'AssignedSinglePageReferenceAttribute', pageValue: { slug: string, title: string } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedSingleProductReferenceAttribute_Fragment = { __typename: 'AssignedSingleProductReferenceAttribute', productValue: { slug: string, name: string } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedSingleProductVariantReferenceAttribute_Fragment = { __typename: 'AssignedSingleProductVariantReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedSwatchAttribute_Fragment = { __typename: 'AssignedSwatchAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

type AssignedAttributeFields_AssignedTextAttribute_Fragment = { __typename: 'AssignedTextAttribute', richTextValue: unknown, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } };

export type AssignedAttributeFieldsFragment =
  | AssignedAttributeFields_AssignedBooleanAttribute_Fragment
  | AssignedAttributeFields_AssignedDateAttribute_Fragment
  | AssignedAttributeFields_AssignedDateTimeAttribute_Fragment
  | AssignedAttributeFields_AssignedFileAttribute_Fragment
  | AssignedAttributeFields_AssignedMultiCategoryReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedMultiChoiceAttribute_Fragment
  | AssignedAttributeFields_AssignedMultiCollectionReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedMultiPageReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedMultiProductReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedMultiProductVariantReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedNumericAttribute_Fragment
  | AssignedAttributeFields_AssignedPlainTextAttribute_Fragment
  | AssignedAttributeFields_AssignedSingleCategoryReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedSingleChoiceAttribute_Fragment
  | AssignedAttributeFields_AssignedSingleCollectionReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedSinglePageReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedSingleProductReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedSingleProductVariantReferenceAttribute_Fragment
  | AssignedAttributeFields_AssignedSwatchAttribute_Fragment
  | AssignedAttributeFields_AssignedTextAttribute_Fragment
;

export type GuideBySlugQueryVariables = Exact<{
  slug: string;
  languageCode: LanguageCodeEnum;
}>;


export type GuideBySlugQuery = { page: { id: string, slug: string, title: string, content: string | null, seoTitle: string | null, seoDescription: string | null, publishedAt: string | null, created: string, metadata: Array<{ key: string, value: string }>, pageType: { slug: string }, assignedAttributes: Array<
      | { __typename: 'AssignedBooleanAttribute', booleanValue: boolean | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedDateAttribute', dateValue: string | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedDateTimeAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedFileAttribute', fileValue: { url: string, contentType: string | null } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiCategoryReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiChoiceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiCollectionReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiPageReferenceAttribute', pageListValue: Array<{ slug: string, title: string }>, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiProductReferenceAttribute', productListValue: Array<{ slug: string, name: string }>, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedMultiProductVariantReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedNumericAttribute', numericValue: number | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedPlainTextAttribute', plainTextValue: string | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleCategoryReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleChoiceAttribute', choiceValue: { name: string | null, slug: string | null } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleCollectionReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSinglePageReferenceAttribute', pageValue: { slug: string, title: string } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleProductReferenceAttribute', productValue: { slug: string, name: string } | null, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSingleProductVariantReferenceAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedSwatchAttribute', attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
      | { __typename: 'AssignedTextAttribute', richTextValue: unknown, attribute: { slug: string, inputType: AttributeInputTypeEnum | null } }
    >, translation: { title: string | null, content: string | null, seoTitle: string | null, seoDescription: string | null } | null } | null };

export type GuideTranslationPresenceQueryVariables = Exact<{
  slug: string;
  translationEn?: boolean | null | undefined;
  translationIt?: boolean | null | undefined;
  translationEs?: boolean | null | undefined;
  translationEsMx?: boolean | null | undefined;
  translationFr?: boolean | null | undefined;
  translationNl?: boolean | null | undefined;
  translationPt?: boolean | null | undefined;
  translationPtBr?: boolean | null | undefined;
  translationPl?: boolean | null | undefined;
  translationSv?: boolean | null | undefined;
  translationDa?: boolean | null | undefined;
  translationEl?: boolean | null | undefined;
  translationCs?: boolean | null | undefined;
  translationFi?: boolean | null | undefined;
  translationRo?: boolean | null | undefined;
  translationHu?: boolean | null | undefined;
  translationSk?: boolean | null | undefined;
  translationTr?: boolean | null | undefined;
  translationNb?: boolean | null | undefined;
  translationBg?: boolean | null | undefined;
  translationHr?: boolean | null | undefined;
  translationSl?: boolean | null | undefined;
  translationSr?: boolean | null | undefined;
  translationBs?: boolean | null | undefined;
  translationMk?: boolean | null | undefined;
  translationLt?: boolean | null | undefined;
  translationLv?: boolean | null | undefined;
  translationEt?: boolean | null | undefined;
}>;


export type GuideTranslationPresenceQuery = { page: { id: string, slug: string, title: string, content: string | null, pageType: { slug: string }, translationEn?: { title: string | null, content: string | null } | null, translationIt?: { title: string | null, content: string | null } | null, translationEs?: { title: string | null, content: string | null } | null, translationEsMx?: { title: string | null, content: string | null } | null, translationFr?: { title: string | null, content: string | null } | null, translationNl?: { title: string | null, content: string | null } | null, translationPt?: { title: string | null, content: string | null } | null, translationPtBr?: { title: string | null, content: string | null } | null, translationPl?: { title: string | null, content: string | null } | null, translationSv?: { title: string | null, content: string | null } | null, translationDa?: { title: string | null, content: string | null } | null, translationEl?: { title: string | null, content: string | null } | null, translationCs?: { title: string | null, content: string | null } | null, translationFi?: { title: string | null, content: string | null } | null, translationRo?: { title: string | null, content: string | null } | null, translationHu?: { title: string | null, content: string | null } | null, translationSk?: { title: string | null, content: string | null } | null, translationTr?: { title: string | null, content: string | null } | null, translationNb?: { title: string | null, content: string | null } | null, translationBg?: { title: string | null, content: string | null } | null, translationHr?: { title: string | null, content: string | null } | null, translationSl?: { title: string | null, content: string | null } | null, translationSr?: { title: string | null, content: string | null } | null, translationBs?: { title: string | null, content: string | null } | null, translationMk?: { title: string | null, content: string | null } | null, translationLt?: { title: string | null, content: string | null } | null, translationLv?: { title: string | null, content: string | null } | null, translationEt?: { title: string | null, content: string | null } | null } | null };

export type GuidesListQueryVariables = Exact<{
  first: number;
  after?: string | null | undefined;
  languageCode: LanguageCodeEnum;
  pageTypes?: Array<string | number> | string | number | null | undefined;
}>;


export type GuidesListQuery = { pages: { edges: Array<{ node: { id: string, slug: string, title: string, seoTitle: string | null, seoDescription: string | null, publishedAt: string | null, created: string, metadata: Array<{ key: string, value: string }>, pageType: { slug: string }, assignedAttributes: Array<
          | { __typename: 'AssignedBooleanAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedDateAttribute', dateValue: string | null, attribute: { slug: string } }
          | { __typename: 'AssignedDateTimeAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedFileAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiCategoryReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiChoiceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiCollectionReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiPageReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiProductReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedMultiProductVariantReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedNumericAttribute', numericValue: number | null, attribute: { slug: string } }
          | { __typename: 'AssignedPlainTextAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSingleCategoryReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSingleChoiceAttribute', choiceValue: { slug: string | null } | null, attribute: { slug: string } }
          | { __typename: 'AssignedSingleCollectionReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSinglePageReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSingleProductReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSingleProductVariantReferenceAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedSwatchAttribute', attribute: { slug: string } }
          | { __typename: 'AssignedTextAttribute', attribute: { slug: string } }
        >, translation: { title: string | null, seoTitle: string | null, seoDescription: string | null } | null } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null };

export type HomepageBundleProductsQueryVariables = Exact<{
  channel: string;
  locale: LanguageCodeEnum;
}>;


export type HomepageBundleProductsQuery = { retatrutide: { id: string, name: string, slug: string, thumbnail: { url: string, alt: string | null } | null, translation: { name: string | null } | null, variants: Array<{ id: string, name: string, sku: string | null, quantityAvailable: number | null, pricing: { price: { net: { amount: number, currency: string }, gross: { amount: number, currency: string } } | null } | null }> | null } | null, bacWater: { id: string, name: string, variants: Array<{ id: string, name: string, sku: string | null, quantityAvailable: number | null, pricing: { price: { net: { amount: number, currency: string }, gross: { amount: number, currency: string } } | null } | null }> | null } | null };

export type LpSupportingProductsQueryVariables = Exact<{
  channel: string;
  languageCode: LanguageCodeEnum;
  slugs: Array<string> | string;
}>;


export type LpSupportingProductsQuery = { bac: { slug: string, name: string, isAvailable: boolean | null, isAvailableForPurchase: boolean | null, translation: { name: string | null } | null, thumbnail: { url: string, alt: string | null } | null, variants: Array<{ id: string, name: string, quantityAvailable: number | null, pricing: { price: { gross: { amount: number, currency: string } } | null } | null }> | null } | null, peptides: { edges: Array<{ node: { slug: string, name: string, isAvailable: boolean | null, isAvailableForPurchase: boolean | null, translation: { name: string | null } | null, category: { slug: string } | null, variants: Array<{ id: string, name: string, quantityAvailable: number | null, pricing: { price: { gross: { amount: number, currency: string } } | null } | null }> | null } }> } | null };

export type MenuItemFragment = { id: string, name: string, level: number, url: string | null, translation: { name: string } | null, category: { id: string, slug: string, name: string, translation: { name: string | null } | null } | null, collection: { id: string, name: string, slug: string, translation: { name: string | null } | null } | null, page: { id: string, title: string, slug: string, translation: { title: string | null } | null } | null };

export type MenuGetBySlugQueryVariables = Exact<{
  slug: string;
  channel: string;
  languageCode: LanguageCodeEnum;
}>;


export type MenuGetBySlugQuery = { menu: { items: Array<{ id: string, name: string, level: number, url: string | null, children: Array<{ id: string, name: string, level: number, url: string | null, translation: { name: string } | null, category: { id: string, slug: string, name: string, translation: { name: string | null } | null } | null, collection: { id: string, name: string, slug: string, translation: { name: string | null } | null } | null, page: { id: string, title: string, slug: string, translation: { title: string | null } | null } | null }> | null, translation: { name: string } | null, category: { id: string, slug: string, name: string, translation: { name: string | null } | null } | null, collection: { id: string, name: string, slug: string, translation: { name: string | null } | null } | null, page: { id: string, title: string, slug: string, translation: { title: string | null } | null } | null }> | null } | null };

export type OrderByIdQueryVariables = Exact<{
  id: string | number;
}>;


export type OrderByIdQuery = { order: { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, total: { gross: { amount: number, currency: string } }, lines: Array<{ id: string, quantity: number, variant: { id: string, name: string, product: { id: string, name: string, description: string | null, slug: string, thumbnail: { url: string, alt: string | null } | null, category: { id: string, name: string } | null }, pricing: { price: { gross: { amount: number, currency: string } } | null } | null } | null }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, statusDisplay: string | null, trackingNumber: string, metadata: Array<{ key: string, value: string }>, lines: Array<{ id: string, quantity: number, orderLine: { id: string } | null }> | null }> } | null };

export type OrderDetailsFragment = { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, total: { gross: { amount: number, currency: string } }, lines: Array<{ id: string, quantity: number, variant: { id: string, name: string, product: { id: string, name: string, description: string | null, slug: string, thumbnail: { url: string, alt: string | null } | null, category: { id: string, name: string } | null }, pricing: { price: { gross: { amount: number, currency: string } } | null } | null } | null }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, statusDisplay: string | null, trackingNumber: string, metadata: Array<{ key: string, value: string }>, lines: Array<{ id: string, quantity: number, orderLine: { id: string } | null }> | null }> };

export type OrderDetailsFullFragment = { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, voucherCode: string | null, subtotal: { gross: { amount: number, currency: string } }, shippingPrice: { gross: { amount: number, currency: string } }, total: { gross: { amount: number, currency: string } }, undiscountedTotal: { gross: { amount: number, currency: string } }, discounts: Array<{ id: string, type: OrderDiscountType, valueType: DiscountValueTypeEnum, value: number, reason: string | null, amount: { amount: number, currency: string } }>, shippingAddress: { firstName: string, lastName: string, companyName: string, streetAddress1: string, streetAddress2: string, city: string, postalCode: string, countryArea: string, phone: string | null, country: { code: string, country: string } } | null, billingAddress: { firstName: string, lastName: string, companyName: string, streetAddress1: string, streetAddress2: string, city: string, postalCode: string, countryArea: string, phone: string | null, country: { code: string, country: string } } | null, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, variant: { id: string, name: string, product: { id: string, name: string, slug: string, thumbnail: { url: string, alt: string | null } | null }, pricing: { price: { gross: { amount: number, currency: string } } | null } | null } | null }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, statusDisplay: string | null, trackingNumber: string, metadata: Array<{ key: string, value: string }>, lines: Array<{ id: string, quantity: number, orderLine: { id: string } | null }> | null }> };

export type PageGetBySlugQueryVariables = Exact<{
  slug: string;
  languageCode: LanguageCodeEnum;
}>;


export type PageGetBySlugQuery = { page: { id: string, slug: string, title: string, seoTitle: string | null, seoDescription: string | null, content: string | null, pageType: { slug: string }, translation: { title: string | null, content: string | null, seoTitle: string | null, seoDescription: string | null } | null } | null };

export type PageTranslateMutationVariables = Exact<{
  id: string | number;
  languageCode: LanguageCodeEnum;
  input: PageTranslationInput;
}>;


export type PageTranslateMutation = { pageTranslate: { page: { id: string } | null, errors: Array<{ field: string | null, message: string | null }> } | null };

export type PageTypeBySlugQueryVariables = Exact<{
  slug: string;
}>;


export type PageTypeBySlugQuery = { pageTypes: { edges: Array<{ node: { id: string } }> } | null };

export type ProductChannelListingUpdateMutationVariables = Exact<{
  id: string | number;
  input: ProductChannelListingUpdateInput;
}>;


export type ProductChannelListingUpdateMutation = { productChannelListingUpdate: { product: { id: string, slug: string } | null, errors: Array<{ field: string | null, message: string | null, code: ProductErrorCode }> } | null };

export type ProductDetailsQueryVariables = Exact<{
  slug: string;
  channel: string;
  languageCode: LanguageCodeEnum;
}>;


export type ProductDetailsQuery = { product: { id: string, isAvailableForPurchase: boolean | null, name: string, slug: string, description: string | null, seoTitle: string | null, seoDescription: string | null, translation: { name: string | null, description: string | null, seoTitle: string | null, seoDescription: string | null } | null, translationEsMx: { name: string | null, description: string | null, seoTitle: string | null, seoDescription: string | null } | null, thumbnail: { url: string, alt: string | null } | null, media: Array<{ url: string, alt: string, type: ProductMediaType }> | null, metadata: Array<{ key: string, value: string }>, category: { name: string, slug: string, translation: { name: string | null } | null } | null, faqAttribute:
      | { value: string | null, translation: string | null }
      | Record<PropertyKey, never>
     | null, variants: Array<{ id: string, name: string, sku: string | null, quantityAvailable: number | null, selectionAttributes: Array<{ values: Array<{ id: string, slug: string | null, name: string | null, value: string | null }>, attribute: { id: string, slug: string } }>, media: Array<{ url: string, alt: string, type: ProductMediaType }> | null, pricing: { price: { gross: { currency: string, amount: number } } | null, priceUndiscounted: { gross: { currency: string, amount: number } } | null } | null }> | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null } | null } | null };

export type ProductListQueryVariables = Exact<{
  first?: number | null | undefined;
  channel: string;
  languageCode: LanguageCodeEnum;
}>;


export type ProductListQuery = { products: { edges: Array<{ node: { id: string, name: string, slug: string, created: string, metafields: Record<string, string> | null, translation: { name: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null, priceRangeUndiscounted: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null } | null, category: { id: string, name: string, slug: string, translation: { name: string | null } | null } | null, thumbnail: { url: string, alt: string | null } | null, variants: Array<{ id: string, selectionAttributes: Array<{ attribute: { slug: string, name: string }, values: Array<{ name: string | null, value: string | null }> }> }> | null } }> } | null };

export type ProductListByCategoryQueryVariables = Exact<{
  slug: string;
  channel: string;
  first?: number | null | undefined;
  after?: string | null | undefined;
  last?: number | null | undefined;
  before?: string | null | undefined;
  sortBy?: ProductOrder | null | undefined;
  filter?: ProductFilterInput | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type ProductListByCategoryQuery = { category: { id: string, name: string, slug: string, description: string | null, seoDescription: string | null, seoTitle: string | null, translation: { name: string | null, description: string | null, seoDescription: string | null, seoTitle: string | null } | null, backgroundImage: { url: string, alt: string | null } | null, products: { totalCount: number | null, edges: Array<{ cursor: string, node: { id: string, name: string, slug: string, created: string, metafields: Record<string, string> | null, translation: { name: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null, priceRangeUndiscounted: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null } | null, category: { id: string, name: string, slug: string, translation: { name: string | null } | null } | null, thumbnail: { url: string, alt: string | null } | null, variants: Array<{ id: string, selectionAttributes: Array<{ attribute: { slug: string, name: string }, values: Array<{ name: string | null, value: string | null }> }> }> | null } }>, pageInfo: { hasNextPage: boolean, hasPreviousPage: boolean, startCursor: string | null, endCursor: string | null } } | null } | null };

export type ProductListByCollectionQueryVariables = Exact<{
  slug: string;
  channel: string;
  first?: number | null | undefined;
  after?: string | null | undefined;
  last?: number | null | undefined;
  before?: string | null | undefined;
  sortBy?: ProductOrder | null | undefined;
  filter?: ProductFilterInput | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type ProductListByCollectionQuery = { collection: { id: string, name: string, slug: string, description: string | null, seoDescription: string | null, seoTitle: string | null, translation: { name: string | null, description: string | null, seoDescription: string | null, seoTitle: string | null } | null, backgroundImage: { url: string, alt: string | null } | null, products: { totalCount: number | null, edges: Array<{ cursor: string, node: { id: string, name: string, slug: string, created: string, metafields: Record<string, string> | null, translation: { name: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null, priceRangeUndiscounted: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null } | null, category: { id: string, name: string, slug: string, translation: { name: string | null } | null } | null, thumbnail: { url: string, alt: string | null } | null, variants: Array<{ id: string, selectionAttributes: Array<{ attribute: { slug: string, name: string }, values: Array<{ name: string | null, value: string | null }> }> }> | null } }>, pageInfo: { hasNextPage: boolean, hasPreviousPage: boolean, startCursor: string | null, endCursor: string | null } } | null } | null };

export type ProductListItemFragment = { id: string, name: string, slug: string, created: string, metafields: Record<string, string> | null, translation: { name: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null, priceRangeUndiscounted: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null } | null, category: { id: string, name: string, slug: string, translation: { name: string | null } | null } | null, thumbnail: { url: string, alt: string | null } | null, variants: Array<{ id: string, selectionAttributes: Array<{ attribute: { slug: string, name: string }, values: Array<{ name: string | null, value: string | null }> }> }> | null };

export type ProductListPaginatedQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
  last?: number | null | undefined;
  before?: string | null | undefined;
  channel: string;
  sortBy?: ProductOrder | null | undefined;
  filter?: ProductFilterInput | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type ProductListPaginatedQuery = { products: { totalCount: number | null, edges: Array<{ cursor: string, node: { updatedAt: string, id: string, name: string, slug: string, created: string, metafields: Record<string, string> | null, translation: { name: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null, priceRangeUndiscounted: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null } | null, category: { id: string, name: string, slug: string, translation: { name: string | null } | null } | null, thumbnail: { url: string, alt: string | null } | null, variants: Array<{ id: string, selectionAttributes: Array<{ attribute: { slug: string, name: string }, values: Array<{ name: string | null, value: string | null }> }> }> | null } }>, pageInfo: { hasNextPage: boolean, hasPreviousPage: boolean, startCursor: string | null, endCursor: string | null } } | null };

export type ProductTranslateMutationVariables = Exact<{
  id: string | number;
  languageCode: LanguageCodeEnum;
  input: TranslationInput;
}>;


export type ProductTranslateMutation = { productTranslate: { product: { id: string } | null, errors: Array<{ field: string | null, message: string | null }> } | null };

export type ProductVariantTranslateMutationVariables = Exact<{
  id: string | number;
  languageCode: LanguageCodeEnum;
  input: NameTranslationInput;
}>;


export type ProductVariantTranslateMutation = { productVariantTranslate: { productVariant: { id: string } | null, errors: Array<{ field: string | null, message: string | null }> } | null };

export type ProductsListForVisibilitySyncQueryVariables = Exact<{
  channel: string;
  first: number;
  after?: string | null | undefined;
}>;


export type ProductsListForVisibilitySyncQuery = { products: { edges: Array<{ node: { id: string, slug: string } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null };

export type PublicDocumentProductsQueryVariables = Exact<{
  first: number;
  after?: string | null | undefined;
  channel: string;
  languageCode: LanguageCodeEnum;
}>;


export type PublicDocumentProductsQuery = { products: { edges: Array<{ node: { slug: string, name: string, updatedAt: string, translation: { name: string | null } | null, translationEsMx: { description: string | null } | null, category: { slug: string, name: string, translation: { name: string | null } | null } | null } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null };

export type RootCategoriesQueryVariables = Exact<{
  first?: number | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type RootCategoriesQuery = { categories: { edges: Array<{ node: { id: string, name: string, slug: string, description: string | null, backgroundImage: { url: string, alt: string | null } | null, translation: { name: string | null, description: string | null } | null } }> } | null };

export type SearchIndexQueryVariables = Exact<{
  channel: string;
  first: number;
  after?: string | null | undefined;
}>;


export type SearchIndexQuery = { products: { edges: Array<{ node: { id: string, name: string, slug: string, isAvailable: boolean | null, searchAliases: string | null, thumbnail: { url: string, alt: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null } | null } | null, category: { name: string, translationDe: { name: string | null } | null, translationIt: { name: string | null } | null, translationEs: { name: string | null } | null, translationFr: { name: string | null } | null, translationNl: { name: string | null } | null, translationPt: { name: string | null } | null, translationPtBr: { name: string | null } | null, translationPl: { name: string | null } | null, translationSv: { name: string | null } | null, translationDa: { name: string | null } | null, translationEl: { name: string | null } | null, translationCs: { name: string | null } | null, translationFi: { name: string | null } | null, translationRo: { name: string | null } | null, translationHu: { name: string | null } | null, translationSk: { name: string | null } | null, translationTr: { name: string | null } | null, translationNb: { name: string | null } | null, translationBg: { name: string | null } | null, translationHr: { name: string | null } | null, translationSl: { name: string | null } | null, translationSr: { name: string | null } | null, translationBs: { name: string | null } | null, translationMk: { name: string | null } | null, translationLt: { name: string | null } | null, translationLv: { name: string | null } | null, translationEt: { name: string | null } | null } | null, translationDe: { name: string | null } | null, translationIt: { name: string | null } | null, translationEs: { name: string | null } | null, translationFr: { name: string | null } | null, translationNl: { name: string | null } | null, translationPt: { name: string | null } | null, translationPtBr: { name: string | null } | null, translationPl: { name: string | null } | null, translationSv: { name: string | null } | null, translationDa: { name: string | null } | null, translationEl: { name: string | null } | null, translationCs: { name: string | null } | null, translationFi: { name: string | null } | null, translationRo: { name: string | null } | null, translationHu: { name: string | null } | null, translationSk: { name: string | null } | null, translationTr: { name: string | null } | null, translationNb: { name: string | null } | null, translationBg: { name: string | null } | null, translationHr: { name: string | null } | null, translationSl: { name: string | null } | null, translationSr: { name: string | null } | null, translationBs: { name: string | null } | null, translationMk: { name: string | null } | null, translationLt: { name: string | null } | null, translationLv: { name: string | null } | null, translationEt: { name: string | null } | null } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null };

export type SearchProductsQueryVariables = Exact<{
  filter: ProductFilterInput;
  sortBy: ProductOrderField;
  sortDirection: OrderDirection;
  first?: number | null | undefined;
  after?: string | null | undefined;
  last?: number | null | undefined;
  before?: string | null | undefined;
  channel: string;
  languageCode: LanguageCodeEnum;
}>;


export type SearchProductsQuery = { products: { totalCount: number | null, edges: Array<{ cursor: string, node: { id: string, name: string, slug: string, created: string, metafields: Record<string, string> | null, translation: { name: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null, priceRangeUndiscounted: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null } | null, category: { id: string, name: string, slug: string, translation: { name: string | null } | null } | null, thumbnail: { url: string, alt: string | null } | null, variants: Array<{ id: string, selectionAttributes: Array<{ attribute: { slug: string, name: string }, values: Array<{ name: string | null, value: string | null }> }> }> | null } }>, pageInfo: { hasNextPage: boolean, hasPreviousPage: boolean, endCursor: string | null, startCursor: string | null } } | null };

export type SitemapProductContentQueryVariables = Exact<{
  first: number;
  after?: string | null | undefined;
  channel: string;
  languageCode: LanguageCodeEnum;
  withRegional: boolean;
}>;


export type SitemapProductContentQuery = { products: { edges: Array<{ node: { slug: string, name: string, description: string | null, seoTitle: string | null, seoDescription: string | null, translation: { name: string | null, description: string | null, seoTitle: string | null, seoDescription: string | null } | null, translationEsMx?: { name: string | null, description: string | null, seoTitle: string | null, seoDescription: string | null } | null, metadata: Array<{ key: string, value: string }>, category: { slug: string } | null, media: Array<{ id: string, alt: string, type: ProductMediaType }> | null, faqAttribute:
          | { value: string | null, translation: string | null }
          | Record<PropertyKey, never>
         | null } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null };

export type StackBuilderProductsQueryVariables = Exact<{
  channel: string;
  languageCode: LanguageCodeEnum;
  first: number;
  after?: string | null | undefined;
}>;


export type StackBuilderProductsQuery = { products: { edges: Array<{ node: { id: string, slug: string, name: string, isAvailable: boolean | null, translation: { name: string | null } | null, thumbnail: { url: string, alt: string | null } | null, pricing: { priceRange: { start: { gross: { amount: number, currency: string } } | null, stop: { gross: { amount: number, currency: string } } | null } | null } | null, category: { name: string, slug: string } | null, variants: Array<{ id: string, name: string, quantityAvailable: number | null, pricing: { price: { gross: { amount: number, currency: string } } | null } | null, selectionAttributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null }> }> }> | null } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null };

export type StaffAfterSalesOrdersQueryVariables = Exact<{
  ids?: Array<string | number> | string | number | null | undefined;
  first: number;
}>;


export type StaffAfterSalesOrdersQuery = { orders: { edges: Array<{ node: { id: string, number: string, created: string, userEmail: string | null, languageCodeEnum: LanguageCodeEnum, shippingAddress: { firstName: string, lastName: string } | null, billingAddress: { firstName: string, lastName: string } | null, lines: Array<{ productName: string, quantity: number }>, total: { gross: { amount: number, currency: string } } } }> } | null };

export type StaffChatwootCustomerContextQueryVariables = Exact<{
  email: string;
  first: number;
}>;


export type StaffChatwootCustomerContextQuery = { user: { id: string, email: string, firstName: string, lastName: string, dateJoined: string, languageCode: LanguageCodeEnum, defaultShippingAddress: { city: string, country: { code: string, country: string } } | null, orders: { totalCount: number | null, edges: Array<{ node: { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, chargeStatus: OrderChargeStatusEnum, authorizeStatus: OrderAuthorizeStatusEnum, isPaid: boolean, userEmail: string | null, languageCode: string, user: { id: string, email: string } | null, channel: { slug: string, currencyCode: string }, total: { gross: { amount: number, currency: string } }, totalCharged: { amount: number, currency: string }, totalRefunded: { amount: number, currency: string }, totalRefundPending: { amount: number, currency: string }, shippingAddress: { city: string, country: { code: string, country: string } } | null, lines: Array<{ id: string, productName: string, variantName: string, productSku: string | null, quantity: number, quantityFulfilled: number, quantityToFulfill: number }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, created: string, trackingNumber: string, lines: Array<{ quantity: number, orderLine: { id: string } | null }> | null }> } }> } | null } | null, orders: { totalCount: number | null, edges: Array<{ node: { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, chargeStatus: OrderChargeStatusEnum, authorizeStatus: OrderAuthorizeStatusEnum, isPaid: boolean, userEmail: string | null, languageCode: string, user: { id: string, email: string } | null, channel: { slug: string, currencyCode: string }, total: { gross: { amount: number, currency: string } }, totalCharged: { amount: number, currency: string }, totalRefunded: { amount: number, currency: string }, totalRefundPending: { amount: number, currency: string }, shippingAddress: { city: string, country: { code: string, country: string } } | null, lines: Array<{ id: string, productName: string, variantName: string, productSku: string | null, quantity: number, quantityFulfilled: number, quantityToFulfill: number }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, created: string, trackingNumber: string, lines: Array<{ quantity: number, orderLine: { id: string } | null }> | null }> } }> } | null };

export type StaffChatwootOrderByNumberQueryVariables = Exact<{
  number: number;
}>;


export type StaffChatwootOrderByNumberQuery = { orders: { totalCount: number | null, edges: Array<{ node: { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, chargeStatus: OrderChargeStatusEnum, authorizeStatus: OrderAuthorizeStatusEnum, isPaid: boolean, userEmail: string | null, languageCode: string, user: { id: string, email: string } | null, channel: { slug: string, currencyCode: string }, total: { gross: { amount: number, currency: string } }, totalCharged: { amount: number, currency: string }, totalRefunded: { amount: number, currency: string }, totalRefundPending: { amount: number, currency: string }, shippingAddress: { city: string, country: { code: string, country: string } } | null, lines: Array<{ id: string, productName: string, variantName: string, productSku: string | null, quantity: number, quantityFulfilled: number, quantityToFulfill: number }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, created: string, trackingNumber: string, lines: Array<{ quantity: number, orderLine: { id: string } | null }> | null }> } }> } | null };

export type StaffDueOrderIdsQueryVariables = Exact<{
  first: number;
  after?: string | null | undefined;
}>;


export type StaffDueOrderIdsQuery = { orders: { totalCount: number | null, edges: Array<{ node: { id: string } }>, pageInfo: { hasNextPage: boolean, endCursor: string | null } } | null };

export type StaffFulfillmentOrderCountsQueryVariables = Exact<{ [key: string]: never; }>;


export type StaffFulfillmentOrderCountsQuery = { unfulfilled: { totalCount: number | null } | null, partial: { totalCount: number | null } | null, fulfilled: { totalCount: number | null } | null, due: { totalCount: number | null } | null };

export type StaffFulfillmentOrdersQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
  last?: number | null | undefined;
  before?: string | null | undefined;
  filter?: OrderFilterInput | null | undefined;
}>;


export type StaffFulfillmentOrdersQuery = { orders: { totalCount: number | null, edges: Array<{ node: { id: string, number: string, created: string, userEmail: string | null, paymentStatus: PaymentChargeStatusEnum, status: OrderStatus, chargeStatus: OrderChargeStatusEnum, channel: { slug: string }, metadata: Array<{ key: string, value: string }>, total: { gross: { amount: number, currency: string } }, lines: Array<{ id: string, productName: string, productSku: string | null, quantity: number, quantityFulfilled: number, quantityToFulfill: number, thumbnail: { url: string, alt: string | null } | null }> } }>, pageInfo: { hasNextPage: boolean, hasPreviousPage: boolean, startCursor: string | null, endCursor: string | null } } | null };

export type StaffOrderDetailQueryVariables = Exact<{
  id: string | number;
}>;


export type StaffOrderDetailQuery = { order: { id: string, number: string, created: string, userEmail: string | null, languageCode: string, paymentStatus: PaymentChargeStatusEnum, status: OrderStatus, channel: { slug: string }, shippingAddress: { firstName: string, lastName: string, streetAddress1: string, streetAddress2: string, city: string, postalCode: string, country: { country: string, code: string }, metadata: Array<{ key: string, value: string }> } | null, total: { gross: { amount: number, currency: string } }, lines: Array<{ id: string, productName: string, productSku: string | null, quantity: number, quantityFulfilled: number, quantityToFulfill: number, thumbnail: { url: string, alt: string | null } | null, metadata: Array<{ key: string, value: string }> }>, fulfillments: Array<{ id: string, status: FulfillmentStatus, statusDisplay: string | null, trackingNumber: string, created: string, metadata: Array<{ key: string, value: string }>, lines: Array<{ id: string, quantity: number, orderLine: { id: string, productName: string, productSku: string | null } | null }> | null }> } | null };

export type StaffOrderFulfillMutationVariables = Exact<{
  orderId: string | number;
  input: OrderFulfillInput;
}>;


export type StaffOrderFulfillMutation = { orderFulfill: { fulfillments: Array<{ id: string, status: FulfillmentStatus, trackingNumber: string }> | null, errors: Array<{ field: string | null, message: string | null, code: OrderErrorCode, warehouse: string | null, orderLines: Array<string> | null }> } | null };

export type StaffOrderFulfillmentUpdateTrackingMutationVariables = Exact<{
  id: string | number;
  input: FulfillmentUpdateTrackingInput;
}>;


export type StaffOrderFulfillmentUpdateTrackingMutation = { orderFulfillmentUpdateTracking: { fulfillment: { id: string, status: FulfillmentStatus, statusDisplay: string | null, trackingNumber: string } | null, errors: Array<{ field: string | null, message: string | null, code: OrderErrorCode }> } | null };

export type StaffOrdersForExportQueryVariables = Exact<{
  ids?: Array<string | number> | string | number | null | undefined;
  first: number;
}>;


export type StaffOrdersForExportQuery = { orders: { edges: Array<{ node: { id: string, number: string, created: string, userEmail: string | null, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, channel: { slug: string }, shippingAddress: { firstName: string, lastName: string, companyName: string, streetAddress1: string, streetAddress2: string, city: string, postalCode: string, countryArea: string, phone: string | null, country: { code: string, country: string }, metadata: Array<{ key: string, value: string }> } | null, billingAddress: { firstName: string, lastName: string, companyName: string, streetAddress1: string, streetAddress2: string, city: string, postalCode: string, countryArea: string, phone: string | null, country: { code: string, country: string } } | null, lines: Array<{ productName: string, productSku: string | null, quantity: number, quantityToFulfill: number }>, total: { gross: { amount: number, currency: string } } } }> } | null };

export type StaffRecentOrderPhonesQueryVariables = Exact<{
  first: number;
}>;


export type StaffRecentOrderPhonesQuery = { orders: { edges: Array<{ node: { shippingAddress: { phone: string | null, country: { code: string } } | null } }> } | null };

export type StaffSupportOutcomeOrdersQueryVariables = Exact<{
  where: OrderWhereInput;
  first: number;
  after?: string | null | undefined;
}>;


export type StaffSupportOutcomeOrdersQuery = { orders: { pageInfo: { hasNextPage: boolean, endCursor: string | null }, edges: Array<{ node: { id: string, number: string, created: string, status: OrderStatus, userEmail: string | null, voucherCode: string | null, paytreeIntentId: string | null, user: { id: string, isStaff: boolean } | null, channel: { slug: string }, total: { gross: { amount: number, currency: string } }, totalRefunded: { amount: number, currency: string }, fulfillments: Array<{ status: FulfillmentStatus }>, transactions: Array<{ id: string }> } }> } | null };

export type StaffSupportOutcomeUserQueryVariables = Exact<{
  email: string;
}>;


export type StaffSupportOutcomeUserQuery = { user: { id: string } | null };

export type StaffUpdateFulfillmentMetadataMutationVariables = Exact<{
  id: string | number;
  input: Array<MetadataInput> | MetadataInput;
}>;


export type StaffUpdateFulfillmentMetadataMutation = { updateMetadata: { item:
      | { id: string, metadata: Array<{ key: string, value: string }> }
      | Record<PropertyKey, never>
     | null, errors: Array<{ field: string | null, message: string | null }> } | null };

export type StaffVariantStocksQueryVariables = Exact<{
  variantId: string | number;
}>;


export type StaffVariantStocksQuery = { productVariant: { id: string, sku: string | null, name: string, stocks: Array<{ quantity: number, quantityAllocated: number, warehouse: { id: string, name: string, slug: string } }> | null, channelListings: Array<{ channel: { slug: string }, price: { amount: number, currency: string } | null, costPrice: { amount: number, currency: string } | null }> | null } | null };

export type StaffWarehousesQueryVariables = Exact<{ [key: string]: never; }>;


export type StaffWarehousesQuery = { warehouses: { edges: Array<{ node: { id: string, name: string, slug: string } }> } | null };

export type SupportVoucherByCodeQueryVariables = Exact<{
  code: string;
}>;


export type SupportVoucherByCodeQuery = { vouchers: { edges: Array<{ node: { id: string, type: VoucherTypeEnum, discountValueType: DiscountValueTypeEnum, singleUse: boolean, onlyForStaff: boolean, applyOncePerOrder: boolean, minCheckoutItemsQuantity: number | null, endDate: string | null, codes: { edges: Array<{ node: { code: string | null } }> } | null, channelListings: Array<{ discountValue: number, channel: { slug: string }, minSpent: { amount: number } | null }> | null, privateMetadata: Array<{ key: string, value: string }> } }> } | null };

export type SupportVoucherChannelListingMutationVariables = Exact<{
  id: string | number;
  input: VoucherChannelListingInput;
}>;


export type SupportVoucherChannelListingMutation = { voucherChannelListingUpdate: { voucher: { id: string } | null, errors: Array<{ field: string | null, code: DiscountErrorCode }> } | null };

export type SupportVoucherChannelsQueryVariables = Exact<{ [key: string]: never; }>;


export type SupportVoucherChannelsQuery = { channels: Array<{ id: string, slug: string, isActive: boolean }> | null };

export type SupportVoucherCreateMutationVariables = Exact<{
  input: VoucherInput;
}>;


export type SupportVoucherCreateMutation = { voucherCreate: { voucher: { id: string } | null, errors: Array<{ field: string | null, code: DiscountErrorCode }> } | null };

export type SupportVoucherPrivateMetadataMutationVariables = Exact<{
  id: string | number;
  input: Array<MetadataInput> | MetadataInput;
}>;


export type SupportVoucherPrivateMetadataMutation = { updatePrivateMetadata: { errors: Array<{ field: string | null, code: MetadataErrorCode }> } | null };

export type TrackingOrderForReconciliationQueryVariables = Exact<{
  id: string | number;
}>;


export type TrackingOrderForReconciliationQuery = { order: { id: string, number: string, status: OrderStatus, channel: { slug: string }, shippingAddress: { postalCode: string, country: { code: string } } | null, fulfillments: Array<{ id: string, created: string, status: FulfillmentStatus, trackingNumber: string, metadata: Array<{ key: string, value: string }> }> } | null };

export type TrackingOrderContactQueryVariables = Exact<{
  id: string | number;
}>;


export type TrackingOrderContactQuery = { order: { id: string, number: string, userEmail: string | null, languageCodeEnum: LanguageCodeEnum, channel: { slug: string }, shippingAddress: { firstName: string } | null } | null };

export type UserCheckoutsQueryVariables = Exact<{
  channel: string;
  languageCode: LanguageCodeEnum;
}>;


export type UserCheckoutsQuery = { me: { checkouts: { edges: Array<{ node: { updatedAt: string, id: string, email: string | null, isShippingRequired: boolean, voucherCode: string | null, shippingAddress: { country: { code: string } } | null, problems: Array<
            | { __typename: 'CheckoutLineProblemInsufficientStock' }
            | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
            | { __typename: 'CheckoutProblemDeliveryMethodInvalid' }
            | { __typename: 'CheckoutProblemDeliveryMethodStale' }
          > | null, channel: { slug: string }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, undiscountedUnitPrice: { amount: number, currency: string }, totalPrice: { net: { amount: number, currency: string }, gross: { amount: number, currency: string } }, variant: { sku: string | null, name: string, id: string, product: { id: string, name: string, slug: string, translation: { name: string | null } | null, thumbnail: { url: string, alt: string | null } | null, category: { name: string, translation: { name: string | null } | null } | null }, pricing: { price: { net: { amount: number, currency: string }, gross: { amount: number, currency: string } } | null, priceUndiscounted: { gross: { amount: number, currency: string } } | null } | null, selectionAttributes: Array<{ attribute: { id: string, name: string, slug: string }, values: Array<{ id: string, slug: string | null, name: string | null, value: string | null }> }>, nonSelectionAttributes: Array<{ attribute: { id: string, name: string, slug: string }, values: Array<{ id: string, slug: string | null, name: string | null, value: string | null }> }> } }>, discount: { amount: number, currency: string } | null, delivery: { id: string } | null, shippingPrice: { gross: { amount: number, currency: string } }, subtotalPrice: { gross: { amount: number, currency: string }, net: { amount: number, currency: string } }, totalPrice: { gross: { amount: number, currency: string } } } }> } | null } | null };

export type UserDetailsFragment = { id: string, email: string, firstName: string, lastName: string, avatar: { url: string, alt: string | null } | null };

export type VariantDetailsFragment = { id: string, name: string, sku: string | null, quantityAvailable: number | null, selectionAttributes: Array<{ values: Array<{ id: string, slug: string | null, name: string | null, value: string | null }>, attribute: { id: string, slug: string } }>, media: Array<{ url: string, alt: string, type: ProductMediaType }> | null, pricing: { price: { gross: { currency: string, amount: number } } | null, priceUndiscounted: { gross: { currency: string, amount: number } } | null } | null };

export type CustomerByIdQueryVariables = Exact<{
  id: string | number;
}>;


export type CustomerByIdQuery = { user: { id: string, email: string, firstName: string, lastName: string, dateJoined: string, defaultShippingAddress: { streetAddress1: string, streetAddress2: string, city: string, postalCode: string, phone: string | null, country: { code: string, country: string } } | null, defaultBillingAddress: { streetAddress1: string, streetAddress2: string, city: string, postalCode: string, country: { code: string, country: string } } | null, orders: { totalCount: number | null, edges: Array<{ node: { id: string, number: string, created: string, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, channel: { slug: string, currencyCode: string }, total: { gross: { amount: number, currency: string } } } }> } | null } | null };

export type CustomersListQueryVariables = Exact<{
  first: number;
  after?: string | null | undefined;
  search?: string | null | undefined;
}>;


export type CustomersListQuery = { customers: { totalCount: number | null, pageInfo: { hasNextPage: boolean, endCursor: string | null }, edges: Array<{ node: { id: string, email: string, firstName: string, lastName: string, dateJoined: string, defaultShippingAddress: { country: { code: string, country: string } } | null, orders: { totalCount: number | null } | null } }> } | null };

export type OrdersInRangeQueryVariables = Exact<{
  first: number;
  after?: string | null | undefined;
  from?: string | null | undefined;
  to?: string | null | undefined;
  channels?: Array<string | number> | string | number | null | undefined;
}>;


export type OrdersInRangeQuery = { orders: { totalCount: number | null, pageInfo: { hasNextPage: boolean, endCursor: string | null }, edges: Array<{ node: { id: string, number: string, created: string, userEmail: string | null, status: OrderStatus, paymentStatus: PaymentChargeStatusEnum, channel: { slug: string, currencyCode: string }, total: { gross: { amount: number, currency: string } }, user: { id: string, isStaff: boolean } | null, shippingAddress: { firstName: string, country: { code: string } } | null, billingAddress: { country: { code: string } } | null, lines: Array<{ id: string, quantity: number, totalPrice: { gross: { amount: number, currency: string } }, variant: { id: string, name: string, product: { id: string, name: string, slug: string, category: { id: string, name: string, slug: string } | null }, attributes: Array<{ attribute: { slug: string, name: string }, values: Array<{ name: string | null, slug: string | null }> }> } | null }> } }> } | null };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}
export const AccountErrorFragmentFragmentDoc = new TypedDocumentString(`
    fragment AccountErrorFragment on AccountError {
  message
  field
  code
}
    `, {"fragmentName":"AccountErrorFragment"}) as unknown as TypedDocumentString<AccountErrorFragmentFragment, unknown>;
export const AccountAddressFragmentFragmentDoc = new TypedDocumentString(`
    fragment AccountAddressFragment on Address {
  id
  city
  phone
  postalCode
  companyName
  cityArea
  streetAddress1
  streetAddress2
  countryArea
  country {
    country
    code
  }
  firstName
  lastName
}
    `, {"fragmentName":"AccountAddressFragment"}) as unknown as TypedDocumentString<AccountAddressFragmentFragment, unknown>;
export const AccountUserWithAddressesFragmentFragmentDoc = new TypedDocumentString(`
    fragment AccountUserWithAddressesFragment on User {
  id
  email
  addresses {
    ...AccountAddressFragment
  }
  defaultBillingAddress {
    ...AccountAddressFragment
  }
  defaultShippingAddress {
    ...AccountAddressFragment
  }
}
    fragment AccountAddressFragment on Address {
  id
  city
  phone
  postalCode
  companyName
  cityArea
  streetAddress1
  streetAddress2
  countryArea
  country {
    country
    code
  }
  firstName
  lastName
}`, {"fragmentName":"AccountUserWithAddressesFragment"}) as unknown as TypedDocumentString<AccountUserWithAddressesFragmentFragment, unknown>;
export const ChatwootPanelOrderFragmentDoc = new TypedDocumentString(`
    fragment ChatwootPanelOrder on Order {
  id
  number
  created
  status
  paymentStatus
  chargeStatus
  authorizeStatus
  isPaid
  userEmail
  languageCode
  user {
    id
    email
  }
  channel {
    slug
    currencyCode
  }
  total {
    gross {
      amount
      currency
    }
  }
  totalCharged {
    amount
    currency
  }
  totalRefunded {
    amount
    currency
  }
  totalRefundPending {
    amount
    currency
  }
  shippingAddress {
    city
    country {
      code
      country
    }
  }
  lines {
    id
    productName
    variantName
    productSku
    quantity
    quantityFulfilled
    quantityToFulfill
  }
  fulfillments {
    id
    status
    created
    trackingNumber
    lines {
      orderLine {
        id
      }
      quantity
    }
  }
}
    `, {"fragmentName":"ChatwootPanelOrder"}) as unknown as TypedDocumentString<ChatwootPanelOrderFragment, unknown>;
export const AssignedAttributeFieldsFragmentDoc = new TypedDocumentString(`
    fragment AssignedAttributeFields on AssignedAttribute {
  __typename
  attribute {
    slug
    inputType
  }
  ... on AssignedSingleChoiceAttribute {
    choiceValue: value {
      name
      slug
    }
  }
  ... on AssignedNumericAttribute {
    numericValue: value
  }
  ... on AssignedPlainTextAttribute {
    plainTextValue: value
  }
  ... on AssignedTextAttribute {
    richTextValue: value
  }
  ... on AssignedDateAttribute {
    dateValue: value
  }
  ... on AssignedBooleanAttribute {
    booleanValue: value
  }
  ... on AssignedFileAttribute {
    fileValue: value {
      url
      contentType
    }
  }
  ... on AssignedSingleProductReferenceAttribute {
    productValue: value {
      slug
      name
    }
  }
  ... on AssignedSinglePageReferenceAttribute {
    pageValue: value {
      slug
      title
    }
  }
  ... on AssignedMultiProductReferenceAttribute {
    productListValue: value(limit: 20) {
      slug
      name
    }
  }
  ... on AssignedMultiPageReferenceAttribute {
    pageListValue: value(limit: 20) {
      slug
      title
    }
  }
}
    `, {"fragmentName":"AssignedAttributeFields"}) as unknown as TypedDocumentString<AssignedAttributeFieldsFragment, unknown>;
export const MenuItemFragmentDoc = new TypedDocumentString(`
    fragment MenuItem on MenuItem {
  id
  name
  level
  translation(languageCode: $languageCode) {
    name
  }
  category {
    id
    slug
    name
    translation(languageCode: $languageCode) {
      name
    }
  }
  collection {
    id
    name
    slug
    translation(languageCode: $languageCode) {
      name
    }
  }
  page {
    id
    title
    slug
    translation(languageCode: $languageCode) {
      title
    }
  }
  url
}
    `, {"fragmentName":"MenuItem"}) as unknown as TypedDocumentString<MenuItemFragment, unknown>;
export const OrderDetailsFragmentDoc = new TypedDocumentString(`
    fragment OrderDetails on Order {
  id
  number
  created
  status
  total {
    gross {
      amount
      currency
    }
  }
  lines {
    id
    variant {
      id
      name
      product {
        id
        name
        description
        slug
        thumbnail {
          url
          alt
        }
        category {
          id
          name
        }
      }
      pricing {
        price {
          gross {
            amount
            currency
          }
        }
      }
    }
    quantity
  }
  paymentStatus
  fulfillments {
    id
    status
    statusDisplay
    trackingNumber
    metadata {
      key
      value
    }
    lines {
      id
      quantity
      orderLine {
        id
      }
    }
  }
}
    `, {"fragmentName":"OrderDetails"}) as unknown as TypedDocumentString<OrderDetailsFragment, unknown>;
export const OrderDetailsFullFragmentDoc = new TypedDocumentString(`
    fragment OrderDetailsFull on Order {
  id
  number
  created
  status
  paymentStatus
  subtotal {
    gross {
      amount
      currency
    }
  }
  shippingPrice {
    gross {
      amount
      currency
    }
  }
  total {
    gross {
      amount
      currency
    }
  }
  undiscountedTotal {
    gross {
      amount
      currency
    }
  }
  discounts {
    id
    type
    valueType
    value
    reason
    amount {
      amount
      currency
    }
  }
  voucherCode
  shippingAddress {
    firstName
    lastName
    companyName
    streetAddress1
    streetAddress2
    city
    postalCode
    countryArea
    country {
      code
      country
    }
    phone
  }
  billingAddress {
    firstName
    lastName
    companyName
    streetAddress1
    streetAddress2
    city
    postalCode
    countryArea
    country {
      code
      country
    }
    phone
  }
  lines {
    id
    quantity
    metadata {
      key
      value
    }
    variant {
      id
      name
      product {
        id
        name
        slug
        thumbnail {
          url
          alt
        }
      }
      pricing {
        price {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
  fulfillments {
    id
    status
    statusDisplay
    trackingNumber
    metadata {
      key
      value
    }
    lines {
      id
      quantity
      orderLine {
        id
      }
    }
  }
}
    `, {"fragmentName":"OrderDetailsFull"}) as unknown as TypedDocumentString<OrderDetailsFullFragment, unknown>;
export const ProductListItemFragmentDoc = new TypedDocumentString(`
    fragment ProductListItem on Product {
  id
  name
  slug
  created
  translation(languageCode: $languageCode) {
    name
  }
  pricing {
    priceRange {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
    priceRangeUndiscounted {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
  }
  category {
    id
    name
    slug
    translation(languageCode: $languageCode) {
      name
    }
  }
  thumbnail(size: 640, format: WEBP) {
    url
    alt
  }
  metafields(
    keys: ["ux_card_copy_json", "ux_short_description", "ux_trust_badges_json", "ux_research_level", "ux_peptide_scores_json", "ux_peptide_categories_json", "ux_availability", "ux_mechanism", "ux_typical_dosage", "ux_half_life", "ux_sequence", "ux_pubmed_url", "ux_aliases_json", "ux_timing", "ux_dosing_frequency", "ux_contraindicated_combinations_json"]
  )
  variants {
    id
    selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
      attribute {
        slug
        name
      }
      values {
        name
        value
      }
    }
  }
}
    `, {"fragmentName":"ProductListItem"}) as unknown as TypedDocumentString<ProductListItemFragment, unknown>;
export const UserDetailsFragmentDoc = new TypedDocumentString(`
    fragment UserDetails on User {
  id
  email
  firstName
  lastName
  avatar {
    url
    alt
  }
}
    `, {"fragmentName":"UserDetails"}) as unknown as TypedDocumentString<UserDetailsFragment, unknown>;
export const VariantDetailsFragmentDoc = new TypedDocumentString(`
    fragment VariantDetails on ProductVariant {
  id
  name
  sku
  quantityAvailable
  selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
    values {
      id
      slug
      name
      value
    }
    attribute {
      id
      slug
    }
  }
  media {
    url(size: 1024, format: WEBP)
    alt
    type
  }
  pricing {
    price {
      gross {
        currency
        amount
      }
    }
    priceUndiscounted {
      gross {
        currency
        amount
      }
    }
  }
}
    `, {"fragmentName":"VariantDetails"}) as unknown as TypedDocumentString<VariantDetailsFragment, unknown>;
export const AccountAddressDeleteDocument = new TypedDocumentString(`
    mutation AccountAddressDelete($id: ID!) {
  accountAddressDelete(id: $id) {
    user {
      ...AccountUserWithAddressesFragment
    }
    errors {
      ...AccountErrorFragment
    }
    address {
      ...AccountAddressFragment
    }
  }
}
    fragment AccountAddressFragment on Address {
  id
  city
  phone
  postalCode
  companyName
  cityArea
  streetAddress1
  streetAddress2
  countryArea
  country {
    country
    code
  }
  firstName
  lastName
}
fragment AccountErrorFragment on AccountError {
  message
  field
  code
}
fragment AccountUserWithAddressesFragment on User {
  id
  email
  addresses {
    ...AccountAddressFragment
  }
  defaultBillingAddress {
    ...AccountAddressFragment
  }
  defaultShippingAddress {
    ...AccountAddressFragment
  }
}`) as unknown as TypedDocumentString<AccountAddressDeleteMutation, AccountAddressDeleteMutationVariables>;
export const AccountAddressUpdateDocument = new TypedDocumentString(`
    mutation AccountAddressUpdate($id: ID!, $address: AddressInput!) {
  accountAddressUpdate(id: $id, input: $address) {
    user {
      ...AccountUserWithAddressesFragment
    }
    errors {
      ...AccountErrorFragment
    }
    address {
      ...AccountAddressFragment
    }
  }
}
    fragment AccountAddressFragment on Address {
  id
  city
  phone
  postalCode
  companyName
  cityArea
  streetAddress1
  streetAddress2
  countryArea
  country {
    country
    code
  }
  firstName
  lastName
}
fragment AccountErrorFragment on AccountError {
  message
  field
  code
}
fragment AccountUserWithAddressesFragment on User {
  id
  email
  addresses {
    ...AccountAddressFragment
  }
  defaultBillingAddress {
    ...AccountAddressFragment
  }
  defaultShippingAddress {
    ...AccountAddressFragment
  }
}`) as unknown as TypedDocumentString<AccountAddressUpdateMutation, AccountAddressUpdateMutationVariables>;
export const AccountAddressCreateDocument = new TypedDocumentString(`
    mutation AccountAddressCreate($address: AddressInput!, $type: AddressTypeEnum) {
  accountAddressCreate(type: $type, input: $address) {
    user {
      ...AccountUserWithAddressesFragment
    }
    errors {
      ...AccountErrorFragment
    }
    address {
      ...AccountAddressFragment
    }
  }
}
    fragment AccountAddressFragment on Address {
  id
  city
  phone
  postalCode
  companyName
  cityArea
  streetAddress1
  streetAddress2
  countryArea
  country {
    country
    code
  }
  firstName
  lastName
}
fragment AccountErrorFragment on AccountError {
  message
  field
  code
}
fragment AccountUserWithAddressesFragment on User {
  id
  email
  addresses {
    ...AccountAddressFragment
  }
  defaultBillingAddress {
    ...AccountAddressFragment
  }
  defaultShippingAddress {
    ...AccountAddressFragment
  }
}`) as unknown as TypedDocumentString<AccountAddressCreateMutation, AccountAddressCreateMutationVariables>;
export const AccountSetDefaultAddressDocument = new TypedDocumentString(`
    mutation AccountSetDefaultAddress($id: ID!, $type: AddressTypeEnum!) {
  accountSetDefaultAddress(id: $id, type: $type) {
    user {
      ...AccountUserWithAddressesFragment
    }
    errors {
      ...AccountErrorFragment
    }
  }
}
    fragment AccountAddressFragment on Address {
  id
  city
  phone
  postalCode
  companyName
  cityArea
  streetAddress1
  streetAddress2
  countryArea
  country {
    country
    code
  }
  firstName
  lastName
}
fragment AccountErrorFragment on AccountError {
  message
  field
  code
}
fragment AccountUserWithAddressesFragment on User {
  id
  email
  addresses {
    ...AccountAddressFragment
  }
  defaultBillingAddress {
    ...AccountAddressFragment
  }
  defaultShippingAddress {
    ...AccountAddressFragment
  }
}`) as unknown as TypedDocumentString<AccountSetDefaultAddressMutation, AccountSetDefaultAddressMutationVariables>;
export const AccountConfirmDocument = new TypedDocumentString(`
    mutation AccountConfirm($email: String!, $token: String!) {
  confirmAccount(email: $email, token: $token) {
    user {
      id
      email
      isActive
    }
    errors {
      field
      message
      code
    }
  }
}
    `) as unknown as TypedDocumentString<AccountConfirmMutation, AccountConfirmMutationVariables>;
export const BacCrossSellProductDocument = new TypedDocumentString(`
    query BacCrossSellProduct($slug: String!, $channel: String!) {
  product(slug: $slug, channel: $channel) {
    slug
    thumbnail(size: 128, format: WEBP) {
      url
    }
    variants {
      id
      name
      pricing {
        price {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<BacCrossSellProductQuery, BacCrossSellProductQueryVariables>;
export const BlackFridayCheckoutDocument = new TypedDocumentString(`
    query BlackFridayCheckout($id: ID!, $includeReason: Boolean!) {
  checkout(id: $id) {
    id
    email
    voucherCode
    channel {
      slug
    }
    totalPrice {
      gross {
        amount
        currency
      }
    }
    lines {
      id
      quantity
      metadata {
        key
        value
      }
      priceOverrideReason @include(if: $includeReason)
      priorUnitPrice {
        amount
      }
      undiscountedUnitPrice {
        amount
      }
      variant {
        id
        product {
          slug
        }
        pricing {
          priceUndiscounted {
            net {
              amount
              currency
            }
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<BlackFridayCheckoutQuery, BlackFridayCheckoutQueryVariables>;
export const BlackFridayCheckoutLinesUpdateDocument = new TypedDocumentString(`
    mutation BlackFridayCheckoutLinesUpdate($id: ID!, $lines: [CheckoutLineUpdateInput!]!) {
  checkoutLinesUpdate(id: $id, lines: $lines) {
    checkout {
      id
    }
    errors {
      code
      field
    }
  }
}
    `) as unknown as TypedDocumentString<BlackFridayCheckoutLinesUpdateMutation, BlackFridayCheckoutLinesUpdateMutationVariables>;
export const BlackFridayCheckoutRemovePromoCodeDocument = new TypedDocumentString(`
    mutation BlackFridayCheckoutRemovePromoCode($id: ID!, $promoCode: String!) {
  checkoutRemovePromoCode(id: $id, promoCode: $promoCode) {
    checkout {
      id
    }
    errors {
      code
      field
    }
  }
}
    `) as unknown as TypedDocumentString<BlackFridayCheckoutRemovePromoCodeMutation, BlackFridayCheckoutRemovePromoCodeMutationVariables>;
export const BlackFridayProductsDocument = new TypedDocumentString(`
    query BlackFridayProducts($channel: String!, $languageCode: LanguageCodeEnum!, $slugs: [String!]!) {
  products(
    channel: $channel
    first: 12
    filter: {slugs: $slugs, isPublished: true}
  ) {
    edges {
      node {
        id
        slug
        name
        translation(languageCode: $languageCode) {
          name
        }
        isAvailable
        isAvailableForPurchase
        thumbnail(size: 512, format: WEBP) {
          url
          alt
        }
        variants {
          id
          name
          sku
          quantityAvailable
          pricing {
            price {
              gross {
                amount
                currency
              }
            }
            priceUndiscounted {
              gross {
                amount
                currency
              }
            }
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<BlackFridayProductsQuery, BlackFridayProductsQueryVariables>;
export const CalculatorBuyBridgeProductDocument = new TypedDocumentString(`
    query CalculatorBuyBridgeProduct($slug: String!, $channel: String!, $languageCode: LanguageCodeEnum!) {
  product(slug: $slug, channel: $channel) {
    id
    slug
    name
    isAvailable
    translation(languageCode: $languageCode) {
      name
    }
    thumbnail(size: 256, format: WEBP) {
      url
      alt
    }
    pricing {
      priceRange {
        start {
          gross {
            amount
            currency
          }
        }
      }
    }
    variants {
      id
      name
      quantityAvailable
      pricing {
        price {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<CalculatorBuyBridgeProductQuery, CalculatorBuyBridgeProductQueryVariables>;
export const CategoriesBySlugDocument = new TypedDocumentString(`
    query CategoriesBySlug($slugs: [String!]!, $first: Int = 20, $languageCode: LanguageCodeEnum!) {
  categories(filter: {slugs: $slugs}, first: $first) {
    edges {
      node {
        id
        slug
        name
        translation(languageCode: $languageCode) {
          name
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<CategoriesBySlugQuery, CategoriesBySlugQueryVariables>;
export const CategoryTranslateDocument = new TypedDocumentString(`
    mutation CategoryTranslate($id: ID!, $languageCode: LanguageCodeEnum!, $input: TranslationInput!) {
  categoryTranslate(id: $id, languageCode: $languageCode, input: $input) {
    category {
      id
    }
    errors {
      field
      message
    }
  }
}
    `) as unknown as TypedDocumentString<CategoryTranslateMutation, CategoryTranslateMutationVariables>;
export const CategoryTreeForSwitcherDocument = new TypedDocumentString(`
    query CategoryTreeForSwitcher($languageCode: LanguageCodeEnum!, $channel: String!) {
  category(slug: "peptides") {
    id
    slug
    children(first: 20) {
      edges {
        node {
          id
          slug
          name
          translation(languageCode: $languageCode) {
            name
          }
          products(channel: $channel, first: 1) {
            totalCount
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<CategoryTreeForSwitcherQuery, CategoryTreeForSwitcherQueryVariables>;
export const ChannelsListDocument = new TypedDocumentString(`
    query ChannelsList {
  channels {
    id
    name
    slug
    isActive
    currencyCode
    countries {
      country
      code
    }
  }
}
    `) as unknown as TypedDocumentString<ChannelsListQuery, ChannelsListQueryVariables>;
export const ChatwootPanelTrackingDetailsDocument = new TypedDocumentString(`
    query ChatwootPanelTrackingDetails($ids: [ID!]!) {
  orders(first: 10, where: {ids: $ids}) {
    edges {
      node {
        id
        number
        status
        userEmail
        user {
          email
        }
        channel {
          slug
        }
        fulfillments {
          id
          created
          status
          trackingNumber
          metadata {
            key
            value
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<ChatwootPanelTrackingDetailsQuery, ChatwootPanelTrackingDetailsQueryVariables>;
export const CheckoutAddLineDocument = new TypedDocumentString(`
    mutation CheckoutAddLine($id: ID!, $productVariantId: ID!, $quantity: Int!) {
  checkoutLinesAdd(
    id: $id
    lines: [{quantity: $quantity, variantId: $productVariantId}]
  ) {
    checkout {
      id
      lines {
        id
        quantity
        unitPrice {
          gross {
            amount
            currency
          }
        }
        totalPrice {
          gross {
            amount
            currency
          }
        }
        variant {
          id
          name
          sku
          selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
            attribute {
              slug
            }
            values {
              name
              value
            }
          }
          product {
            id
            name
            slug
          }
        }
      }
    }
    errors {
      field
      code
      message
    }
  }
}
    `) as unknown as TypedDocumentString<CheckoutAddLineMutation, CheckoutAddLineMutationVariables>;
export const CheckoutAddLinesDocument = new TypedDocumentString(`
    mutation CheckoutAddLines($id: ID!, $lines: [CheckoutLineInput!]!) {
  checkoutLinesAdd(id: $id, lines: $lines) {
    checkout {
      id
      lines {
        id
        quantity
        unitPrice {
          gross {
            amount
            currency
          }
        }
        totalPrice {
          gross {
            amount
            currency
          }
        }
        variant {
          id
          name
          selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
            attribute {
              slug
            }
            values {
              name
              value
            }
          }
        }
      }
    }
    errors {
      field
      code
      message
    }
  }
}
    `) as unknown as TypedDocumentString<CheckoutAddLinesMutation, CheckoutAddLinesMutationVariables>;
export const CheckoutAddPromoCodeDocument = new TypedDocumentString(`
    mutation CheckoutAddPromoCode($checkoutId: ID, $promoCode: String!) {
  checkoutAddPromoCode(checkoutId: $checkoutId, promoCode: $promoCode) {
    errors {
      field
      code
      message
    }
    checkout {
      id
      voucherCode
      discount {
        amount
        currency
      }
      totalPrice {
        gross {
          amount
          currency
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<CheckoutAddPromoCodeMutation, CheckoutAddPromoCodeMutationVariables>;
export const CheckoutCreateDocument = new TypedDocumentString(`
    mutation CheckoutCreate($channel: String!, $languageCode: LanguageCodeEnum!) {
  checkoutCreate(
    input: {channel: $channel, lines: [], languageCode: $languageCode}
  ) {
    checkout {
      id
      email
      lines {
        id
        quantity
        totalPrice {
          gross {
            amount
            currency
          }
        }
        variant {
          product {
            id
            name
            slug
            thumbnail {
              url
              alt
            }
            category {
              name
            }
          }
          pricing {
            price {
              gross {
                amount
                currency
              }
            }
          }
          name
          id
        }
      }
      totalPrice {
        gross {
          amount
          currency
        }
      }
    }
    errors {
      field
      code
    }
  }
}
    `) as unknown as TypedDocumentString<CheckoutCreateMutation, CheckoutCreateMutationVariables>;
export const CheckoutDeleteLinesDocument = new TypedDocumentString(`
    mutation CheckoutDeleteLines($checkoutId: ID!, $lineIds: [ID!]!) {
  checkoutLinesDelete(id: $checkoutId, linesIds: $lineIds) {
    checkout {
      id
      lines {
        id
      }
      channel {
        slug
      }
    }
    errors {
      field
      code
    }
  }
}
    `) as unknown as TypedDocumentString<CheckoutDeleteLinesMutation, CheckoutDeleteLinesMutationVariables>;
export const CheckoutFindDocument = new TypedDocumentString(`
    query CheckoutFind($id: ID!, $languageCode: LanguageCodeEnum!) {
  checkout(id: $id) {
    id
    email
    isShippingRequired
    shippingAddress {
      country {
        code
      }
    }
    problems {
      __typename
    }
    channel {
      slug
    }
    lines {
      id
      quantity
      metadata {
        key
        value
      }
      undiscountedUnitPrice {
        amount
        currency
      }
      totalPrice {
        net {
          amount
          currency
        }
        gross {
          amount
          currency
        }
      }
      variant {
        sku
        product {
          id
          name
          slug
          translation(languageCode: $languageCode) {
            name
          }
          thumbnail {
            url
            alt
          }
          category {
            name
            translation(languageCode: $languageCode) {
              name
            }
          }
        }
        pricing {
          price {
            net {
              amount
              currency
            }
            gross {
              amount
              currency
            }
          }
          priceUndiscounted {
            gross {
              amount
              currency
            }
          }
        }
        name
        id
        selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
          attribute {
            id
            name
            slug
          }
          values {
            id
            slug
            name
            value
          }
        }
        nonSelectionAttributes: attributes(variantSelection: NOT_VARIANT_SELECTION) {
          attribute {
            id
            name
            slug
          }
          values {
            id
            slug
            name
            value
          }
        }
      }
    }
    voucherCode
    discount {
      amount
      currency
    }
    delivery {
      id
    }
    shippingPrice {
      gross {
        amount
        currency
      }
    }
    subtotalPrice {
      gross {
        amount
        currency
      }
      net {
        amount
        currency
      }
    }
    totalPrice {
      gross {
        amount
        currency
      }
    }
  }
}
    `) as unknown as TypedDocumentString<CheckoutFindQuery, CheckoutFindQueryVariables>;
export const CheckoutLinesUpdateDocument = new TypedDocumentString(`
    mutation CheckoutLinesUpdate($checkoutId: ID!, $lines: [CheckoutLineUpdateInput!]!) {
  checkoutLinesUpdate(id: $checkoutId, lines: $lines) {
    checkout {
      id
      lines {
        id
        quantity
        totalPrice {
          gross {
            amount
            currency
          }
        }
      }
      totalPrice {
        gross {
          amount
          currency
        }
      }
    }
    errors {
      field
      message
      code
    }
  }
}
    `) as unknown as TypedDocumentString<CheckoutLinesUpdateMutation, CheckoutLinesUpdateMutationVariables>;
export const CheckoutReplacementLineAddDocument = new TypedDocumentString(`
    mutation CheckoutReplacementLineAdd($id: ID!, $line: CheckoutLineInput!) {
  checkoutLinesAdd(id: $id, lines: [$line]) {
    checkout {
      id
      lines {
        id
        quantity
        metadata {
          key
          value
        }
        variant {
          id
        }
      }
    }
    errors {
      field
      code
      message
    }
  }
}
    `) as unknown as TypedDocumentString<CheckoutReplacementLineAddMutation, CheckoutReplacementLineAddMutationVariables>;
export const CollectionTranslateDocument = new TypedDocumentString(`
    mutation CollectionTranslate($id: ID!, $languageCode: LanguageCodeEnum!, $input: TranslationInput!) {
  collectionTranslate(id: $id, languageCode: $languageCode, input: $input) {
    collection {
      id
    }
    errors {
      field
      message
    }
  }
}
    `) as unknown as TypedDocumentString<CollectionTranslateMutation, CollectionTranslateMutationVariables>;
export const CollectionsListDocument = new TypedDocumentString(`
    query CollectionsList($channel: String!, $first: Int, $after: String, $languageCode: LanguageCodeEnum!) {
  collections(channel: $channel, first: $first, after: $after) {
    edges {
      node {
        id
        slug
        name
        translation(languageCode: $languageCode) {
          name
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<CollectionsListQuery, CollectionsListQueryVariables>;
export const CompareBySlugDocument = new TypedDocumentString(`
    query CompareBySlug($slug: String!, $languageCode: LanguageCodeEnum!) {
  page(slug: $slug) {
    id
    slug
    title
    seoTitle
    seoDescription
    publishedAt
    created
    metadata {
      key
      value
    }
    pageType {
      slug
    }
    assignedAttributes(limit: 20) {
      ...AssignedAttributeFields
    }
    attributes {
      attribute {
        slug
      }
      values {
        id
        plainText
        translation(languageCode: $languageCode) {
          plainText
        }
      }
    }
    translation(languageCode: $languageCode) {
      title
      seoTitle
      seoDescription
    }
  }
}
    fragment AssignedAttributeFields on AssignedAttribute {
  __typename
  attribute {
    slug
    inputType
  }
  ... on AssignedSingleChoiceAttribute {
    choiceValue: value {
      name
      slug
    }
  }
  ... on AssignedNumericAttribute {
    numericValue: value
  }
  ... on AssignedPlainTextAttribute {
    plainTextValue: value
  }
  ... on AssignedTextAttribute {
    richTextValue: value
  }
  ... on AssignedDateAttribute {
    dateValue: value
  }
  ... on AssignedBooleanAttribute {
    booleanValue: value
  }
  ... on AssignedFileAttribute {
    fileValue: value {
      url
      contentType
    }
  }
  ... on AssignedSingleProductReferenceAttribute {
    productValue: value {
      slug
      name
    }
  }
  ... on AssignedSinglePageReferenceAttribute {
    pageValue: value {
      slug
      title
    }
  }
  ... on AssignedMultiProductReferenceAttribute {
    productListValue: value(limit: 20) {
      slug
      name
    }
  }
  ... on AssignedMultiPageReferenceAttribute {
    pageListValue: value(limit: 20) {
      slug
      title
    }
  }
}`) as unknown as TypedDocumentString<CompareBySlugQuery, CompareBySlugQueryVariables>;
export const ComparisonCtaProductDocument = new TypedDocumentString(`
    query ComparisonCtaProduct($slug: String!, $channel: String!, $languageCode: LanguageCodeEnum!) {
  product(slug: $slug, channel: $channel) {
    name
    translation(languageCode: $languageCode) {
      name
    }
    thumbnail(size: 256, format: WEBP) {
      url
      alt
    }
    pricing {
      priceRange {
        start {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<ComparisonCtaProductQuery, ComparisonCtaProductQueryVariables>;
export const ComparisonTranslationPresenceDocument = new TypedDocumentString(`
    query ComparisonTranslationPresence($slug: String!) {
  page(slug: $slug) {
    id
    slug
    pageType {
      slug
    }
    attributes {
      attribute {
        slug
      }
      values {
        id
        translationEn: translation(languageCode: EN_US) {
          plainText
        }
        translationIt: translation(languageCode: IT_IT) {
          plainText
        }
        translationEs: translation(languageCode: ES_ES) {
          plainText
        }
        translationEsMx: translation(languageCode: ES_MX) {
          plainText
        }
        translationFr: translation(languageCode: FR_FR) {
          plainText
        }
        translationNl: translation(languageCode: NL_NL) {
          plainText
        }
        translationPt: translation(languageCode: PT_PT) {
          plainText
        }
        translationPtBr: translation(languageCode: PT_BR) {
          plainText
        }
        translationPl: translation(languageCode: PL_PL) {
          plainText
        }
        translationSv: translation(languageCode: SV_SE) {
          plainText
        }
        translationDa: translation(languageCode: DA_DK) {
          plainText
        }
        translationEl: translation(languageCode: EL_GR) {
          plainText
        }
        translationCs: translation(languageCode: CS_CZ) {
          plainText
        }
        translationFi: translation(languageCode: FI_FI) {
          plainText
        }
        translationRo: translation(languageCode: RO_RO) {
          plainText
        }
        translationHu: translation(languageCode: HU_HU) {
          plainText
        }
        translationSk: translation(languageCode: SK_SK) {
          plainText
        }
        translationTr: translation(languageCode: TR_TR) {
          plainText
        }
        translationNb: translation(languageCode: NB_NO) {
          plainText
        }
        translationBg: translation(languageCode: BG_BG) {
          plainText
        }
        translationHr: translation(languageCode: HR_HR) {
          plainText
        }
        translationSl: translation(languageCode: SL_SI) {
          plainText
        }
        translationSr: translation(languageCode: SR_LATN_RS) {
          plainText
        }
        translationBs: translation(languageCode: BS_LATN) {
          plainText
        }
        translationMk: translation(languageCode: MK_MK) {
          plainText
        }
        translationLt: translation(languageCode: LT_LT) {
          plainText
        }
        translationLv: translation(languageCode: LV_LV) {
          plainText
        }
        translationEt: translation(languageCode: ET_EE) {
          plainText
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<ComparisonTranslationPresenceQuery, ComparisonTranslationPresenceQueryVariables>;
export const ComparisonsListDocument = new TypedDocumentString(`
    query ComparisonsList($first: Int!, $after: String, $pageTypes: [ID!]) {
  pages(
    first: $first
    after: $after
    sortBy: {field: PUBLISHED_AT, direction: DESC}
    filter: {pageTypes: $pageTypes}
  ) {
    edges {
      node {
        id
        slug
        publishedAt
        metadata {
          key
          value
        }
        assignedAttributes(limit: 20) {
          __typename
          attribute {
            slug
          }
          ... on AssignedDateAttribute {
            dateValue: value
          }
          ... on AssignedMultiProductReferenceAttribute {
            productListValue: value(limit: 5) {
              slug
              name
            }
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<ComparisonsListQuery, ComparisonsListQueryVariables>;
export const CurrentUserDocument = new TypedDocumentString(`
    query CurrentUser {
  me {
    ...UserDetails
  }
}
    fragment UserDetails on User {
  id
  email
  firstName
  lastName
  avatar {
    url
    alt
  }
}`) as unknown as TypedDocumentString<CurrentUserQuery, CurrentUserQueryVariables>;
export const CurrentUserAccountSummaryDocument = new TypedDocumentString(`
    query CurrentUserAccountSummary {
  me {
    ...UserDetails
    orders(first: 1) {
      totalCount
    }
  }
}
    fragment UserDetails on User {
  id
  email
  firstName
  lastName
  avatar {
    url
    alt
  }
}`) as unknown as TypedDocumentString<CurrentUserAccountSummaryQuery, CurrentUserAccountSummaryQueryVariables>;
export const CurrentUserAddressesDocument = new TypedDocumentString(`
    query CurrentUserAddresses {
  me {
    ...AccountUserWithAddressesFragment
  }
}
    fragment AccountAddressFragment on Address {
  id
  city
  phone
  postalCode
  companyName
  cityArea
  streetAddress1
  streetAddress2
  countryArea
  country {
    country
    code
  }
  firstName
  lastName
}
fragment AccountUserWithAddressesFragment on User {
  id
  email
  addresses {
    ...AccountAddressFragment
  }
  defaultBillingAddress {
    ...AccountAddressFragment
  }
  defaultShippingAddress {
    ...AccountAddressFragment
  }
}`) as unknown as TypedDocumentString<CurrentUserAddressesQuery, CurrentUserAddressesQueryVariables>;
export const CurrentUserOrderDetailsDocument = new TypedDocumentString(`
    query CurrentUserOrderDetails($id: ID!) {
  order(id: $id) {
    ...OrderDetailsFull
  }
}
    fragment OrderDetailsFull on Order {
  id
  number
  created
  status
  paymentStatus
  subtotal {
    gross {
      amount
      currency
    }
  }
  shippingPrice {
    gross {
      amount
      currency
    }
  }
  total {
    gross {
      amount
      currency
    }
  }
  undiscountedTotal {
    gross {
      amount
      currency
    }
  }
  discounts {
    id
    type
    valueType
    value
    reason
    amount {
      amount
      currency
    }
  }
  voucherCode
  shippingAddress {
    firstName
    lastName
    companyName
    streetAddress1
    streetAddress2
    city
    postalCode
    countryArea
    country {
      code
      country
    }
    phone
  }
  billingAddress {
    firstName
    lastName
    companyName
    streetAddress1
    streetAddress2
    city
    postalCode
    countryArea
    country {
      code
      country
    }
    phone
  }
  lines {
    id
    quantity
    metadata {
      key
      value
    }
    variant {
      id
      name
      product {
        id
        name
        slug
        thumbnail {
          url
          alt
        }
      }
      pricing {
        price {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
  fulfillments {
    id
    status
    statusDisplay
    trackingNumber
    metadata {
      key
      value
    }
    lines {
      id
      quantity
      orderLine {
        id
      }
    }
  }
}`) as unknown as TypedDocumentString<CurrentUserOrderDetailsQuery, CurrentUserOrderDetailsQueryVariables>;
export const CurrentUserOrderListDocument = new TypedDocumentString(`
    query CurrentUserOrderList($first: Int = 50, $after: String, $where: CustomerOrderWhereInput) {
  me {
    ...UserDetails
    orders(first: $first, after: $after, where: $where) {
      edges {
        cursor
        node {
          ...OrderDetails
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
    fragment OrderDetails on Order {
  id
  number
  created
  status
  total {
    gross {
      amount
      currency
    }
  }
  lines {
    id
    variant {
      id
      name
      product {
        id
        name
        description
        slug
        thumbnail {
          url
          alt
        }
        category {
          id
          name
        }
      }
      pricing {
        price {
          gross {
            amount
            currency
          }
        }
      }
    }
    quantity
  }
  paymentStatus
  fulfillments {
    id
    status
    statusDisplay
    trackingNumber
    metadata {
      key
      value
    }
    lines {
      id
      quantity
      orderLine {
        id
      }
    }
  }
}
fragment UserDetails on User {
  id
  email
  firstName
  lastName
  avatar {
    url
    alt
  }
}`) as unknown as TypedDocumentString<CurrentUserOrderListQuery, CurrentUserOrderListQueryVariables>;
export const FileUploadDocument = new TypedDocumentString(`
    mutation FileUpload($file: Upload!) {
  fileUpload(file: $file) {
    uploadedFile {
      url
      contentType
    }
    errors {
      field
      message
    }
  }
}
    `) as unknown as TypedDocumentString<FileUploadMutation, FileUploadMutationVariables>;
export const GuideBySlugDocument = new TypedDocumentString(`
    query GuideBySlug($slug: String!, $languageCode: LanguageCodeEnum!) {
  page(slug: $slug) {
    id
    slug
    title
    content
    seoTitle
    seoDescription
    publishedAt
    created
    metadata {
      key
      value
    }
    pageType {
      slug
    }
    assignedAttributes(limit: 20) {
      ...AssignedAttributeFields
    }
    translation(languageCode: $languageCode) {
      title
      content
      seoTitle
      seoDescription
    }
  }
}
    fragment AssignedAttributeFields on AssignedAttribute {
  __typename
  attribute {
    slug
    inputType
  }
  ... on AssignedSingleChoiceAttribute {
    choiceValue: value {
      name
      slug
    }
  }
  ... on AssignedNumericAttribute {
    numericValue: value
  }
  ... on AssignedPlainTextAttribute {
    plainTextValue: value
  }
  ... on AssignedTextAttribute {
    richTextValue: value
  }
  ... on AssignedDateAttribute {
    dateValue: value
  }
  ... on AssignedBooleanAttribute {
    booleanValue: value
  }
  ... on AssignedFileAttribute {
    fileValue: value {
      url
      contentType
    }
  }
  ... on AssignedSingleProductReferenceAttribute {
    productValue: value {
      slug
      name
    }
  }
  ... on AssignedSinglePageReferenceAttribute {
    pageValue: value {
      slug
      title
    }
  }
  ... on AssignedMultiProductReferenceAttribute {
    productListValue: value(limit: 20) {
      slug
      name
    }
  }
  ... on AssignedMultiPageReferenceAttribute {
    pageListValue: value(limit: 20) {
      slug
      title
    }
  }
}`) as unknown as TypedDocumentString<GuideBySlugQuery, GuideBySlugQueryVariables>;
export const GuideTranslationPresenceDocument = new TypedDocumentString(`
    query GuideTranslationPresence($slug: String!, $translationEn: Boolean = false, $translationIt: Boolean = false, $translationEs: Boolean = false, $translationEsMx: Boolean = false, $translationFr: Boolean = false, $translationNl: Boolean = false, $translationPt: Boolean = false, $translationPtBr: Boolean = false, $translationPl: Boolean = false, $translationSv: Boolean = false, $translationDa: Boolean = false, $translationEl: Boolean = false, $translationCs: Boolean = false, $translationFi: Boolean = false, $translationRo: Boolean = false, $translationHu: Boolean = false, $translationSk: Boolean = false, $translationTr: Boolean = false, $translationNb: Boolean = false, $translationBg: Boolean = false, $translationHr: Boolean = false, $translationSl: Boolean = false, $translationSr: Boolean = false, $translationBs: Boolean = false, $translationMk: Boolean = false, $translationLt: Boolean = false, $translationLv: Boolean = false, $translationEt: Boolean = false) {
  page(slug: $slug) {
    id
    slug
    title
    content
    pageType {
      slug
    }
    translationEn: translation(languageCode: EN_US) @include(if: $translationEn) {
      title
      content
    }
    translationIt: translation(languageCode: IT_IT) @include(if: $translationIt) {
      title
      content
    }
    translationEs: translation(languageCode: ES_ES) @include(if: $translationEs) {
      title
      content
    }
    translationEsMx: translation(languageCode: ES_MX) @include(if: $translationEsMx) {
      title
      content
    }
    translationFr: translation(languageCode: FR_FR) @include(if: $translationFr) {
      title
      content
    }
    translationNl: translation(languageCode: NL_NL) @include(if: $translationNl) {
      title
      content
    }
    translationPt: translation(languageCode: PT_PT) @include(if: $translationPt) {
      title
      content
    }
    translationPtBr: translation(languageCode: PT_BR) @include(if: $translationPtBr) {
      title
      content
    }
    translationPl: translation(languageCode: PL_PL) @include(if: $translationPl) {
      title
      content
    }
    translationSv: translation(languageCode: SV_SE) @include(if: $translationSv) {
      title
      content
    }
    translationDa: translation(languageCode: DA_DK) @include(if: $translationDa) {
      title
      content
    }
    translationEl: translation(languageCode: EL_GR) @include(if: $translationEl) {
      title
      content
    }
    translationCs: translation(languageCode: CS_CZ) @include(if: $translationCs) {
      title
      content
    }
    translationFi: translation(languageCode: FI_FI) @include(if: $translationFi) {
      title
      content
    }
    translationRo: translation(languageCode: RO_RO) @include(if: $translationRo) {
      title
      content
    }
    translationHu: translation(languageCode: HU_HU) @include(if: $translationHu) {
      title
      content
    }
    translationSk: translation(languageCode: SK_SK) @include(if: $translationSk) {
      title
      content
    }
    translationTr: translation(languageCode: TR_TR) @include(if: $translationTr) {
      title
      content
    }
    translationNb: translation(languageCode: NB_NO) @include(if: $translationNb) {
      title
      content
    }
    translationBg: translation(languageCode: BG_BG) @include(if: $translationBg) {
      title
      content
    }
    translationHr: translation(languageCode: HR_HR) @include(if: $translationHr) {
      title
      content
    }
    translationSl: translation(languageCode: SL_SI) @include(if: $translationSl) {
      title
      content
    }
    translationSr: translation(languageCode: SR_LATN_RS) @include(if: $translationSr) {
      title
      content
    }
    translationBs: translation(languageCode: BS_LATN) @include(if: $translationBs) {
      title
      content
    }
    translationMk: translation(languageCode: MK_MK) @include(if: $translationMk) {
      title
      content
    }
    translationLt: translation(languageCode: LT_LT) @include(if: $translationLt) {
      title
      content
    }
    translationLv: translation(languageCode: LV_LV) @include(if: $translationLv) {
      title
      content
    }
    translationEt: translation(languageCode: ET_EE) @include(if: $translationEt) {
      title
      content
    }
  }
}
    `) as unknown as TypedDocumentString<GuideTranslationPresenceQuery, GuideTranslationPresenceQueryVariables>;
export const GuidesListDocument = new TypedDocumentString(`
    query GuidesList($first: Int!, $after: String, $languageCode: LanguageCodeEnum!, $pageTypes: [ID!]) {
  pages(
    first: $first
    after: $after
    sortBy: {field: PUBLISHED_AT, direction: DESC}
    filter: {pageTypes: $pageTypes}
  ) {
    edges {
      node {
        id
        slug
        title
        seoTitle
        seoDescription
        publishedAt
        created
        metadata {
          key
          value
        }
        pageType {
          slug
        }
        assignedAttributes(limit: 20) {
          __typename
          attribute {
            slug
          }
          ... on AssignedSingleChoiceAttribute {
            choiceValue: value {
              slug
            }
          }
          ... on AssignedNumericAttribute {
            numericValue: value
          }
          ... on AssignedDateAttribute {
            dateValue: value
          }
        }
        translation(languageCode: $languageCode) {
          title
          seoTitle
          seoDescription
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<GuidesListQuery, GuidesListQueryVariables>;
export const HomepageBundleProductsDocument = new TypedDocumentString(`
    query HomepageBundleProducts($channel: String!, $locale: LanguageCodeEnum!) {
  retatrutide: product(slug: "retatrutide", channel: $channel) {
    id
    name
    slug
    thumbnail(size: 512, format: WEBP) {
      url
      alt
    }
    translation(languageCode: $locale) {
      name
    }
    variants {
      id
      name
      sku
      quantityAvailable
      pricing {
        price {
          net {
            amount
            currency
          }
          gross {
            amount
            currency
          }
        }
      }
    }
  }
  bacWater: product(slug: "bac-water-10ml", channel: $channel) {
    id
    name
    variants {
      id
      name
      sku
      quantityAvailable
      pricing {
        price {
          net {
            amount
            currency
          }
          gross {
            amount
            currency
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<HomepageBundleProductsQuery, HomepageBundleProductsQueryVariables>;
export const LpSupportingProductsDocument = new TypedDocumentString(`
    query LpSupportingProducts($channel: String!, $languageCode: LanguageCodeEnum!, $slugs: [String!]!) {
  bac: product(slug: "bac-water-10ml", channel: $channel) {
    slug
    name
    translation(languageCode: $languageCode) {
      name
    }
    isAvailable
    isAvailableForPurchase
    thumbnail(size: 128, format: WEBP) {
      url
      alt
    }
    variants {
      id
      name
      quantityAvailable
      pricing {
        price {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
  peptides: products(
    channel: $channel
    first: 14
    filter: {slugs: $slugs, isPublished: true}
  ) {
    edges {
      node {
        slug
        name
        translation(languageCode: $languageCode) {
          name
        }
        isAvailable
        isAvailableForPurchase
        category {
          slug
        }
        variants {
          id
          name
          quantityAvailable
          pricing {
            price {
              gross {
                amount
                currency
              }
            }
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<LpSupportingProductsQuery, LpSupportingProductsQueryVariables>;
export const MenuGetBySlugDocument = new TypedDocumentString(`
    query MenuGetBySlug($slug: String!, $channel: String!, $languageCode: LanguageCodeEnum!) {
  menu(slug: $slug, channel: $channel) {
    items {
      ...MenuItem
      children {
        ...MenuItem
      }
    }
  }
}
    fragment MenuItem on MenuItem {
  id
  name
  level
  translation(languageCode: $languageCode) {
    name
  }
  category {
    id
    slug
    name
    translation(languageCode: $languageCode) {
      name
    }
  }
  collection {
    id
    name
    slug
    translation(languageCode: $languageCode) {
      name
    }
  }
  page {
    id
    title
    slug
    translation(languageCode: $languageCode) {
      title
    }
  }
  url
}`) as unknown as TypedDocumentString<MenuGetBySlugQuery, MenuGetBySlugQueryVariables>;
export const OrderByIdDocument = new TypedDocumentString(`
    query OrderById($id: ID!) {
  order(id: $id) {
    ...OrderDetails
  }
}
    fragment OrderDetails on Order {
  id
  number
  created
  status
  total {
    gross {
      amount
      currency
    }
  }
  lines {
    id
    variant {
      id
      name
      product {
        id
        name
        description
        slug
        thumbnail {
          url
          alt
        }
        category {
          id
          name
        }
      }
      pricing {
        price {
          gross {
            amount
            currency
          }
        }
      }
    }
    quantity
  }
  paymentStatus
  fulfillments {
    id
    status
    statusDisplay
    trackingNumber
    metadata {
      key
      value
    }
    lines {
      id
      quantity
      orderLine {
        id
      }
    }
  }
}`) as unknown as TypedDocumentString<OrderByIdQuery, OrderByIdQueryVariables>;
export const PageGetBySlugDocument = new TypedDocumentString(`
    query PageGetBySlug($slug: String!, $languageCode: LanguageCodeEnum!) {
  page(slug: $slug) {
    id
    slug
    title
    seoTitle
    seoDescription
    content
    pageType {
      slug
    }
    translation(languageCode: $languageCode) {
      title
      content
      seoTitle
      seoDescription
    }
  }
}
    `) as unknown as TypedDocumentString<PageGetBySlugQuery, PageGetBySlugQueryVariables>;
export const PageTranslateDocument = new TypedDocumentString(`
    mutation PageTranslate($id: ID!, $languageCode: LanguageCodeEnum!, $input: PageTranslationInput!) {
  pageTranslate(id: $id, languageCode: $languageCode, input: $input) {
    page {
      id
    }
    errors {
      field
      message
    }
  }
}
    `) as unknown as TypedDocumentString<PageTranslateMutation, PageTranslateMutationVariables>;
export const PageTypeBySlugDocument = new TypedDocumentString(`
    query PageTypeBySlug($slug: String!) {
  pageTypes(filter: {slugs: [$slug]}, first: 1) {
    edges {
      node {
        id
      }
    }
  }
}
    `) as unknown as TypedDocumentString<PageTypeBySlugQuery, PageTypeBySlugQueryVariables>;
export const ProductChannelListingUpdateDocument = new TypedDocumentString(`
    mutation ProductChannelListingUpdate($id: ID!, $input: ProductChannelListingUpdateInput!) {
  productChannelListingUpdate(id: $id, input: $input) {
    product {
      id
      slug
    }
    errors {
      field
      message
      code
    }
  }
}
    `) as unknown as TypedDocumentString<ProductChannelListingUpdateMutation, ProductChannelListingUpdateMutationVariables>;
export const ProductDetailsDocument = new TypedDocumentString(`
    query ProductDetails($slug: String!, $channel: String!, $languageCode: LanguageCodeEnum!) {
  product(slug: $slug, channel: $channel) {
    id
    isAvailableForPurchase
    name
    slug
    description
    seoTitle
    seoDescription
    translation(languageCode: $languageCode) {
      name
      description
      seoTitle
      seoDescription
    }
    translationEsMx: translation(languageCode: ES_MX) {
      name
      description
      seoTitle
      seoDescription
    }
    thumbnail(size: 800, format: WEBP) {
      url
      alt
    }
    media {
      url(size: 960, format: WEBP)
      alt
      type
    }
    metadata {
      key
      value
    }
    category {
      name
      slug
      translation(languageCode: $languageCode) {
        name
      }
    }
    faqAttribute: assignedAttribute(slug: "faq-json") {
      ... on AssignedPlainTextAttribute {
        value
        translation(languageCode: $languageCode)
      }
    }
    variants {
      ...VariantDetails
    }
    pricing {
      priceRange {
        start {
          gross {
            amount
            currency
          }
        }
        stop {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
}
    fragment VariantDetails on ProductVariant {
  id
  name
  sku
  quantityAvailable
  selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
    values {
      id
      slug
      name
      value
    }
    attribute {
      id
      slug
    }
  }
  media {
    url(size: 1024, format: WEBP)
    alt
    type
  }
  pricing {
    price {
      gross {
        currency
        amount
      }
    }
    priceUndiscounted {
      gross {
        currency
        amount
      }
    }
  }
}`) as unknown as TypedDocumentString<ProductDetailsQuery, ProductDetailsQueryVariables>;
export const ProductListDocument = new TypedDocumentString(`
    query ProductList($first: Int = 9, $channel: String!, $languageCode: LanguageCodeEnum!) {
  products(first: $first, channel: $channel) {
    edges {
      node {
        ...ProductListItem
      }
    }
  }
}
    fragment ProductListItem on Product {
  id
  name
  slug
  created
  translation(languageCode: $languageCode) {
    name
  }
  pricing {
    priceRange {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
    priceRangeUndiscounted {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
  }
  category {
    id
    name
    slug
    translation(languageCode: $languageCode) {
      name
    }
  }
  thumbnail(size: 640, format: WEBP) {
    url
    alt
  }
  metafields(
    keys: ["ux_card_copy_json", "ux_short_description", "ux_trust_badges_json", "ux_research_level", "ux_peptide_scores_json", "ux_peptide_categories_json", "ux_availability", "ux_mechanism", "ux_typical_dosage", "ux_half_life", "ux_sequence", "ux_pubmed_url", "ux_aliases_json", "ux_timing", "ux_dosing_frequency", "ux_contraindicated_combinations_json"]
  )
  variants {
    id
    selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
      attribute {
        slug
        name
      }
      values {
        name
        value
      }
    }
  }
}`) as unknown as TypedDocumentString<ProductListQuery, ProductListQueryVariables>;
export const ProductListByCategoryDocument = new TypedDocumentString(`
    query ProductListByCategory($slug: String!, $channel: String!, $first: Int, $after: String, $last: Int, $before: String, $sortBy: ProductOrder, $filter: ProductFilterInput, $languageCode: LanguageCodeEnum!) {
  category(slug: $slug) {
    id
    name
    slug
    description
    seoDescription
    seoTitle
    translation(languageCode: $languageCode) {
      name
      description
      seoDescription
      seoTitle
    }
    backgroundImage {
      url
      alt
    }
    products(
      first: $first
      after: $after
      last: $last
      before: $before
      channel: $channel
      sortBy: $sortBy
      filter: $filter
    ) {
      edges {
        node {
          ...ProductListItem
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
}
    fragment ProductListItem on Product {
  id
  name
  slug
  created
  translation(languageCode: $languageCode) {
    name
  }
  pricing {
    priceRange {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
    priceRangeUndiscounted {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
  }
  category {
    id
    name
    slug
    translation(languageCode: $languageCode) {
      name
    }
  }
  thumbnail(size: 640, format: WEBP) {
    url
    alt
  }
  metafields(
    keys: ["ux_card_copy_json", "ux_short_description", "ux_trust_badges_json", "ux_research_level", "ux_peptide_scores_json", "ux_peptide_categories_json", "ux_availability", "ux_mechanism", "ux_typical_dosage", "ux_half_life", "ux_sequence", "ux_pubmed_url", "ux_aliases_json", "ux_timing", "ux_dosing_frequency", "ux_contraindicated_combinations_json"]
  )
  variants {
    id
    selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
      attribute {
        slug
        name
      }
      values {
        name
        value
      }
    }
  }
}`) as unknown as TypedDocumentString<ProductListByCategoryQuery, ProductListByCategoryQueryVariables>;
export const ProductListByCollectionDocument = new TypedDocumentString(`
    query ProductListByCollection($slug: String!, $channel: String!, $first: Int, $after: String, $last: Int, $before: String, $sortBy: ProductOrder, $filter: ProductFilterInput, $languageCode: LanguageCodeEnum!) {
  collection(slug: $slug, channel: $channel) {
    id
    name
    slug
    description
    seoDescription
    seoTitle
    translation(languageCode: $languageCode) {
      name
      description
      seoDescription
      seoTitle
    }
    backgroundImage {
      url
      alt
    }
    products(
      first: $first
      after: $after
      last: $last
      before: $before
      sortBy: $sortBy
      filter: $filter
    ) {
      edges {
        node {
          ...ProductListItem
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
}
    fragment ProductListItem on Product {
  id
  name
  slug
  created
  translation(languageCode: $languageCode) {
    name
  }
  pricing {
    priceRange {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
    priceRangeUndiscounted {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
  }
  category {
    id
    name
    slug
    translation(languageCode: $languageCode) {
      name
    }
  }
  thumbnail(size: 640, format: WEBP) {
    url
    alt
  }
  metafields(
    keys: ["ux_card_copy_json", "ux_short_description", "ux_trust_badges_json", "ux_research_level", "ux_peptide_scores_json", "ux_peptide_categories_json", "ux_availability", "ux_mechanism", "ux_typical_dosage", "ux_half_life", "ux_sequence", "ux_pubmed_url", "ux_aliases_json", "ux_timing", "ux_dosing_frequency", "ux_contraindicated_combinations_json"]
  )
  variants {
    id
    selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
      attribute {
        slug
        name
      }
      values {
        name
        value
      }
    }
  }
}`) as unknown as TypedDocumentString<ProductListByCollectionQuery, ProductListByCollectionQueryVariables>;
export const ProductListPaginatedDocument = new TypedDocumentString(`
    query ProductListPaginated($first: Int, $after: String, $last: Int, $before: String, $channel: String!, $sortBy: ProductOrder, $filter: ProductFilterInput, $languageCode: LanguageCodeEnum!) {
  products(
    first: $first
    after: $after
    last: $last
    before: $before
    channel: $channel
    sortBy: $sortBy
    filter: $filter
  ) {
    totalCount
    edges {
      node {
        ...ProductListItem
        updatedAt
      }
      cursor
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    fragment ProductListItem on Product {
  id
  name
  slug
  created
  translation(languageCode: $languageCode) {
    name
  }
  pricing {
    priceRange {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
    priceRangeUndiscounted {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
  }
  category {
    id
    name
    slug
    translation(languageCode: $languageCode) {
      name
    }
  }
  thumbnail(size: 640, format: WEBP) {
    url
    alt
  }
  metafields(
    keys: ["ux_card_copy_json", "ux_short_description", "ux_trust_badges_json", "ux_research_level", "ux_peptide_scores_json", "ux_peptide_categories_json", "ux_availability", "ux_mechanism", "ux_typical_dosage", "ux_half_life", "ux_sequence", "ux_pubmed_url", "ux_aliases_json", "ux_timing", "ux_dosing_frequency", "ux_contraindicated_combinations_json"]
  )
  variants {
    id
    selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
      attribute {
        slug
        name
      }
      values {
        name
        value
      }
    }
  }
}`) as unknown as TypedDocumentString<ProductListPaginatedQuery, ProductListPaginatedQueryVariables>;
export const ProductTranslateDocument = new TypedDocumentString(`
    mutation ProductTranslate($id: ID!, $languageCode: LanguageCodeEnum!, $input: TranslationInput!) {
  productTranslate(id: $id, languageCode: $languageCode, input: $input) {
    product {
      id
    }
    errors {
      field
      message
    }
  }
}
    `) as unknown as TypedDocumentString<ProductTranslateMutation, ProductTranslateMutationVariables>;
export const ProductVariantTranslateDocument = new TypedDocumentString(`
    mutation ProductVariantTranslate($id: ID!, $languageCode: LanguageCodeEnum!, $input: NameTranslationInput!) {
  productVariantTranslate(id: $id, languageCode: $languageCode, input: $input) {
    productVariant {
      id
    }
    errors {
      field
      message
    }
  }
}
    `) as unknown as TypedDocumentString<ProductVariantTranslateMutation, ProductVariantTranslateMutationVariables>;
export const ProductsListForVisibilitySyncDocument = new TypedDocumentString(`
    query ProductsListForVisibilitySync($channel: String!, $first: Int!, $after: String) {
  products(first: $first, after: $after, channel: $channel) {
    edges {
      node {
        id
        slug
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<ProductsListForVisibilitySyncQuery, ProductsListForVisibilitySyncQueryVariables>;
export const PublicDocumentProductsDocument = new TypedDocumentString(`
    query PublicDocumentProducts($first: Int!, $after: String, $channel: String!, $languageCode: LanguageCodeEnum!) {
  products(first: $first, after: $after, channel: $channel) {
    edges {
      node {
        slug
        name
        updatedAt
        translation(languageCode: $languageCode) {
          name
        }
        translationEsMx: translation(languageCode: ES_MX) {
          description
        }
        category {
          slug
          name
          translation(languageCode: $languageCode) {
            name
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<PublicDocumentProductsQuery, PublicDocumentProductsQueryVariables>;
export const RootCategoriesDocument = new TypedDocumentString(`
    query RootCategories($first: Int = 6, $languageCode: LanguageCodeEnum!) {
  categories(level: 0, first: $first) {
    edges {
      node {
        id
        name
        slug
        description
        backgroundImage {
          url
          alt
        }
        translation(languageCode: $languageCode) {
          name
          description
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<RootCategoriesQuery, RootCategoriesQueryVariables>;
export const SearchIndexDocument = new TypedDocumentString(`
    query SearchIndex($channel: String!, $first: Int!, $after: String) {
  products(
    first: $first
    after: $after
    channel: $channel
    sortBy: {field: NAME, direction: ASC}
  ) {
    edges {
      node {
        id
        name
        slug
        isAvailable
        searchAliases: metafield(key: "ux_aliases_json")
        thumbnail(size: 640, format: WEBP) {
          url
          alt
        }
        pricing {
          priceRange {
            start {
              gross {
                amount
                currency
              }
            }
          }
        }
        category {
          name
          translationDe: translation(languageCode: DE) {
            name
          }
          translationIt: translation(languageCode: IT_IT) {
            name
          }
          translationEs: translation(languageCode: ES_ES) {
            name
          }
          translationFr: translation(languageCode: FR_FR) {
            name
          }
          translationNl: translation(languageCode: NL_NL) {
            name
          }
          translationPt: translation(languageCode: PT_PT) {
            name
          }
          translationPtBr: translation(languageCode: PT_BR) {
            name
          }
          translationPl: translation(languageCode: PL_PL) {
            name
          }
          translationSv: translation(languageCode: SV_SE) {
            name
          }
          translationDa: translation(languageCode: DA_DK) {
            name
          }
          translationEl: translation(languageCode: EL_GR) {
            name
          }
          translationCs: translation(languageCode: CS_CZ) {
            name
          }
          translationFi: translation(languageCode: FI_FI) {
            name
          }
          translationRo: translation(languageCode: RO_RO) {
            name
          }
          translationHu: translation(languageCode: HU_HU) {
            name
          }
          translationSk: translation(languageCode: SK_SK) {
            name
          }
          translationTr: translation(languageCode: TR_TR) {
            name
          }
          translationNb: translation(languageCode: NB_NO) {
            name
          }
          translationBg: translation(languageCode: BG_BG) {
            name
          }
          translationHr: translation(languageCode: HR_HR) {
            name
          }
          translationSl: translation(languageCode: SL_SI) {
            name
          }
          translationSr: translation(languageCode: SR_LATN_RS) {
            name
          }
          translationBs: translation(languageCode: BS_LATN) {
            name
          }
          translationMk: translation(languageCode: MK_MK) {
            name
          }
          translationLt: translation(languageCode: LT_LT) {
            name
          }
          translationLv: translation(languageCode: LV_LV) {
            name
          }
          translationEt: translation(languageCode: ET_EE) {
            name
          }
        }
        translationDe: translation(languageCode: DE) {
          name
        }
        translationIt: translation(languageCode: IT_IT) {
          name
        }
        translationEs: translation(languageCode: ES_ES) {
          name
        }
        translationFr: translation(languageCode: FR_FR) {
          name
        }
        translationNl: translation(languageCode: NL_NL) {
          name
        }
        translationPt: translation(languageCode: PT_PT) {
          name
        }
        translationPtBr: translation(languageCode: PT_BR) {
          name
        }
        translationPl: translation(languageCode: PL_PL) {
          name
        }
        translationSv: translation(languageCode: SV_SE) {
          name
        }
        translationDa: translation(languageCode: DA_DK) {
          name
        }
        translationEl: translation(languageCode: EL_GR) {
          name
        }
        translationCs: translation(languageCode: CS_CZ) {
          name
        }
        translationFi: translation(languageCode: FI_FI) {
          name
        }
        translationRo: translation(languageCode: RO_RO) {
          name
        }
        translationHu: translation(languageCode: HU_HU) {
          name
        }
        translationSk: translation(languageCode: SK_SK) {
          name
        }
        translationTr: translation(languageCode: TR_TR) {
          name
        }
        translationNb: translation(languageCode: NB_NO) {
          name
        }
        translationBg: translation(languageCode: BG_BG) {
          name
        }
        translationHr: translation(languageCode: HR_HR) {
          name
        }
        translationSl: translation(languageCode: SL_SI) {
          name
        }
        translationSr: translation(languageCode: SR_LATN_RS) {
          name
        }
        translationBs: translation(languageCode: BS_LATN) {
          name
        }
        translationMk: translation(languageCode: MK_MK) {
          name
        }
        translationLt: translation(languageCode: LT_LT) {
          name
        }
        translationLv: translation(languageCode: LV_LV) {
          name
        }
        translationEt: translation(languageCode: ET_EE) {
          name
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<SearchIndexQuery, SearchIndexQueryVariables>;
export const SearchProductsDocument = new TypedDocumentString(`
    query SearchProducts($filter: ProductFilterInput!, $sortBy: ProductOrderField!, $sortDirection: OrderDirection!, $first: Int, $after: String, $last: Int, $before: String, $channel: String!, $languageCode: LanguageCodeEnum!) {
  products(
    first: $first
    after: $after
    last: $last
    before: $before
    channel: $channel
    sortBy: {field: $sortBy, direction: $sortDirection}
    filter: $filter
  ) {
    totalCount
    edges {
      node {
        ...ProductListItem
      }
      cursor
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      endCursor
      startCursor
    }
  }
}
    fragment ProductListItem on Product {
  id
  name
  slug
  created
  translation(languageCode: $languageCode) {
    name
  }
  pricing {
    priceRange {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
    priceRangeUndiscounted {
      start {
        gross {
          amount
          currency
        }
      }
      stop {
        gross {
          amount
          currency
        }
      }
    }
  }
  category {
    id
    name
    slug
    translation(languageCode: $languageCode) {
      name
    }
  }
  thumbnail(size: 640, format: WEBP) {
    url
    alt
  }
  metafields(
    keys: ["ux_card_copy_json", "ux_short_description", "ux_trust_badges_json", "ux_research_level", "ux_peptide_scores_json", "ux_peptide_categories_json", "ux_availability", "ux_mechanism", "ux_typical_dosage", "ux_half_life", "ux_sequence", "ux_pubmed_url", "ux_aliases_json", "ux_timing", "ux_dosing_frequency", "ux_contraindicated_combinations_json"]
  )
  variants {
    id
    selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
      attribute {
        slug
        name
      }
      values {
        name
        value
      }
    }
  }
}`) as unknown as TypedDocumentString<SearchProductsQuery, SearchProductsQueryVariables>;
export const SitemapProductContentDocument = new TypedDocumentString(`
    query SitemapProductContent($first: Int!, $after: String, $channel: String!, $languageCode: LanguageCodeEnum!, $withRegional: Boolean!) {
  products(first: $first, after: $after, channel: $channel) {
    edges {
      node {
        slug
        name
        description
        seoTitle
        seoDescription
        translation(languageCode: $languageCode) {
          name
          description
          seoTitle
          seoDescription
        }
        translationEsMx: translation(languageCode: ES_MX) @include(if: $withRegional) {
          name
          description
          seoTitle
          seoDescription
        }
        metadata {
          key
          value
        }
        category {
          slug
        }
        media {
          id
          alt
          type
        }
        faqAttribute: assignedAttribute(slug: "faq-json") {
          ... on AssignedPlainTextAttribute {
            value
            translation(languageCode: $languageCode)
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<SitemapProductContentQuery, SitemapProductContentQueryVariables>;
export const StackBuilderProductsDocument = new TypedDocumentString(`
    query StackBuilderProducts($channel: String!, $languageCode: LanguageCodeEnum!, $first: Int!, $after: String) {
  products(
    channel: $channel
    first: $first
    after: $after
    filter: {isPublished: true}
  ) {
    edges {
      node {
        id
        slug
        name
        isAvailable
        translation(languageCode: $languageCode) {
          name
        }
        thumbnail(size: 640, format: WEBP) {
          url
          alt
        }
        pricing {
          priceRange {
            start {
              gross {
                amount
                currency
              }
            }
            stop {
              gross {
                amount
                currency
              }
            }
          }
        }
        category {
          name
          slug
        }
        variants {
          id
          name
          quantityAvailable
          pricing {
            price {
              gross {
                amount
                currency
              }
            }
          }
          selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
            attribute {
              slug
            }
            values {
              name
              value
            }
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<StackBuilderProductsQuery, StackBuilderProductsQueryVariables>;
export const StaffAfterSalesOrdersDocument = new TypedDocumentString(`
    query StaffAfterSalesOrders($ids: [ID!], $first: Int!) {
  orders(filter: {ids: $ids}, first: $first) {
    edges {
      node {
        id
        number
        created
        userEmail
        languageCodeEnum
        shippingAddress {
          firstName
          lastName
        }
        billingAddress {
          firstName
          lastName
        }
        lines {
          productName
          quantity
        }
        total {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<StaffAfterSalesOrdersQuery, StaffAfterSalesOrdersQueryVariables>;
export const StaffChatwootCustomerContextDocument = new TypedDocumentString(`
    query StaffChatwootCustomerContext($email: String!, $first: Int!) {
  user(email: $email) {
    id
    email
    firstName
    lastName
    dateJoined
    languageCode
    defaultShippingAddress {
      city
      country {
        code
        country
      }
    }
    orders(first: $first) {
      totalCount
      edges {
        node {
          ...ChatwootPanelOrder
        }
      }
    }
  }
  orders(
    first: $first
    sortBy: {field: CREATED_AT, direction: DESC}
    where: {userEmail: {eq: $email}}
  ) {
    totalCount
    edges {
      node {
        ...ChatwootPanelOrder
      }
    }
  }
}
    fragment ChatwootPanelOrder on Order {
  id
  number
  created
  status
  paymentStatus
  chargeStatus
  authorizeStatus
  isPaid
  userEmail
  languageCode
  user {
    id
    email
  }
  channel {
    slug
    currencyCode
  }
  total {
    gross {
      amount
      currency
    }
  }
  totalCharged {
    amount
    currency
  }
  totalRefunded {
    amount
    currency
  }
  totalRefundPending {
    amount
    currency
  }
  shippingAddress {
    city
    country {
      code
      country
    }
  }
  lines {
    id
    productName
    variantName
    productSku
    quantity
    quantityFulfilled
    quantityToFulfill
  }
  fulfillments {
    id
    status
    created
    trackingNumber
    lines {
      orderLine {
        id
      }
      quantity
    }
  }
}`) as unknown as TypedDocumentString<StaffChatwootCustomerContextQuery, StaffChatwootCustomerContextQueryVariables>;
export const StaffChatwootOrderByNumberDocument = new TypedDocumentString(`
    query StaffChatwootOrderByNumber($number: Int!) {
  orders(first: 2, where: {number: {eq: $number}}) {
    totalCount
    edges {
      node {
        ...ChatwootPanelOrder
      }
    }
  }
}
    fragment ChatwootPanelOrder on Order {
  id
  number
  created
  status
  paymentStatus
  chargeStatus
  authorizeStatus
  isPaid
  userEmail
  languageCode
  user {
    id
    email
  }
  channel {
    slug
    currencyCode
  }
  total {
    gross {
      amount
      currency
    }
  }
  totalCharged {
    amount
    currency
  }
  totalRefunded {
    amount
    currency
  }
  totalRefundPending {
    amount
    currency
  }
  shippingAddress {
    city
    country {
      code
      country
    }
  }
  lines {
    id
    productName
    variantName
    productSku
    quantity
    quantityFulfilled
    quantityToFulfill
  }
  fulfillments {
    id
    status
    created
    trackingNumber
    lines {
      orderLine {
        id
      }
      quantity
    }
  }
}`) as unknown as TypedDocumentString<StaffChatwootOrderByNumberQuery, StaffChatwootOrderByNumberQueryVariables>;
export const StaffDueOrderIdsDocument = new TypedDocumentString(`
    query StaffDueOrderIds($first: Int!, $after: String) {
  orders(
    filter: {status: [UNFULFILLED, PARTIALLY_FULFILLED], chargeStatus: [FULL, OVERCHARGED]}
    sortBy: {field: CREATED_AT, direction: DESC}
    first: $first
    after: $after
  ) {
    totalCount
    edges {
      node {
        id
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<StaffDueOrderIdsQuery, StaffDueOrderIdsQueryVariables>;
export const StaffFulfillmentOrderCountsDocument = new TypedDocumentString(`
    query StaffFulfillmentOrderCounts {
  unfulfilled: orders(filter: {status: [UNFULFILLED]}, first: 1) {
    totalCount
  }
  partial: orders(filter: {status: [PARTIALLY_FULFILLED]}, first: 1) {
    totalCount
  }
  fulfilled: orders(filter: {status: [FULFILLED]}, first: 1) {
    totalCount
  }
  due: orders(
    filter: {status: [UNFULFILLED, PARTIALLY_FULFILLED], chargeStatus: [FULL, OVERCHARGED]}
    first: 1
  ) {
    totalCount
  }
}
    `) as unknown as TypedDocumentString<StaffFulfillmentOrderCountsQuery, StaffFulfillmentOrderCountsQueryVariables>;
export const StaffFulfillmentOrdersDocument = new TypedDocumentString(`
    query StaffFulfillmentOrders($first: Int, $after: String, $last: Int, $before: String, $filter: OrderFilterInput) {
  orders(
    filter: $filter
    sortBy: {field: CREATED_AT, direction: DESC}
    first: $first
    after: $after
    last: $last
    before: $before
  ) {
    totalCount
    edges {
      node {
        id
        number
        created
        userEmail
        channel {
          slug
        }
        paymentStatus
        status
        chargeStatus
        metadata {
          key
          value
        }
        total {
          gross {
            amount
            currency
          }
        }
        lines {
          id
          productName
          productSku
          quantity
          quantityFulfilled
          quantityToFulfill
          thumbnail {
            url
            alt
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<StaffFulfillmentOrdersQuery, StaffFulfillmentOrdersQueryVariables>;
export const StaffOrderDetailDocument = new TypedDocumentString(`
    query StaffOrderDetail($id: ID!) {
  order(id: $id) {
    id
    number
    created
    userEmail
    languageCode
    channel {
      slug
    }
    paymentStatus
    status
    shippingAddress {
      firstName
      lastName
      streetAddress1
      streetAddress2
      city
      postalCode
      country {
        country
        code
      }
      metadata {
        key
        value
      }
    }
    total {
      gross {
        amount
        currency
      }
    }
    lines {
      id
      productName
      productSku
      quantity
      quantityFulfilled
      quantityToFulfill
      thumbnail {
        url
        alt
      }
      metadata {
        key
        value
      }
    }
    fulfillments {
      id
      status
      statusDisplay
      trackingNumber
      created
      metadata {
        key
        value
      }
      lines {
        id
        quantity
        orderLine {
          id
          productName
          productSku
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<StaffOrderDetailQuery, StaffOrderDetailQueryVariables>;
export const StaffOrderFulfillDocument = new TypedDocumentString(`
    mutation StaffOrderFulfill($orderId: ID!, $input: OrderFulfillInput!) {
  orderFulfill(order: $orderId, input: $input) {
    fulfillments {
      id
      status
      trackingNumber
    }
    errors {
      field
      message
      code
      warehouse
      orderLines
    }
  }
}
    `) as unknown as TypedDocumentString<StaffOrderFulfillMutation, StaffOrderFulfillMutationVariables>;
export const StaffOrderFulfillmentUpdateTrackingDocument = new TypedDocumentString(`
    mutation StaffOrderFulfillmentUpdateTracking($id: ID!, $input: FulfillmentUpdateTrackingInput!) {
  orderFulfillmentUpdateTracking(id: $id, input: $input) {
    fulfillment {
      id
      status
      statusDisplay
      trackingNumber
    }
    errors {
      field
      message
      code
    }
  }
}
    `) as unknown as TypedDocumentString<StaffOrderFulfillmentUpdateTrackingMutation, StaffOrderFulfillmentUpdateTrackingMutationVariables>;
export const StaffOrdersForExportDocument = new TypedDocumentString(`
    query StaffOrdersForExport($ids: [ID!], $first: Int!) {
  orders(filter: {ids: $ids}, first: $first) {
    edges {
      node {
        id
        number
        created
        userEmail
        channel {
          slug
        }
        status
        paymentStatus
        shippingAddress {
          firstName
          lastName
          companyName
          streetAddress1
          streetAddress2
          city
          postalCode
          countryArea
          phone
          country {
            code
            country
          }
          metadata {
            key
            value
          }
        }
        billingAddress {
          firstName
          lastName
          companyName
          streetAddress1
          streetAddress2
          city
          postalCode
          countryArea
          phone
          country {
            code
            country
          }
        }
        lines {
          productName
          productSku
          quantity
          quantityToFulfill
        }
        total {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<StaffOrdersForExportQuery, StaffOrdersForExportQueryVariables>;
export const StaffRecentOrderPhonesDocument = new TypedDocumentString(`
    query StaffRecentOrderPhones($first: Int!) {
  orders(sortBy: {field: CREATED_AT, direction: DESC}, first: $first) {
    edges {
      node {
        shippingAddress {
          phone
          country {
            code
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<StaffRecentOrderPhonesQuery, StaffRecentOrderPhonesQueryVariables>;
export const StaffSupportOutcomeOrdersDocument = new TypedDocumentString(`
    query StaffSupportOutcomeOrders($where: OrderWhereInput!, $first: Int!, $after: String) {
  orders(
    first: $first
    after: $after
    where: $where
    sortBy: {field: CREATED_AT, direction: DESC}
  ) {
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        id
        number
        created
        status
        userEmail
        voucherCode
        user {
          id
          isStaff
        }
        channel {
          slug
        }
        total {
          gross {
            amount
            currency
          }
        }
        totalRefunded {
          amount
          currency
        }
        fulfillments {
          status
        }
        transactions {
          id
        }
        paytreeIntentId: privateMetafield(key: "paytree_intent_id")
      }
    }
  }
}
    `) as unknown as TypedDocumentString<StaffSupportOutcomeOrdersQuery, StaffSupportOutcomeOrdersQueryVariables>;
export const StaffSupportOutcomeUserDocument = new TypedDocumentString(`
    query StaffSupportOutcomeUser($email: String!) {
  user(email: $email) {
    id
  }
}
    `) as unknown as TypedDocumentString<StaffSupportOutcomeUserQuery, StaffSupportOutcomeUserQueryVariables>;
export const StaffUpdateFulfillmentMetadataDocument = new TypedDocumentString(`
    mutation StaffUpdateFulfillmentMetadata($id: ID!, $input: [MetadataInput!]!) {
  updateMetadata(id: $id, input: $input) {
    item {
      ... on Fulfillment {
        id
        metadata {
          key
          value
        }
      }
    }
    errors {
      field
      message
    }
  }
}
    `) as unknown as TypedDocumentString<StaffUpdateFulfillmentMetadataMutation, StaffUpdateFulfillmentMetadataMutationVariables>;
export const StaffVariantStocksDocument = new TypedDocumentString(`
    query StaffVariantStocks($variantId: ID!) {
  productVariant(id: $variantId) {
    id
    sku
    name
    stocks {
      warehouse {
        id
        name
        slug
      }
      quantity
      quantityAllocated
    }
    channelListings {
      channel {
        slug
      }
      price {
        amount
        currency
      }
      costPrice {
        amount
        currency
      }
    }
  }
}
    `) as unknown as TypedDocumentString<StaffVariantStocksQuery, StaffVariantStocksQueryVariables>;
export const StaffWarehousesDocument = new TypedDocumentString(`
    query StaffWarehouses {
  warehouses(first: 20) {
    edges {
      node {
        id
        name
        slug
      }
    }
  }
}
    `) as unknown as TypedDocumentString<StaffWarehousesQuery, StaffWarehousesQueryVariables>;
export const SupportVoucherByCodeDocument = new TypedDocumentString(`
    query SupportVoucherByCode($code: String!) {
  vouchers(first: 20, filter: {search: $code}) {
    edges {
      node {
        id
        type
        discountValueType
        singleUse
        onlyForStaff
        applyOncePerOrder
        minCheckoutItemsQuantity
        endDate
        codes(first: 5) {
          edges {
            node {
              code
            }
          }
        }
        channelListings {
          channel {
            slug
          }
          discountValue
          minSpent {
            amount
          }
        }
        privateMetadata {
          key
          value
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<SupportVoucherByCodeQuery, SupportVoucherByCodeQueryVariables>;
export const SupportVoucherChannelListingDocument = new TypedDocumentString(`
    mutation SupportVoucherChannelListing($id: ID!, $input: VoucherChannelListingInput!) {
  voucherChannelListingUpdate(id: $id, input: $input) {
    voucher {
      id
    }
    errors {
      field
      code
    }
  }
}
    `) as unknown as TypedDocumentString<SupportVoucherChannelListingMutation, SupportVoucherChannelListingMutationVariables>;
export const SupportVoucherChannelsDocument = new TypedDocumentString(`
    query SupportVoucherChannels {
  channels {
    id
    slug
    isActive
  }
}
    `) as unknown as TypedDocumentString<SupportVoucherChannelsQuery, SupportVoucherChannelsQueryVariables>;
export const SupportVoucherCreateDocument = new TypedDocumentString(`
    mutation SupportVoucherCreate($input: VoucherInput!) {
  voucherCreate(input: $input) {
    voucher {
      id
    }
    errors {
      field
      code
    }
  }
}
    `) as unknown as TypedDocumentString<SupportVoucherCreateMutation, SupportVoucherCreateMutationVariables>;
export const SupportVoucherPrivateMetadataDocument = new TypedDocumentString(`
    mutation SupportVoucherPrivateMetadata($id: ID!, $input: [MetadataInput!]!) {
  updatePrivateMetadata(id: $id, input: $input) {
    errors {
      field
      code
    }
  }
}
    `) as unknown as TypedDocumentString<SupportVoucherPrivateMetadataMutation, SupportVoucherPrivateMetadataMutationVariables>;
export const TrackingOrderForReconciliationDocument = new TypedDocumentString(`
    query TrackingOrderForReconciliation($id: ID!) {
  order(id: $id) {
    id
    number
    status
    channel {
      slug
    }
    shippingAddress {
      postalCode
      country {
        code
      }
    }
    fulfillments {
      id
      created
      status
      trackingNumber
      metadata {
        key
        value
      }
    }
  }
}
    `) as unknown as TypedDocumentString<TrackingOrderForReconciliationQuery, TrackingOrderForReconciliationQueryVariables>;
export const TrackingOrderContactDocument = new TypedDocumentString(`
    query TrackingOrderContact($id: ID!) {
  order(id: $id) {
    id
    number
    userEmail
    languageCodeEnum
    channel {
      slug
    }
    shippingAddress {
      firstName
    }
  }
}
    `) as unknown as TypedDocumentString<TrackingOrderContactQuery, TrackingOrderContactQueryVariables>;
export const UserCheckoutsDocument = new TypedDocumentString(`
    query UserCheckouts($channel: String!, $languageCode: LanguageCodeEnum!) {
  me {
    checkouts(channel: $channel, first: 10) {
      edges {
        node {
          updatedAt
          id
          email
          isShippingRequired
          shippingAddress {
            country {
              code
            }
          }
          problems {
            __typename
          }
          channel {
            slug
          }
          lines {
            id
            quantity
            metadata {
              key
              value
            }
            undiscountedUnitPrice {
              amount
              currency
            }
            totalPrice {
              net {
                amount
                currency
              }
              gross {
                amount
                currency
              }
            }
            variant {
              sku
              product {
                id
                name
                slug
                translation(languageCode: $languageCode) {
                  name
                }
                thumbnail {
                  url
                  alt
                }
                category {
                  name
                  translation(languageCode: $languageCode) {
                    name
                  }
                }
              }
              pricing {
                price {
                  net {
                    amount
                    currency
                  }
                  gross {
                    amount
                    currency
                  }
                }
                priceUndiscounted {
                  gross {
                    amount
                    currency
                  }
                }
              }
              name
              id
              selectionAttributes: attributes(variantSelection: VARIANT_SELECTION) {
                attribute {
                  id
                  name
                  slug
                }
                values {
                  id
                  slug
                  name
                  value
                }
              }
              nonSelectionAttributes: attributes(variantSelection: NOT_VARIANT_SELECTION) {
                attribute {
                  id
                  name
                  slug
                }
                values {
                  id
                  slug
                  name
                  value
                }
              }
            }
          }
          voucherCode
          discount {
            amount
            currency
          }
          delivery {
            id
          }
          shippingPrice {
            gross {
              amount
              currency
            }
          }
          subtotalPrice {
            gross {
              amount
              currency
            }
            net {
              amount
              currency
            }
          }
          totalPrice {
            gross {
              amount
              currency
            }
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<UserCheckoutsQuery, UserCheckoutsQueryVariables>;
export const CustomerByIdDocument = new TypedDocumentString(`
    query CustomerById($id: ID!) {
  user(id: $id) {
    id
    email
    firstName
    lastName
    dateJoined
    defaultShippingAddress {
      streetAddress1
      streetAddress2
      city
      postalCode
      phone
      country {
        code
        country
      }
    }
    defaultBillingAddress {
      streetAddress1
      streetAddress2
      city
      postalCode
      country {
        code
        country
      }
    }
    orders(first: 100) {
      totalCount
      edges {
        node {
          id
          number
          created
          status
          paymentStatus
          channel {
            slug
            currencyCode
          }
          total {
            gross {
              amount
              currency
            }
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<CustomerByIdQuery, CustomerByIdQueryVariables>;
export const CustomersListDocument = new TypedDocumentString(`
    query CustomersList($first: Int!, $after: String, $search: String) {
  customers(
    first: $first
    after: $after
    filter: {search: $search}
    sortBy: {field: CREATED_AT, direction: DESC}
  ) {
    totalCount
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        id
        email
        firstName
        lastName
        dateJoined
        defaultShippingAddress {
          country {
            code
            country
          }
        }
        orders(first: 1) {
          totalCount
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<CustomersListQuery, CustomersListQueryVariables>;
export const OrdersInRangeDocument = new TypedDocumentString(`
    query OrdersInRange($first: Int!, $after: String, $from: Date, $to: Date, $channels: [ID!]) {
  orders(
    first: $first
    after: $after
    filter: {created: {gte: $from, lte: $to}, channels: $channels, status: [UNFULFILLED, PARTIALLY_FULFILLED, FULFILLED]}
    sortBy: {field: CREATED_AT, direction: DESC}
  ) {
    totalCount
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        id
        number
        created
        userEmail
        status
        paymentStatus
        channel {
          slug
          currencyCode
        }
        total {
          gross {
            amount
            currency
          }
        }
        user {
          id
          isStaff
        }
        shippingAddress {
          firstName
          country {
            code
          }
        }
        billingAddress {
          country {
            code
          }
        }
        lines {
          id
          quantity
          totalPrice {
            gross {
              amount
              currency
            }
          }
          variant {
            id
            name
            product {
              id
              name
              slug
              category {
                id
                name
                slug
              }
            }
            attributes {
              attribute {
                slug
                name
              }
              values {
                name
                slug
              }
            }
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<OrdersInRangeQuery, OrdersInRangeQueryVariables>;